import './Workspace.css'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'

import type { ChatStreamEvent } from '@molecule/app-ai-chat'
import type { UserProfile } from '@molecule/app-auth'
import { AuthModalMount } from '@molecule/app-auth-modal-react'
import { writeText } from '@molecule/app-clipboard'
import type { FixWithAIRequest } from '@molecule/app-code-editor'
import type { IconName } from '@molecule/app-icons'
import type {
  Activity,
  ActivityType,
  ChatUserIdentity,
  Command,
  CommandDef,
  PreviewRenderState,
  PreviewStuckReport,
  PreviewUiCommand,
  PreviewUiResult,
  ReportResult,
  ShareLinkResult,
  ShortcutEntry,
  TestRunEvent,
  TestSelection,
} from '@molecule/app-ide-react'
import {
  buildShareUrl,
  ChatPanel as ChatPanelBase,
  CommandPalette,
  EditorPanel as EditorPanelBase,
  FileExplorer,
  formatReportConfirmation,
  KeyboardShortcutsPanel,
  PreviewPanel as PreviewPanelBase,
  QuickOpen,
  ReportModal,
  ResizeHandle,
  SearchPanel,
  ShareModal,
  SidebarTabs,
  useKeyboardShortcuts,
  WorkspaceLayout,
} from '@molecule/app-ide-react'
import {
  useAuth,
  useEditor,
  useEditorProvider,
  useHttpClient,
  usePreview,
  useStoragePreference,
  useThemeMode,
  useTranslation,
  useVersion,
} from '@molecule/app-react'
import { getPreferenceStore } from '@molecule/app-storage'
import { getClassMap } from '@molecule/app-ui'
import { Button } from '@molecule/app-ui-react'

import { dailyAiLimitRecentlyHit } from '../bonds/app-http.js'
import { adoptLoginSession } from '../bonds/auth-default.js'
import { APP_NAME, IDE_IDENTITY, PRODUCT_NAME } from '../branding.js'
import {
  AUTORUNS_OPEN_EVENT,
  IMPORT_REPO_CONNECT_RETURN_KEY,
  IMPORT_REPO_OPEN_EVENT,
  IMPORT_REPO_SKIP_EVENT,
  setChatCardCanEdit,
} from '../chat-custom-events.js'
import { ActivityPanel } from '../components/ActivityPanel.js'
import { confirmDialog, promptDialog } from '../components/AppDialogs.js'
// ApiKeysPanel import removed with the hidden API keys button/panel (2026-08-19,
// owner request "for now"); restore it alongside them to re-enable.
import { AuthModalCaptcha } from '../components/AuthModalCaptcha.js'
import { AutorunsPanel } from '../components/AutorunsPanel.js'
import { ProjectBackupSection } from '../components/BackupNowButton.js'
import { type BootWait, BootWaitNotice, parseBootWait } from '../components/BootWaitNotice.js'
import { BudgetResetBanner } from '../components/BudgetResetBanner.js'
import { attachCaptchaToken, isCaptchaConfigured } from '../components/captcha.js'
import { CaptchaGate } from '../components/CaptchaGate.js'
import { ConversationMenu, type ConversationSummary } from '../components/ConversationMenu.js'
import { DeployPanel } from '../components/DeployPanel.js'
import { EnvironmentPanel } from '../components/EnvironmentPanel.js'
import { GuestReminder } from '../components/GuestReminder.js'
import { Icon } from '../components/Icon.js'
import { ImportRepoModal } from '../components/ImportRepoModal.js'
import { InlineEditableTitle } from '../components/InlineEditableTitle.js'
import { LogsPanel } from '../components/LogsPanel.js'
import { MobileSheet } from '../components/MobileSheet.js'
import { ModelProviderModal } from '../components/ModelProviderModal.js'
import { MoleculeSpinner } from '../components/MoleculeSpinner.js'
import { PanelModal } from '../components/PanelModal.js'
import { PreviewBrokenOverlay } from '../components/PreviewBrokenOverlay.js'
import { ProfileModal, type ProfileView } from '../components/ProfileModal.js'
import {
  ProjectTestRecordHistory,
  ProjectTestRecordStatus,
  useProjectTestRecords,
} from '../components/ProjectTestRecords.js'
import { PublicBuildShareSection } from '../components/PublicBuildPanel.js'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner.js'
import { SandboxLostState } from '../components/SandboxLostState.js'
import { StoragePanel } from '../components/StoragePanel.js'
import { TeamPanel } from '../components/TeamPanel.js'
import { TerminalPanel } from '../components/TerminalPanel.js'
import { ToolbarIconButton } from '../components/ToolbarIconButton.js'
import { UpgradeModal, type UpgradeModalView } from '../components/UpgradeModal.js'
import { UsageNudge } from '../components/UsageNudge.js'
import { UserSettingsDrawer } from '../components/UserMenu.js'
import { WorkspaceMoreSheet } from '../components/WorkspaceMoreSheet.js'
import { WorkspaceThemeToggle } from '../components/WorkspaceThemeToggle.js'
import { API_BASE_URL, oauthConfig } from '../config.js'
import { usePublishFixedTopOffset } from '../hooks/useFixedTopOffset.js'
import { useIsMobile } from '../hooks/useMediaQuery.js'
import { usePageResume } from '../hooks/usePageResume.js'
import { usePreviewHealth } from '../hooks/usePreviewHealth.js'
import { useScreenWakeLock } from '../hooks/useScreenWakeLock.js'
import {
  onPageResume,
  RESUME_RECONCILE_AFTER_HIDDEN_MS,
  WS_BOUNCE_AFTER_HIDDEN_MS,
  WS_SERVER_REAPED_AFTER_MS,
} from '../lib/pageResume.js'
import { isToolingInternalError } from '../lib/previewErrorClassify.js'
import { composePreviewStuckMessage } from '../lib/previewStuckMessage.js'
import { listProjectTests, runProjectTests } from '../lib/projectTests.js'
import {
  endPromptMorph,
  getPromptMorph,
  movePromptMorph,
  usePromptMorph,
} from '../lib/promptMorph.js'
import type { PublicWorkspaceMode } from '../lib/public-workspace.js'
import { safeStorageGet, safeStorageSet } from '../lib/safe-storage.js'
import { useAdvancedMode, useKeepAwake } from '../lib/workspaceSettings.js'
import { ensureWsAuthToken, wsTokenIsUsable } from '../lib/wsAuth.js'
import { buildWsUrl } from '../lib/wsUrl.js'
import { emitNotification } from '../notifications.js'
import { billingCta } from '../upgrade-copy.js'
import { focusFromActivity } from '../utilities/activity.js'
import { createErrorView } from '../utilities/create-error.js'
import type { FileNode } from '../utilities/fileTree.js'
import { annotateGitStatus, injectChildrenInTree, mergeFileTree } from '../utilities/fileTree.js'
import { isGuestUser, stashGuestUserId } from '../utilities/guestClaim.js'
import { langFromPath } from '../utilities/langFromPath.js'
import { sanitizeAppBase } from '../utilities/preview-base.js'
import {
  notifyResourceInvalidated,
  onResourceInvalidated,
} from '../utilities/resource-invalidation.js'
import {
  isSandboxUnavailableBody,
  SANDBOX_CONCURRENCY_KEY,
  sandboxErrorAction,
  sandboxErrorCopy,
  sandboxErrorSentence,
} from '../utilities/sandbox-error-copy.js'
import { notifyUsageInvalidated } from '../utilities/usage-utilities.js'
import {
  activeMobileNav,
  MOBILE_NAV_TARGETS,
  type MobileNavTarget,
  type MobilePane,
  resolveMobileNav,
} from '../utilities/workspace-responsive.js'
import { nextPanel, type WorkspacePanel } from '../utilities/workspacePanels.js'

// Memoize heavy child components so they don't re-render when unrelated
// Workspace state changes (e.g. file tree updates that only affect FileExplorer).
const ChatPanel = memo(ChatPanelBase)
const EditorPanel = memo(EditorPanelBase)
const PreviewPanel = memo(PreviewPanelBase)

/** Bounded request-id memory shared by SSE/WS delivery and PreviewPanel's result callback. */
function rememberPreviewRequest(ids: Set<string>, requestId: string): boolean {
  if (ids.has(requestId)) return false
  ids.add(requestId)
  if (ids.size > 256) ids.delete(ids.values().next().value!)
  return true
}

/**
 * How often the file tree self-heals while the workspace tab is open and
 * visible (the "belt" to the explicit-broadcast "suspenders" — see
 * {@link https://github.com/molecule-dev/molecule-dev/blob/main/docs/mvp/FILE-WATCHER-ANALYSIS.md FILE-WATCHER-ANALYSIS.md}).
 *
 * The IDE file tree is kept in sync by per-mutation `file_change` broadcasts the
 * API does NOT buffer. If one is ever dropped — a new FS-mutating endpoint that
 * forgot to broadcast, or an event lost while the WebSocket was briefly down —
 * the tree would otherwise stay stale until a manual reload. A slow periodic
 * reconcile guarantees convergence within this interval instead. Kept in the
 * 30–60 s band (the analysis's recommended range): long enough that an idle,
 * open tab adds negligible traffic, short enough that a missed change surfaces
 * on its own without the user reaching for reload.
 */
export const FILE_TREE_RECONCILE_INTERVAL_MS = 45_000

/**
 * How many expanded directories the tree reconcile lists at once. Each listing
 * is several sandbox round trips on the API side, so the whole set in one burst
 * is a self-inflicted rate limit on a deep, fully-expanded tree.
 */
export const FILE_TREE_FETCH_BATCH = 8

/**
 * How often the IDE reconciles the sandbox STATUS while it believes the sandbox is
 * running (ms). This is the "belt" to the `sandbox_status` WS broadcast "suspenders":
 * the IDE otherwise learns of a hibernation ONLY via that one-shot broadcast, and the
 * file-watch WS does NOT close on hibernation (so the close-handler re-check never
 * fires) — so a broadcast missed because the WS was momentarily disconnected when the
 * server hibernated leaves the IDE stuck thinking the sandbox is awake, never offering
 * to wake it. A slow visible-tab poll converges the status on its own.
 */
export const SANDBOX_STATUS_RECONCILE_INTERVAL_MS = 45_000

/**
 * The document title captured at first module evaluation — i.e. the static
 * landing title from `index.html`, before any runtime mutation. The IDE swaps
 * `document.title` to reflect the open project (P2-01) and restores THIS on
 * unmount, so navigating back to the landing/dashboard reverts the tab title
 * instead of leaving a stale project name. The IDE chunk is lazy-loaded on
 * entering `/workspace/:projectId`, so this runs before the title is touched.
 */
const BASE_DOCUMENT_TITLE = typeof document !== 'undefined' ? document.title : ''

/**
 * File-explorer sidebar resize bounds + default (P4-01). The width is stateful
 * and draggable via a {@link ResizeHandle} (the SAME mechanism the chat split
 * uses), persisted per-project through the bonded storage provider; these clamp
 * the drag to a usable range so the editor always keeps room and the file names
 * stay readable.
 */
/**
 * How long the preview must stay `rendered` before the stuck-message dedupe re-arms
 * (see handlePreviewStuck / handleRenderState). Must exceed the freeze watchdog's
 * 8s threshold: an oscillating blocked thread yields short `rendered` gaps between
 * freezes, and an instant re-arm would re-send the same stuck report on every cycle.
 */
const STUCK_REARM_AFTER_RENDERED_MS = 30_000

const FILE_EXPLORER_MIN_WIDTH = 160
const FILE_EXPLORER_MAX_WIDTH = 480
const FILE_EXPLORER_DEFAULT_WIDTH = 220

/**
 * Clamps a persisted file-explorer width into the usable range.
 *
 * The provider stores the width as a number; legacy entries were a decimal
 * string, so both are accepted and anything unparseable (corrupt storage, no
 * value yet) falls back to the default.
 * @param stored - The persisted value, or `undefined` before hydration.
 * @returns A width the sidebar can use.
 */
function normalizeFileExplorerWidth(stored: number | undefined): number {
  const parsed = typeof stored === 'number' ? stored : Number.parseInt(String(stored ?? ''), 10)
  if (Number.isFinite(parsed)) {
    return Math.min(FILE_EXPLORER_MAX_WIDTH, Math.max(FILE_EXPLORER_MIN_WIDTH, parsed))
  }
  return FILE_EXPLORER_DEFAULT_WIDTH
}

/**
 * Sets the `--mol-fixed-bottom-offset` CSS variable while mounted, so
 * viewport-fixed chrome (HealthBanner, NotificationBanner, the PWA update
 * banner) anchors ABOVE the mobile bottom nav instead of overlapping it.
 * Rendered exactly beside the nav in the mobile branch — mount/unmount keeps
 * the offset in perfect sync with the nav's existence.
 * @returns Nothing renders.
 */
/**
 * Publishes the workspace header's bottom edge as `--mol-fixed-top-offset`
 * while mounted (rendered INSIDE the header on phones), so the fixed
 * HealthBanner slots below the header instead of covering it. Headerless
 * phases (discovery) never mount it and the banner sits at the very top.
 * @param props - Component props.
 * @param props.headerRef - Ref to the header element to measure.
 * @returns Nothing renders.
 */
function HeaderTopOffsetPublisher({
  headerRef,
}: {
  headerRef: React.RefObject<HTMLElement | null>
}): null {
  usePublishFixedTopOffset(headerRef, true)
  return null
}

function FixedBottomNavOffset(): null {
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--mol-fixed-bottom-offset',
      'calc(57px + env(safe-area-inset-bottom, 0px))',
    )
    return () => {
      document.documentElement.style.removeProperty('--mol-fixed-bottom-offset')
    }
  }, [])
  return null
}

interface Project {
  id: string
  name: string
  slug: string
  sandboxStatus?: string
  previewUrl?: string
  /** Set once discovery selects a starting point; presence means "ready to boot". */
  templateSlug?: string | null
  /** Settings blob — carries searchExcludes (VS Code search.exclude) among others. */
  settings?: { searchExcludes?: string[] } & Record<string, unknown>
  /**
   * The CALLER's role on this project, as `GET /projects/:id` reports it. A
   * `viewer` is read-only: the server 403s every project-settings write, so the
   * UI must hide/disable the controls that would 403 (model selection, autofix,
   * mode toggle, …). Absent for older payloads → treated as editable.
   */
  role?: 'owner' | 'admin' | 'editor' | 'viewer'
}

interface DirEntry {
  name: string
  type: 'file' | 'directory'
  symlinkTarget?: string
}

interface SandboxResponse {
  sandboxId: string
  status: string
  previewUrl?: string | null
  /** A wake/resume is in flight — the row's sleeping/stopped is not its outcome yet. */
  waking?: boolean
  /**
   * Why the boot is waiting, while the row is queued: the same payload the
   * boot stream's queue-wait events carry (`parseBootWait`), served durably so
   * a page whose SSE stream was cut (proxy timeout) still shows the notice.
   */
  wait?: unknown
}

/** Default labels for sandbox progress steps (i18n keys resolved at render time). */
/**
 * Boot progress step ids. Mirrors the api's `BootProgressStepId`
 * (`molecule-dev/api/src/sandbox/progress-steps.ts`) across the api↔app boundary,
 * which can't share types. `STEP_DEFAULTS` below is typed `Record<BootStepId,
 * string>`, so adding an id here without a matching label is a compile error —
 * the reason `importing`/`surveying`/`imported` once rendered as their raw
 * lowercase ids. Any id the frontend doesn't know yet is capitalized by
 * {@link stepLabel} rather than shown raw.
 */
type BootStepId =
  | 'provisioning'
  | 'creating'
  | 'scaffolding'
  | 'copying'
  | 'installing'
  | 'configuring'
  | 'caching'
  | 'starting'
  | 'waking'
  | 'resuming'
  | 'replicating'
  | 'importing'
  | 'surveying'
  | 'imported'
  | 'restoring'
  | 'waiting'

const STEP_DEFAULTS: Record<BootStepId, string> = {
  // The first step covers env assembly, capacity wait and (only for a project
  // type that has one) the database — a static site has no database, and the
  // old 'Provisioning database' label was a false statement on its boot screen.
  provisioning: 'Preparing environment',
  creating: 'Creating container',
  scaffolding: 'Scaffolding project',
  copying: 'Copying files to sandbox',
  installing: 'Installing dependencies',
  configuring: 'Configuring workspace',
  caching: 'Caching for next time',
  starting: 'Starting dev servers',
  waking: 'Waking sandbox',
  resuming: 'Starting container',
  replicating: 'Replicating project files',
  restoring: 'Restoring your project',
  // A held start: no checklist row (BootWaitNotice says why) — kept for the type.
  waiting: 'Waiting for a sandbox',
  importing: 'Importing repository',
  surveying: 'Surveying repository',
  imported: 'Imported',
}

/**
 * Human label for a boot progress step id. Known ids resolve via STEP_DEFAULTS;
 * an unknown id (api↔app drift on the SSE wire) is capitalized as a last resort
 * so it never renders raw-lowercase.
 *
 * @param id - The step id from the `progress` SSE event.
 * @returns The display label.
 */
function stepLabel(id: string): string {
  if (id in STEP_DEFAULTS) return STEP_DEFAULTS[id as BootStepId]
  return id.charAt(0).toUpperCase() + id.slice(1)
}

/**
 * The "synthesizing your molecule" status view — spinner, phase title, hint,
 * and the live progress-step checklist. Shown while a sandbox boots: in the
 * boot two-pane (where the preview will appear) and as a standalone fallback.
 *
 * @param root0 - Props.
 * @param root0.progressSteps - Ordered boot progress steps from the SSE stream.
 * @param root0.displayProgress - Smoothed 0–100 progress-bar value.
 * @param root0.sandboxCached - Whether the boot is from a cached image (null until known).
 * @param root0.sandboxPreWarmed - Whether a pre-warmed container was claimed.
 * @param root0.bootWait - Why the start is slow and when that was heard, or null.
 * @param root0.onCancelBoot - Cancels a start that is still waiting (offered on the wait notice).
 * @param root0.cancellingBoot - Whether that cancel is in flight.
 * @returns The synthesizing status view.
 */
function SynthesizingView({
  progressSteps,
  displayProgress,
  sandboxCached,
  sandboxPreWarmed,
  bootWait,
  onCancelBoot,
  cancellingBoot,
}: {
  progressSteps: Array<{ id: string; detail?: string }>
  displayProgress: number
  sandboxCached: boolean | null
  sandboxPreWarmed: boolean
  bootWait?: { wait: BootWait; receivedAt: number } | null
  onCancelBoot?: () => void
  cancellingBoot?: boolean
}) {
  const { t } = useTranslation()
  const isLight = useThemeMode() === 'light'
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
        color: 'var(--mol-color-text-secondary, #aaaaaa)',
      }}
    >
      <MoleculeSpinner size="lg" />
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--mol-color-text, #e0e0e0)' }}>
        {sandboxPreWarmed
          ? t('workspace.restoringPreWarmed', undefined, {
              defaultValue: 'Launching your molecule…',
            })
          : sandboxCached === false
            ? t('workspace.buildingFromScratch', undefined, {
                defaultValue: 'Synthesizing your molecule…',
              })
            : sandboxCached === true
              ? t('workspace.restoringCached', undefined, {
                  defaultValue: 'Replicating your molecule…',
                })
              : t('workspace.startingSandbox', undefined, { defaultValue: 'Starting sandbox…' })}
      </div>
      {sandboxCached !== null && (
        <div
          style={{ fontSize: 12, color: 'var(--mol-color-text-secondary, #888)', marginTop: -8 }}
        >
          {sandboxPreWarmed
            ? t('workspace.preWarmedHint', undefined, {
                defaultValue: "We've seen this one before — should be quick",
              })
            : sandboxCached
              ? t('workspace.cachedHint', undefined, {
                  defaultValue: "We've seen this one before — should be quick",
                })
              : t('workspace.uncachedHint', undefined, {
                  defaultValue: 'Unique configuration — this may take a minute',
                })}
        </div>
      )}
      {progressSteps.length > 0 && (
        <div
          style={{
            marginTop: 8,
            padding: '12px 16px',
            borderRadius: 10,
            background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minWidth: 240,
            textAlign: 'left',
          }}
        >
          <div
            style={{
              height: 3,
              borderRadius: 2,
              background: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
              overflow: 'hidden',
              marginBottom: 2,
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 2,
                background: 'var(--mol-color-primary, #4070e0)',
                width: `${Math.round(displayProgress)}%`,
                transition: 'width 150ms linear',
              }}
            />
          </div>
          {progressSteps.map((step, i) => {
            const isCurrent = i === progressSteps.length - 1
            const checkColor = isLight ? '#146800' : '#40b020'
            const label = t(`workspace.progress.${step.id}`, undefined, {
              defaultValue: stepLabel(step.id),
            })
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                {isCurrent ? (
                  <span style={{ marginLeft: -2 }}>
                    <MoleculeSpinner size="sm" />
                  </span>
                ) : (
                  <svg width={14} height={14} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
                    <circle
                      cx={7}
                      cy={7}
                      r={6}
                      fill={isLight ? 'rgba(20,104,0,0.1)' : 'rgba(64,176,32,0.15)'}
                      stroke={checkColor}
                      strokeWidth={1}
                    />
                    <path
                      d="M4 7.2 6 9.2 10 5"
                      fill="none"
                      stroke={checkColor}
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                <span
                  style={{
                    color: isCurrent
                      ? 'var(--mol-color-text, #e0e0e0)'
                      : 'var(--mol-color-text-secondary, #888)',
                  }}
                >
                  {label}
                  {step.detail && (
                    <span style={{ opacity: 0.5, fontWeight: 400 }}>{` — ${step.detail}`}</span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      )}
      {bootWait && (
        <BootWaitNotice
          wait={bootWait.wait}
          receivedAt={bootWait.receivedAt}
          onCancel={onCancelBoot}
          cancelling={cancellingBoot}
        />
      )}
    </div>
  )
}

/**
 * Approximate time-weights per step for each flow type.
 * Used to drive the progress bar proportionally to real wall-clock time
 * rather than a naive 1/N per step.
 */
const STEP_WEIGHTS: Record<string, Record<string, number>> = {
  uncached: {
    provisioning: 5,
    creating: 10,
    scaffolding: 15,
    copying: 5,
    installing: 40,
    configuring: 10,
    // No `caching` step: the cache-image snapshot now runs in the background and
    // never blocks dev-server start, so the boot goes straight to "starting".
    starting: 5,
  },
  cached: {
    provisioning: 8,
    creating: 12,
    replicating: 40,
    starting: 40,
  },
  preWarmed: {
    configuring: 50,
    starting: 50,
  },
  existing: {
    resuming: 25,
    waking: 25,
    starting: 75,
  },
}

/**
 * Compute progress range: `base` is the percentage from fully completed steps,
 * `ceiling` is the percentage if the current in-progress step were also done.
 * The displayed bar creeps from base toward ceiling while waiting for the next event.
 */
function computeProgressRange(
  steps: Array<{ id: string }>,
  cached: boolean | null,
  preWarmed?: boolean,
): { base: number; ceiling: number } {
  if (steps.length === 0) return { base: 0, ceiling: 0 }

  const weights = preWarmed
    ? STEP_WEIGHTS.preWarmed
    : cached === false
      ? STEP_WEIGHTS.uncached
      : cached === true
        ? STEP_WEIGHTS.cached
        : STEP_WEIGHTS.existing

  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0)
  let completed = 0
  for (let i = 0; i < steps.length - 1; i++) {
    completed += weights[steps[i].id] ?? 0
  }
  const currentWeight = weights[steps[steps.length - 1].id] ?? 0

  return {
    base: Math.round((completed / totalWeight) * 100),
    ceiling: Math.min(Math.round(((completed + currentWeight) / totalWeight) * 100), 95),
  }
}

/** The `t` shape this module's helpers need (a key + an English fallback). */
type Translate = (key: string, values?: undefined, options?: { defaultValue?: string }) => string

/**
 * Turn a failed file operation into one short sentence the user can act on.
 *
 * Every file-tree mutation and every auto-save used to fail into `console.error`
 * alone: a viewer-role collaborator's Delete did nothing at all, and their edits
 * kept auto-saving into 403s with the tab still marked dirty and no explanation
 * anywhere. The global HTTP interceptor does not cover these — it toasts only
 * 429, 5xx, and a 403 that carries a `limitType` — so the reason has to be said
 * here.
 *
 * @param error - The thrown HTTP error.
 * @param t - The translator.
 * @returns A single plain sentence naming the cause, or null when the global
 *   interceptor already said it (a sandbox-state refusal: asleep / booting /
 *   lost / the running-project limit — toasted once, with its action).
 */
function fileOpErrorReason(error: unknown, t: Translate): string | null {
  const status = (error as { status?: number } | null)?.status
  const errorKey = (error as { response?: { data?: { errorKey?: unknown } } } | null)?.response
    ?.data?.errorKey
  if (sandboxErrorCopy(typeof errorKey === 'string' ? errorKey : null)) return null
  if (status === 403) {
    return t('workspace.fileError.forbidden', undefined, {
      defaultValue: 'You have view-only access to this project.',
    })
  }
  if (status === 404) {
    return t('workspace.fileError.missing', undefined, {
      defaultValue: 'It no longer exists in the sandbox.',
    })
  }
  if (status === 409) {
    return t('workspace.fileError.conflict', undefined, {
      defaultValue: 'Something with that name is already there.',
    })
  }
  if (status === 413) {
    return t('workspace.fileError.tooLarge', undefined, {
      defaultValue: 'The file is too large.',
    })
  }
  if (status === 429) {
    return t('workspace.fileError.rateLimited', undefined, {
      defaultValue: 'Too many requests — try again in a moment.',
    })
  }
  if (status && status >= 500) {
    return t('workspace.fileError.sandbox', undefined, {
      defaultValue: 'The sandbox is not responding.',
    })
  }
  return t('workspace.fileError.generic', undefined, { defaultValue: 'The request was rejected.' })
}

export function Workspace({ publicWorkspace }: { publicWorkspace?: PublicWorkspaceMode } = {}) {
  const isPublicWorkspace = Boolean(publicWorkspace)
  const [publicCommandCopied, setPublicCommandCopied] = useState(false)
  const { t } = useTranslation()
  const cm = getClassMap()
  const { user } = useAuth<UserProfile>()
  // The real build version (same source the footer uses) — threaded to ChatPanel so the
  // /version command + its slash-menu description show the actual version (P4-08).
  const { state: versionState } = useVersion()
  const { projectId: routeProjectId } = useParams<{ projectId: string }>()
  const projectId = publicWorkspace?.projectId ?? routeProjectId
  const location = useLocation()
  const { state } = location
  const navigate = useNavigate()
  const rawInitialPrompt = (state as { initialPrompt?: string } | null)?.initialPrompt
  // Track whether the initial prompt has been consumed (sent by ChatPanel).
  // Once consumed, we never pass it again — prevents re-sending after sandbox
  // sleep/wake cycles which remount ChatInner and reset its sentInitialRef.
  const initialPromptConsumed = useRef(false)
  // Fall back to the bonded provider for the initial prompt — handles the case
  // where the sandbox couldn't start on first visit (e.g. free-user concurrency
  // limit) and the user navigated away and came back (router state lost). The
  // subscription means an ASYNCHRONOUS provider's hydration reaches ChatPanel
  // without a remount; a value written by the previous raw-string helper is
  // preserved by this app's storage codec, not reset.
  const { value: storedInitialPromptValue, removeValue: clearStoredInitialPrompt } =
    useStoragePreference<string>(
      projectId && projectId !== 'new' ? `mol_initial_prompt_${projectId}` : null,
    )
  const storedInitialPrompt =
    typeof storedInitialPromptValue === 'string' ? storedInitialPromptValue : null
  const initialPrompt =
    !isPublicWorkspace && !initialPromptConsumed.current
      ? rawInitialPrompt || storedInitialPrompt || undefined
      : undefined
  // Called by ChatPanel after it sends the initial message — marks it consumed
  // and clears it from router state so sleep/wake doesn't re-trigger.
  const onInitialMessageSent = useCallback(() => {
    if (!initialPromptConsumed.current) {
      initialPromptConsumed.current = true
      navigate(location.pathname, { replace: true, state: {} })
      if (projectId) clearStoredInitialPrompt()
    }
  }, [navigate, location.pathname, projectId, clearStoredInitialPrompt])
  const { setUrl, refresh: refreshPreview, setDevice, state: previewState } = usePreview()
  const [fileChangeTick, setFileChangeTick] = useState(0)
  // Current file the AI is editing — shown in the preview overlay ("Updating
  // `X`…") while a build reloads the iframe. Cleared a few seconds after edits
  // stop so the settled preview shows through.
  const [buildingHint, setBuildingHint] = useState<string | null>(null)
  const buildingHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // True while a chat turn (plan or build) is streaming — the authoritative "Synthase is
  // actively working" signal, surfaced by ChatPanel's onLoadingChange. Passed to PreviewPanel
  // as `isBuilding` so a half-built / blank preview during a long build keeps a "Building your
  // app…" overlay instead of a bare white screen (unlike `buildingHint`, which clears 3s after
  // the last file write and so drops the overlay during install/verify/think pauses).
  const [aiBuilding, setAiBuilding] = useState(false)
  const { openFile, setActiveTab, pinTab } = useEditor()
  const provider = useEditorProvider()
  const [activeFile, setActiveFile] = useState<string | null>(null)
  const [openFilePaths, setOpenFilePaths] = useState<string[]>([])
  const handleTabsChange = useCallback((paths: string[]) => {
    setOpenFilePaths(paths)
    // Clear synthetic 'deleted' status for tabs that are no longer open
    const openSet = new Set(paths)
    setFileStatuses((prev) => {
      let changed = false
      for (const [p, s] of Object.entries(prev)) {
        if (s === 'deleted' && !openSet.has(p)) changed = true
      }
      if (!changed) return prev
      const next: Record<string, string> = {}
      for (const [p, s] of Object.entries(prev)) {
        if (s === 'deleted' && !openSet.has(p)) continue
        next[p] = s
      }
      return next
    })
  }, [])
  const [createError, setCreateError] = useState<{
    message: string
    requiresSignup?: boolean
  } | null>(null)
  // Bot/abuse protection (P2-26): a guest's first prompt must pass a human-proof
  // challenge BEFORE the project is auto-created (which provisions a guest
  // session + spends free AI). Gate ONLY guests (anonymous) on a configured
  // captcha — signed-in users already cleared it at signup, and the server's
  // requireCaptcha gate is itself inert without keys, so this is fully inert in
  // dev/local and a no-op for authed users.
  const captchaRequired = !isPublicWorkspace && isCaptchaConfigured() && !user
  const [captchaCleared, setCaptchaCleared] = useState(false)
  // The solved token, handed over EXPLICITLY. The shared captcha store cannot
  // carry it here: flipping `captchaCleared` unmounts the CaptchaGate, whose
  // effect cleanup runs clearCaptchaToken() BEFORE this component's create
  // effect re-runs (React tears down unmounting children first) — so
  // attachCaptchaToken() read an EMPTY store and every guest create POSTed
  // without a token. Verified against production: the challenge solved, the
  // gate cleared, and the create body carried no captchaToken.
  const guestCaptchaTokenRef = useRef<string | null>(null)
  // Separate from the on-ramp gate above: the in-IDE auth modal runs its OWN
  // challenge. A captcha token is single-use server-side, so the guest-project
  // create and the signup POST cannot share one, and the on-ramp gate has
  // already unmounted (clearing the store) by the time the modal opens.
  const [authModalCaptchaSolved, setAuthModalCaptchaSolved] = useState(false)
  const [editorReady, setEditorReady] = useState(false)
  const handleEditorReady = useCallback(() => setEditorReady(true), [])
  // Set to true once we've attempted tab restoration — prevents the save effect
  // from overwriting localStorage with an empty tab list before restore runs.
  const restoredRef = useRef(false)
  // Track whether the sandbox was previously running — used to detect sleep/stop
  // transitions so we can reset restoration state for the next wake.
  const prevRunningRef = useRef(false)
  // True while previously-saved tabs are being fetched and opened. Initialized
  // eagerly from the persisted state so the overlay appears on first render (no
  // flash).
  const [restoring, setRestoring] = useState<boolean>(() => {
    if (!projectId || projectId === 'new') return false
    // Absent or corrupt reads as null — safe to default to false.
    const saved = getPreferenceStore().get<{ paths?: string[] }>(`mol_editor_tabs_${projectId}`)
    return !!saved?.paths?.length
  })
  const http = useHttpClient()

  // molecule.dev's own slash commands (deploy/push/invite/teamsay) — fetched from
  // the API's single source (GET /ai/commands) and passed to ChatPanel as
  // `extraCommands`, so the slash menu always lists them and never drifts behind
  // the server. Static per session; a failed fetch just leaves the menu without
  // them (never blocks chat).
  const [moleculeCommands, setMoleculeCommands] = useState<CommandDef[]>([])
  useEffect(() => {
    let cancelled = false
    http
      .get<{ commands: CommandDef[] }>('/ai/commands')
      .then((res) => {
        if (!cancelled) setMoleculeCommands(res.data?.commands ?? [])
      })
      .catch((error) => {
        console.error('Failed to load molecule.dev slash commands', error)
      })
    return () => {
      cancelled = true
    }
  }, [http])

  // Cross-origin WebSocket auth. Sockets connect DIRECTLY to the API origin
  // (Netlify can't proxy a WS upgrade), so they need a bearer token in the URL.
  // Logged-in users already have one (getAuthToken); GUESTS auth only via the
  // httpOnly cookie, which the cross-origin socket won't carry — so fetch a
  // short-lived WS ticket (authed by the cookie over the same-origin /api proxy)
  // and prime the http client with it. WS effects gate on `wsAuthReady` so the
  // FIRST connect already carries the token instead of racing a tokenless one.
  // The ticket is short-lived (6h) and every socket (re)connect below goes
  // through `ensureWsAuthToken`, which re-mints it from the cookie once it has
  // expired — a tab left open overnight used to reconnect every socket with a
  // dead ticket and never come back alive until a manual refresh.
  const [wsAuthReady, setWsAuthReady] = useState(
    () => !isPublicWorkspace && wsTokenIsUsable(http.getAuthToken()),
  )
  useEffect(() => {
    if (isPublicWorkspace) return
    if (wsAuthReady) return
    // A still-usable token (e.g. primed by the in-IDE login) — ready immediately.
    if (wsTokenIsUsable(http.getAuthToken())) {
      setWsAuthReady(true)
      return
    }
    // Guests authenticate only via the httpOnly session cookie, which is set
    // when the project (and its guest session) is created — fetching the WS
    // ticket before a real projectId exists 401s. Wait for it, THEN mint the
    // ticket. (This effect re-runs when projectId flips from 'new' to the real
    // id after creation.) A failed mint (session gone) still flips ready: the
    // sockets connect tokenless and the handshake rejects them — never worse
    // than before (guest WS just stays degraded).
    if (!projectId || projectId === 'new') return
    let cancelled = false
    void ensureWsAuthToken(http).finally(() => {
      if (!cancelled) setWsAuthReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [wsAuthReady, http, projectId])

  // Auto-save: debounce dirty file changes, write to sandbox, then auto-format
  const saveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const formatVersions = useRef<Map<string, number>>(new Map())
  const [formattingFile, setFormattingFile] = useState<string | null>(null)
  // Countdown state: which file has an active debounce, and a key to restart the CSS animation
  const [countdownFile, setCountdownFile] = useState<string | null>(null)
  const countdownKeyRef = useRef(0)
  const [countdownKey, setCountdownKey] = useState(0)
  // "Fix with AI" pending message state — incremented key triggers auto-send in ChatPanel
  const [fixWithAIMessage, setFixWithAIMessage] = useState<string | undefined>(undefined)
  const fixWithAIKeyRef = useRef(0)
  const [fixWithAIKey, setFixWithAIKey] = useState(0)
  // True only for the auto-sent build kickoff, so it isn't rendered as a user
  // message (the phase cards convey what's happening). Fix-with-AI messages are
  // user-initiated and stay visible.
  const [pendingSuppressUser, setPendingSuppressUser] = useState(false)
  // True when the pending message was DIRECTLY requested by the user (the
  // editor's / broken-preview overlay's "Fix with AI" buttons) rather than
  // dispatched autonomously (preview-error / preview-stuck / health auto-fix).
  // A user Stop suppresses autonomous automatic sends until the user re-engages;
  // a user-initiated dispatch IS that re-engagement, so it must still send.
  const [pendingUserInitiated, setPendingUserInitiated] = useState(false)
  // User-edited file state — triggers auto-deletion of queued autofix messages in ChatPanel
  const [userEditedFile, setUserEditedFile] = useState<string | undefined>(undefined)
  const userEditedFileKeyRef = useRef(0)
  const [userEditedFileKey, setUserEditedFileKey] = useState(0)
  // Repo-import modal (GIT2) — opened by the chat import_repo card via window
  // events; its outcome resumes the pending request_repo_import tool through
  // the same programmatic-send channel as the build kickoff.
  const [importRepoOpen, setImportRepoOpen] = useState(false)
  // True once the AI has started building (first file write). Until then the
  // app is just the freshly-scaffolded template settling during boot/planning,
  // which throws transient errors — auto-fixing those before there's even a
  // plan derails the whole flow (the AI flips to execute mode to "fix" a
  // template error it knows nothing about). Preview-error/stuck auto-sends are
  // gated on this so they only fire during genuine post-build development.
  const aiWroteFilesRef = useRef(false)
  // Set when discovery ends and the sandbox is booting; consumed once the
  // sandbox is running to auto-kick the plan→build turn (otherwise the AI sits
  // idle after boot with no trigger to actually build).
  const buildKickoffPendingRef = useRef(false)
  // State mirror of buildKickoffPendingRef for rendering: true from ready_to_build
  // (plan done, sandbox booting) until the post-boot kickoff fires. Drives the
  // chat's "waiting for the environment" indicator so the conversation doesn't
  // look stalled between the plan finishing and the build starting.
  const [awaitingSandboxBoot, setAwaitingSandboxBoot] = useState(false)
  const handleFixWithAI = useCallback((request: FixWithAIRequest) => {
    const parts: string[] = []

    if (request.diagnostics.length > 0) {
      // Has diagnostics — ask AI to fix them
      const diagLines = request.diagnostics.map(
        (d) => `- **${d.severity}** (line ${d.startLine}): ${d.message}`,
      )
      parts.push(
        `Fix the following issue${request.diagnostics.length > 1 ? 's' : ''} in \`${request.path}\`:`,
        ...diagLines,
      )
    } else {
      // No diagnostics — general "help with this code" context
      const lineInfo = request.line ? ` (line ${request.line})` : ''
      parts.push(`I have a question about this code in \`${request.path}\`${lineInfo}:`)
    }

    if (request.selectedCode) {
      parts.push('', 'Selected code:', '```', request.selectedCode, '```')
    }
    if (request.codeContext) {
      parts.push('', 'Surrounding context:', '```', request.codeContext, '```')
    }

    setPendingSuppressUser(false)
    // The editor's "Fix with AI" action is a direct user request — it bypasses
    // (and clears) a prior user Stop.
    setPendingUserInitiated(true)
    setFixWithAIMessage(parts.join('\n'))
    fixWithAIKeyRef.current++
    setFixWithAIKey(fixWithAIKeyRef.current)
    // Phones: reveal the conversation the request streams into — without this
    // the tap appears to do nothing (the chat pane is the hidden surface).
    // (Declared later in the component; safe — the closure runs post-render.)
    setMobilePane('chat')
  }, [])

  // "Fix with Synthase" on a failed deploy (DEPLOY1): seed the chat with the
  // build errors + "fix and redeploy" (built by DeployPanel), close the Pipeline
  // modal, and reveal the chat. Same direct-user seed channel as handleFixWithAI;
  // Synthase then uses the `deploy` tool to redeploy once it has fixed the code.
  const handleFixDeployWithSynthase = useCallback((prompt: string) => {
    setPendingSuppressUser(false)
    setPendingUserInitiated(true)
    setFixWithAIMessage(prompt)
    fixWithAIKeyRef.current++
    setFixWithAIKey(fixWithAIKeyRef.current)
    // Leave the modal so the user lands on the conversation (phones need the pane
    // switch too). Both setters are declared later; the closure runs post-render.
    setActivePanel(null)
    setMobilePane('chat')
  }, [])

  // Repo-import card bridge (GIT2): the chat card's actions dispatch window
  // events (see chat-custom-events.ts); the modal's outcome — or a skip —
  // resumes the pending request_repo_import tool via the programmatic-send
  // channel (suppressed user bubble: the answer folds into the tool result).
  const sendImportResume = useCallback((message: string) => {
    setImportRepoOpen(false)
    setPendingSuppressUser(true)
    // The import modal's outcome IS a direct user action (they connected a repo
    // or clicked skip) — flag it so the user-stop gate never swallows it.
    setPendingUserInitiated(true)
    setFixWithAIMessage(message)
    fixWithAIKeyRef.current++
    setFixWithAIKey(fixWithAIKeyRef.current)
  }, [])
  useEffect(() => {
    const open = (): void => setImportRepoOpen(true)
    const skip = (): void =>
      sendImportResume(
        t('importRepo.skipMessage', undefined, {
          defaultValue: "Skip the import — let's work here without importing my code.",
        }),
      )
    window.addEventListener(IMPORT_REPO_OPEN_EVENT, open)
    window.addEventListener(IMPORT_REPO_SKIP_EVENT, skip)
    return () => {
      window.removeEventListener(IMPORT_REPO_OPEN_EVENT, open)
      window.removeEventListener(IMPORT_REPO_SKIP_EVENT, skip)
    }
  }, [sendImportResume, t])

  // Git-provider OAuth return (?gitAuth=…&gitStatus=…). The API callback
  // redirects back to THIS workspace; surface the outcome, strip the params,
  // and — when the departure was the import modal's Connect button (session
  // flag) — reopen the modal so the user resumes the import mid-flow, now
  // connected. Deploy-panel connects never set the flag: notice only.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const gitAuth = params.get('gitAuth')
    const gitStatus = params.get('gitStatus')
    if (!gitAuth || !gitStatus) return
    params.delete('gitAuth')
    params.delete('gitStatus')
    const rest = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`)

    const provider = gitAuth === 'github' ? 'GitHub' : gitAuth === 'gitlab' ? 'GitLab' : gitAuth
    if (gitStatus === 'connected') {
      emitNotification(
        t('git.connectedNotice', { provider }, { defaultValue: `Connected to ${provider}.` }),
        'info',
      )
      if (sessionStorage.getItem(IMPORT_REPO_CONNECT_RETURN_KEY) === projectId) {
        setImportRepoOpen(true)
      }
    } else if (gitStatus === 'unconfigured') {
      emitNotification(
        t(
          'git.unconfigured',
          { provider },
          {
            defaultValue: `This server has no ${provider} app configured. Ask the operator to set up a git provider app.`,
          },
        ),
        'error',
      )
    } else {
      emitNotification(
        t(
          'git.connectError',
          { provider },
          { defaultValue: `Could not connect to ${provider}. Please try again.` },
        ),
        'error',
      )
    }
    sessionStorage.removeItem(IMPORT_REPO_CONNECT_RETURN_KEY)
  }, [projectId, t])

  // When the preview last looked broken (runtime error reported from the iframe,
  // or stuck after PreviewPanel's recovery cycles). The agent's reload_preview is
  // only honored within a short window of this — the preview hot-reloads (and
  // Vite full-reloads itself when needed) on every edit, so a reload of a HEALTHY
  // preview is always redundant and just wipes the user's app state.
  const previewUnhealthyAtRef = useRef(0)
  // Live mirror of the project's /autofix setting (from the health stream), read by the
  // PreviewPanel runtime-failure callbacks below — they're declared before usePreviewHealth,
  // so they can't read previewHealth.autoFix directly. Re-synced from previewHealth.autoFix
  // on every render (see below), so it tracks the SAME value the health-poll path gates on
  // (which defaults on until the project's setting resolves; the not-yet-built guard,
  // aiWroteFilesRef, prevents premature dispatches during the initial boot window). The
  // broken overlay/banner surfaces problems regardless of this gate.
  const previewAutoFixRef = useRef(true)
  // Preview error auto-send — writes errors to the sandbox as a file so the
  // auto-verification loop picks them up. Cancelled if the AI writes files
  // (which likely fixes the error) before the debounce fires.
  const previewErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previewErrorSendsRef = useRef<number[]>([]) // timestamps of recent sends
  const MAX_PREVIEW_ERROR_SENDS = 3
  const PREVIEW_ERROR_WINDOW_MS = 10_000 // 3 sends per 10 seconds

  // Last time we auto-reloaded the preview to heal a stale-module-import error —
  // gates the one-free-reload recovery in handlePreviewError below.
  const staleImportReloadAtRef = useRef(0)

  const handlePreviewError = useCallback(
    (errors: Array<{ message: string; source?: string; line?: number; column?: number }>) => {
      if (!projectId || sandboxStatusRef.current !== 'running') return
      // Drop dev-tooling-INTERNAL errors before anything else: Vite's HMR client
      // (@vite/client), the react-refresh runtime, the Vite error overlay, and
      // browser-extension scripts. These fire transiently on an HMR reconnect /
      // dev-server bounce and self-resolve — they are NOT app bugs, so they must
      // NOT stamp the preview unhealthy or dispatch a fix turn. (Live case: a
      // completed build+handoff forwarded "Cannot read properties of undefined
      // (reading 'frame')" from @vite/client:798 → a ~10-message goose chase.)
      // A MIXED batch keeps the genuine app errors and drops only the tooling ones;
      // if nothing real remains, bail without touching the health/auto-fix path.
      const realErrors = errors.filter((e) => !isToolingInternalError(e))
      if (realErrors.length === 0) {
        if (errors.length > 0) {
          console.debug(
            '[preview] dropped dev-tooling-internal error(s), not an app bug — no fix turn:',
            errors.map((e) => e.message),
          )
        }
        return
      }
      // Health stamp for the agent reload gate (see handleClientAction): a
      // runtime error marks the preview unhealthy regardless of the auto-send
      // gating below.
      previewUnhealthyAtRef.current = Date.now()
      // Stale-module short-circuit: after a dev-server bounce, the page loaded in
      // the iframe still references the OLD vite instance's module URLs, so its
      // imports fail with "Failed to fetch dynamically imported module". That is
      // stale BROWSER state a reload fixes — not a code bug — but the generic
      // "[Preview Error] investigate and fix" message sent the executor hunting
      // phantom import/syntax causes (and killing dev servers). Auto-reload once;
      // only when the same signature recurs right after our reload (a genuinely
      // broken chunk also matches) does it escalate to an AI fix turn below.
      const STALE_IMPORT_RE =
        /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|ChunkLoadError/i
      const staleImport = realErrors.some((e) => STALE_IMPORT_RE.test(e.message))
      if (staleImport && Date.now() - staleImportReloadAtRef.current > 20_000) {
        staleImportReloadAtRef.current = Date.now()
        refreshPreview()
        return
      }
      // Don't chase errors from the not-yet-built template (see aiWroteFilesRef).
      if (!aiWroteFilesRef.current) return
      // A read-only viewer's tab never seeds an autonomous fix turn — gate at the
      // SOURCE (same as fireBuildKickoff), not only at ChatPanel's pendingMessage
      // consumer, so the dispatch never depends on a single downstream check.
      if (!canEditProjectRef.current) return
      // Autonomous fix is gated on the /autofix setting — same rule as the health-poll
      // and stuck paths. When off, the error still stamps the preview unhealthy (above)
      // and surfaces in the UI, but we don't dispatch a fix turn on the user's behalf.
      if (!previewAutoFixRef.current) return

      // Rate limit: max N sends within a rolling time window
      const now = Date.now()
      previewErrorSendsRef.current = previewErrorSendsRef.current.filter(
        (ts) => now - ts < PREVIEW_ERROR_WINDOW_MS,
      )
      if (previewErrorSendsRef.current.length >= MAX_PREVIEW_ERROR_SENDS) return

      // Cancel any pending send — a new batch replaces it
      if (previewErrorTimerRef.current) clearTimeout(previewErrorTimerRef.current)

      // Debounce: wait 5s before sending. If the AI writes files in that window
      // (handleFileChange fires), we cancel — the error is likely being fixed.
      previewErrorTimerRef.current = setTimeout(() => {
        previewErrorTimerRef.current = null
        // Re-checked at fire time (the limit can land during the debounce): out of free
        // AI usage — the dispatch cannot start a turn, it would only spam the chat.
        if (dailyAiLimitRecentlyHit()) return
        previewErrorSendsRef.current.push(Date.now())

        const lines = realErrors
          .slice(0, 5)
          .map((e) => {
            const loc = e.source
              ? ` (${e.source.replace(/^https?:\/\/[^/]+\//, '')}${e.line ? `:${e.line}` : ''})`
              : ''
            return `- ${e.message}${loc}`
          })
          .join('\n')

        const reloadNote = staleImport
          ? '\n\nNote: the IDE already auto-reloaded the preview once and this error came back, ' +
            'so a module genuinely fails to load — do NOT reload again or restart/kill dev ' +
            'servers; find the module and fix its import/build error.'
          : ''
        // Server-module-in-browser class. Two DELIBERATELY structural signals (mirrored in
        // the api's read_logs bundlingNote — keep in sync):
        //  1. a node-injected GLOBAL missing at runtime — a CLOSED set (browsers lack exactly
        //     what node injects), so this is not an open-ended symptom list;
        //  2. Vite's own canonical marker for every OTHER manifestation ("Module X has been
        //     externalized for browser compatibility") — the engine names the class + the
        //     module itself, covering all node builtins (fs/net/crypto/stream/…) without us
        //     enumerating symptoms.
        // Without this note the executor burns dozens of turns polyfill-chasing; the real fix
        // is always the import graph. (Observed live: a pg import reachable from a TanStack
        // Start route component → ~30 messages of churn.)
        const serverModule = realErrors.some(
          (e) =>
            /\b(Buffer|process|global|require|module|exports|__dirname|__filename|setImmediate) is not defined\b/.test(
              e.message,
            ) || /has been externalized for browser compatibility/i.test(e.message),
        )
        const serverModuleNote = serverModule
          ? '\n\nNote: this error class ("<node global> is not defined" / "Module … has been ' +
            'externalized for browser compatibility") means a SERVER-ONLY module (pg, node ' +
            'builtins, most @molecule/api-*) was bundled into the client. Fix the IMPORT ' +
            'GRAPH, not the symbol: move that import into a file only server code reaches ' +
            '(the server route / server-function file itself), or dynamic-import it INSIDE ' +
            'the server handler. Do NOT add Buffer/process polyfills or node-polyfill vite ' +
            'plugins — that ships server code (and its secrets) to the browser. The vite log ' +
            '(read_logs source "app") names the externalized module and who imports it.'
          : ''
        const msg = `[Preview Error] The live preview reported runtime errors:\n${lines}\n\nPlease investigate and fix these errors.${reloadNote}${serverModuleNote}`
        setPendingSuppressUser(false)
        setPendingUserInitiated(false) // autonomous dispatch — a user Stop suppresses it
        setFixWithAIMessage(msg)
        fixWithAIKeyRef.current++
        setFixWithAIKey(fixWithAIKeyRef.current)
      }, 5000)
    },
    [projectId, refreshPreview],
  )

  // While an imported project's dependency install runs in the BACKGROUND (the boot
  // completes after clone+survey; the SSE 'done' carries installPending), the preview
  // CANNOT be up yet — vite needs node_modules. Suppress the stuck-preview auto-fix
  // until the post-install launch's wake broadcast (or a 10-min safety ceiling):
  // without this, the watchdog accused the not-yet-launched preview of crashing and
  // auto-dispatched Synthase to "fix" it — which is how the planner ended up running
  // a SECOND npm install against the one already going.
  const importInstallPendingUntilRef = useRef(0)
  // Poll fallback for the one-shot `woke` broadcast: the server drops it
  // silently when this tab's WS isn't subscribed at that instant (observed
  // live: app served at 13:17:36, panel recovered at 13:19:57 on retry
  // backoff). While the install-pending hold is active we poll sandbox/status
  // for the DURABLE previewReadyAt stamp and treat it as the wake.
  const installReadyPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const clearInstallReadyPoll = useCallback(() => {
    if (installReadyPollRef.current) {
      clearInterval(installReadyPollRef.current)
      installReadyPollRef.current = null
    }
  }, [])
  useEffect(() => clearInstallReadyPoll, [clearInstallReadyPoll])

  // Preview stuck auto-send — fires when the preview gives up showing the running app:
  // exhausted reload recovery, the absolute readiness ceiling, OR a frozen (locked-thread)
  // app the in-iframe bridge can't report. Rate-limited: max 2 sends per 30 seconds, AND
  // deduped: an IDENTICAL report is dispatched at most once until the preview has stayed
  // rendered for a sustained window (see handleRenderState). The dedupe matters because a
  // blocked main thread that occasionally yields one heartbeat oscillates
  // frozen→rendered→frozen every ~10s, and each false→true transition re-fires the
  // watchdog's once-per-episode report — without the dedupe the SAME stuck message
  // spammed the chat indefinitely (worst with the AI budget exhausted, where no fix turn
  // can ever break the loop).
  const previewStuckSendsRef = useRef<number[]>([])
  const lastStuckSentMsgRef = useRef<string | undefined>(undefined)
  const stuckRearmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (stuckRearmTimerRef.current) clearTimeout(stuckRearmTimerRef.current)
    },
    [],
  )
  const handlePreviewStuck = useCallback(
    (report?: PreviewStuckReport) => {
      if (!projectId || sandboxStatusRef.current !== 'running') return
      // A HIDDEN tab must never dispatch an autonomous fix: its timers are
      // throttled (stuck detectors misfire on the throttling itself — the
      // once-a-minute false "frozen" loop observed 2026-08-29), nobody is
      // looking at its preview, and a backgrounded tab dispatching alongside
      // the foreground one would double-send. The foreground tab covers it.
      if (document.visibilityState === 'hidden') return
      // The background dependency install hasn't finished — a dead preview is the
      // EXPECTED state, not a failure to dispatch Synthase at.
      if (Date.now() < importInstallPendingUntilRef.current) return
      // Health stamp for the agent reload gate (see handleClientAction).
      previewUnhealthyAtRef.current = Date.now()
      // Don't chase errors from the not-yet-built template (see aiWroteFilesRef).
      if (!aiWroteFilesRef.current) return
      // A read-only viewer's tab never seeds an autonomous fix turn (gate at the
      // source, same as fireBuildKickoff — never rely on the downstream consumer).
      if (!canEditProjectRef.current) return
      // Autonomous fix is gated on the /autofix setting (the banner/overlay surfaces the
      // problem regardless; a manual "Fix with AI" stays available when this is off).
      if (!previewAutoFixRef.current) return
      // Out of free AI usage — a dispatch cannot start a turn; it would only append the
      // report plus a daily-limit error to the chat. The frozen banner/overlay still
      // surfaces the problem and manual "Fix with AI" stays available.
      if (dailyAiLimitRecentlyHit()) return
      // Identical report already dispatched and the preview has not sustainably
      // recovered since — Synthase is either already on it or was unable to act;
      // re-sending the same words adds nothing.
      const msg = composePreviewStuckMessage(report)
      if (msg === lastStuckSentMsgRef.current) return

      const now = Date.now()
      previewStuckSendsRef.current = previewStuckSendsRef.current.filter((ts) => now - ts < 30_000)
      if (previewStuckSendsRef.current.length >= 2) return
      previewStuckSendsRef.current.push(now)
      lastStuckSentMsgRef.current = msg

      // The message is TARGETED at the failure class (Rule 11: the agent should be told
      // HOW it broke, not just "it's broken") + the route it happened on, so the fix can be
      // aimed at the right component instead of a generic console-hunt.
      setPendingSuppressUser(false)
      setPendingUserInitiated(false) // autonomous dispatch — a user Stop suppresses it
      setFixWithAIMessage(msg)
      fixWithAIKeyRef.current++
      setFixWithAIKey(fixWithAIKeyRef.current)
    },
    [projectId],
  )

  // Keep only the CURRENT verdict. The server asks for it with a request id during post-build
  // verification; unsolicited reports have no server-side authority (see handleClientAction).
  //
  // Also re-arms the stuck-message dedupe (see handlePreviewStuck): only after the
  // preview has stayed `rendered` for a SUSTAINED window is the last stuck report
  // forgotten, so a later, genuinely new freeze can be reported again. The window must
  // exceed the freeze watchdog's threshold (8s) because an oscillating blocked thread
  // yields short `rendered` gaps between freezes — an instant re-arm on the first
  // `rendered` verdict is exactly the loop the dedupe exists to break.
  const previewRenderRef = useRef<{ state: PreviewRenderState; url?: string }>({
    state: 'loading',
  })
  const handledPreviewRequestsRef = useRef(new Set<string>())
  const answeredPreviewRequestsRef = useRef(new Set<string>())
  const pendingRenderRequestsRef = useRef(new Set<string>())
  const previewUiResultHandlerRef = useRef<
    (id: string, result: PreviewUiResult | { verdict: PreviewRenderState; url?: string }) => void
  >(() => {})
  useEffect(() => {
    // A Workspace reused for another project must never answer with the previous project's frame.
    previewRenderRef.current = { state: 'loading' }
    handledPreviewRequestsRef.current.clear()
    answeredPreviewRequestsRef.current.clear()
    pendingRenderRequestsRef.current.clear()
  }, [projectId])
  const handleRenderState = useCallback((renderState: PreviewRenderState, url?: string) => {
    previewRenderRef.current = { state: renderState, ...(url ? { url } : {}) }
    if (renderState !== 'loading' && pendingRenderRequestsRef.current.size > 0) {
      const requestIds = [...pendingRenderRequestsRef.current]
      pendingRenderRequestsRef.current.clear()
      for (const requestId of requestIds) {
        previewUiResultHandlerRef.current(requestId, {
          verdict: renderState,
          ...(url ? { url } : {}),
        })
      }
    }
    if (renderState === 'rendered') {
      if (!stuckRearmTimerRef.current && lastStuckSentMsgRef.current !== undefined) {
        stuckRearmTimerRef.current = setTimeout(() => {
          stuckRearmTimerRef.current = null
          lastStuckSentMsgRef.current = undefined
        }, STUCK_REARM_AFTER_RENDERED_MS)
      }
    } else if (stuckRearmTimerRef.current) {
      clearTimeout(stuckRearmTimerRef.current)
      stuckRearmTimerRef.current = null
    }
  }, [])

  // Rolling average of format duration (ms) for accurate progress bar
  const formatDurationsRef = useRef<number[]>([])
  const [formatEstimate, setFormatEstimate] = useState(2000)
  useEffect(() => {
    // No tracing here: onChange fires on EVERY keystroke (the editor bond
    // notifies per model change, undebounced), so a log line on this path is the
    // dominant console traffic of an editing session for every user with devtools
    // open — and it prints their workspace file paths.
    return provider.onChange((event) => {
      if (!projectId || projectId === 'new') return
      // Viewer belt-over-suspenders: Monaco is readOnly for viewers, but any
      // programmatic model change must not enqueue a save the server would 403.
      if (!canEditProjectRef.current) return
      const tab = provider.getTabs().find((t) => t.path === event.path)
      if (!tab?.isDirty) return
      if (tab.isDiff) return

      // Track version for stale-format detection
      formatVersions.current.set(event.path, event.version)

      const existing = saveTimers.current.get(event.path)
      if (existing) clearTimeout(existing)

      // Dismiss formatting toast if still showing, and start/restart countdown
      setFormattingFile(null)
      countdownKeyRef.current++
      setCountdownFile(event.path)
      setCountdownKey(countdownKeyRef.current)

      const timer = setTimeout(() => {
        saveTimers.current.delete(event.path)
        setCountdownFile(null)
        if (sandboxStatusRef.current !== 'running') return
        const apiPath = event.path.replace(/^\/workspace\//, '')
        const saveVersion = event.version
        // Notify ChatPanel so queued autofix messages referencing this file are auto-deleted
        setUserEditedFile(apiPath)
        userEditedFileKeyRef.current++
        setUserEditedFileKey(userEditedFileKeyRef.current)
        http
          .put(`/projects/${projectId}/files/${apiPath}`, { content: event.content })
          .then(() => {
            provider.markSaved?.(event.path)

            // Auto-format: fire-and-forget, non-blocking
            const formatStart = Date.now()
            setFormattingFile(event.path)
            http
              .post<{ path: string; content: string; changed: boolean }>(
                `/projects/${projectId}/format/${apiPath}`,
              )
              .then((res) => {
                // Track duration for rolling average
                const elapsed = Date.now() - formatStart
                const durations = formatDurationsRef.current
                durations.push(elapsed)
                if (durations.length > 5) durations.shift()
                const avg = Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
                setFormatEstimate(avg)

                if (!res.data.changed) {
                  // No format change — refresh git status now (save is the final write)
                  setGitStatusTick((n) => n + 1)
                  return
                }

                // Stale check: if user typed since save started, discard
                const currentVersion = formatVersions.current.get(event.path)
                if (currentVersion !== undefined && currentVersion !== saveVersion) {
                  setGitStatusTick((n) => n + 1)
                  return
                }

                if (provider.setContentSilent) {
                  provider.setContentSilent(event.path, res.data.content)
                }
                // Format wrote new content — refresh git status after format settles
                setGitStatusTick((n) => n + 1)
              })
              .catch((err) => {
                console.error('[auto-format] FAILED', apiPath, err)
                // Format failed — still refresh from the save that succeeded
                setGitStatusTick((n) => n + 1)
              })
              .finally(() => {
                setTimeout(() => setFormattingFile(null), 600)
              })
          })
          .catch((err: unknown) => {
            console.error('[auto-save] FAILED', apiPath, err)
            // The tab stays dirty and nothing else says why — a viewer-role
            // collaborator would otherwise keep typing into 403s and only find
            // out when the AI read the unchanged file.
            const saveReason = fileOpErrorReason(err, t)
            if (saveReason)
              emitNotification(
                `${t(
                  'workspace.fileError.save',
                  { path: apiPath },
                  {
                    defaultValue: `Could not save ${apiPath}.`,
                  },
                )} ${saveReason}`,
                'error',
              )
            // If save failed, check if sandbox died — show sleep overlay immediately
            const status = (err as { status?: number })?.status
            if (status && status >= 500) {
              http
                .get<Project>(`/projects/${projectId}`)
                .then((res) => {
                  const newStatus = res.data.sandboxStatus || 'stopped'
                  if (newStatus !== 'running') {
                    setSandboxStatus(newStatus)
                  }
                })
                .catch(() => {})
            }
          })
      }, 700)
      saveTimers.current.set(event.path, timer)
    })
  }, [provider, projectId, http])

  // When projectId is "new", auto-create a project and redirect to it.
  // Guard uses both a ref (same mount) and sessionStorage (survives HMR remounts)
  // to prevent duplicate project creation when Vite replaces the module mid-flight.
  const creatingRef = useRef(false)
  useEffect(() => {
    if (projectId !== 'new' || creatingRef.current) return
    // Hold creation until the guest clears the human-proof challenge (P2-26);
    // inert/no-op when captcha is unconfigured or the user is signed in.
    if (captchaRequired && !captchaCleared) return
    const guard = sessionStorage.getItem('mol_creating')
    if (guard && Date.now() - Number(guard) < 60_000) return
    creatingRef.current = true
    sessionStorage.setItem('mol_creating', String(Date.now()))

    // Don't derive the project name from the raw prompt (P2-23). Start as
    // "Untitled" (reusing the same string InlineEditableTitle falls back to);
    // the backend rewrites it to the chosen template's label once discovery
    // selects a starting point, and onReadyToBuild refetches so it shows live.
    const name = t('workspace.title.untitled', undefined, { defaultValue: 'Untitled' })

    const createProject = () =>
      http.post<{ id: string }>(
        '/projects',
        // Attach the human-verification token (P2-26) so the server's
        // requireCaptcha gate can confirm the guest on-ramp is human-originated.
        // A no-op when captcha is unconfigured (dev/local) or no challenge was
        // solved. The explicitly handed-over token (guestCaptchaTokenRef) wins
        // over the shared store, which the unmounting CaptchaGate has already
        // cleared by the time this effect runs — see the ref's declaration.
        {
          ...attachCaptchaToken({
            name,
            projectType: 'full-stack',
            framework: 'react',
          }),
          ...(guestCaptchaTokenRef.current ? { captchaToken: guestCaptchaTokenRef.current } : {}),
        },
      )

    createProject()
      .then((res) => {
        sessionStorage.removeItem('mol_creating')
        // Persist the initial prompt to localStorage so it survives if the sandbox
        // can't start immediately (e.g. free-user concurrency limit) and the user
        // navigates away and comes back (router state would be lost).
        if (initialPrompt) {
          safeStorageSet(`mol_initial_prompt_${res.data.id}`, initialPrompt)
        }
        navigate(`/workspace/${res.data.id}`, { state: { initialPrompt }, replace: true })
      })
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        // Session expired — clear stale cookie and retry (backend auto-provisions guest session)
        if (status === 401) {
          document.cookie = 'token=; path=/; max-age=0'
          document.cookie = 'sessionId=; path=/; max-age=0'
          createProject()
            .then((res) => {
              sessionStorage.removeItem('mol_creating')
              if (initialPrompt) {
                safeStorageSet(`mol_initial_prompt_${res.data.id}`, initialPrompt)
              }
              navigate(`/workspace/${res.data.id}`, { state: { initialPrompt }, replace: true })
            })
            .catch((retryErr: unknown) => {
              sessionStorage.removeItem('mol_creating')
              creatingRef.current = false
              setCreateError(createErrorView(retryErr, t))
            })
          return
        }
        sessionStorage.removeItem('mol_creating')
        creatingRef.current = false
        setCreateError(createErrorView(err, t))
      })
  }, [projectId, initialPrompt, http, navigate, t, captchaRequired, captchaCleared])

  // Persist last workspace so Landing can redirect back to it on next visit
  useEffect(() => {
    if (!isPublicWorkspace && projectId && projectId !== 'new') {
      getPreferenceStore().set('molecule_last_project', projectId)
    }
  }, [projectId])

  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  // The plan/usage limit behind a load failure, when there was one. A 404 or a
  // plain "too many requests" has no upgrade to sell, so the error view shows a
  // billing CTA only when the server actually named a limit — it used to offer
  // Upgrade for every load failure, including "Project not found".
  const [loadErrorMeta, setLoadErrorMeta] = useState<{
    limitType?: string
    requiresSignup?: boolean
    billingAction?: string
    upgradeTier?: string | null
  } | null>(null)
  useEffect(() => {
    if (!projectId || projectId === 'new') return
    setLoading(true)
    setLoadError(null)
    setLoadErrorMeta(null)
    http
      .get<Project>(`/projects/${projectId}`)
      .then((res) => setProject(res.data))
      .catch((err: unknown) => {
        const status = (err as { status?: number })?.status
        const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
        const errorText = typeof data?.error === 'string' ? data.error : ''
        if (status === 429) {
          if (typeof data?.limitType === 'string') {
            setLoadErrorMeta({
              limitType: data.limitType,
              requiresSignup: data.requiresSignup === true,
              ...(typeof data.billingAction === 'string'
                ? { billingAction: data.billingAction }
                : {}),
              ...(data.upgradeTier === null
                ? { upgradeTier: null }
                : typeof data.upgradeTier === 'string'
                  ? { upgradeTier: data.upgradeTier }
                  : {}),
            })
          }
          setLoadError(
            errorText ||
              t('workspace.rateLimited', undefined, {
                defaultValue: 'Rate limited. Please wait a moment and refresh.',
              }),
          )
        } else if (status === 404) {
          setLoadError(
            t('workspace.projectNotFound', undefined, { defaultValue: 'Project not found.' }),
          )
        } else {
          setLoadError(
            errorText ||
              t('workspace.loadError', undefined, { defaultValue: 'Failed to load project.' }),
          )
        }
      })
      .finally(() => setLoading(false))
  }, [projectId, http])

  // Re-read the FULL project row (name, settings, role, previewUrl, plan-adjacent
  // fields) and adopt it. The live-update path for everything the row carries:
  // called when a `resource_change` broadcast says another client (or the server)
  // changed the project or the caller's membership — so a rename, a settings
  // change, or a role promotion/demotion reaches this browser without a reload
  // (canEdit/canAdmin derive from project.role and recompute automatically).
  // Best-effort: a transient failure leaves the current view; the next
  // invalidation or reload converges.
  const reloadProject = useCallback(async () => {
    if (!projectId || projectId === 'new') return
    try {
      const res = await http.get<Project>(`/projects/${projectId}`)
      setProject(res.data)
    } catch (error) {
      // This browser lost access (removed from the team, the project deleted):
      // leave, instead of sitting on a dead preview behind a stale project.
      const status = (error as { status?: number } | null)?.status
      if (status === 403 || status === 404) {
        emitNotification(
          t('workspace.accessEnded', undefined, {
            defaultValue: 'Your access to this project changed.',
          }),
          'warning',
        )
        navigate('/dashboard')
        return
      }
      console.error('Failed to refresh project after a change broadcast', error)
    }
  }, [projectId, http])

  // IDE browser-tab title (P2-01): reflect the open project instead of leaving
  // the static landing-page title. Falls back to the product name while the
  // project loads, and restores the captured base title on unmount/route change
  // so the landing/dashboard tabs never keep a stale project name. Reads the
  // same `project.name` the top bar shows, so a post-selection rename (P2-23)
  // updates the tab title live.
  useEffect(() => {
    document.title = project?.name ? `${project.name} · ${PRODUCT_NAME}` : PRODUCT_NAME
    return () => {
      document.title = BASE_DOCUMENT_TITLE
    }
  }, [project?.name])

  // When the sandbox was last WOKEN from sleep (ms epoch; 0 = never this session). Fed to
  // PreviewPanel's `wakeAt` so it stays patient while the relaunched dev server restarts and
  // cold-recompiles — right after a wake the preview legitimately reloads to a blank/dead
  // document for a while, and without this signal the panel false-fired its "preview is
  // blank" notice seconds before the app rendered fine.
  const [sandboxWakeAt, setSandboxWakeAt] = useState(0)
  // Skip wake-detection on the FIRST status set (initial project load) so returning to an
  // already-running project doesn't read as a wake. Only genuine transitions after that count.
  const statusSeenRef = useRef(false)
  // Last SETTLED sandbox status (running/sleeping/stopped) — transient
  // 'creating'/'queued' never settle here, so the wake detector below sees
  // through the Wake button's optimistic 'creating' hop.
  const lastSettledStatusRef = useRef('')

  const [sandboxStatus, setSandboxStatusRaw] = useState<string>('stopped')
  const sandboxStatusRef = useRef(sandboxStatus)
  const setSandboxStatus = useCallback((status: string) => {
    // THE robust wake signal: a transition INTO 'running' from a slept/stopped state means the
    // dev servers were just (re)launched cold. Detected here — the ONE funnel every status
    // update (boot SSE 'done', the file-watch broadcast, the status poll, the sleeping→wake
    // button) passes through — instead of depending on which SSE progress steps a given wake
    // path happened to emit. Stamps a wake so PreviewPanel stays on its 'Starting…' spinner and
    // withholds the 'preview is blank' accusation while Vite comes back up. Silent wakes (status
    // already 'running', woken by a background chat turn) don't transition here, so the api's
    // 'running'/'woke' file-watch broadcast handles those (see the WS handler below).
    // Compare against the last SETTLED status (running/sleeping/stopped), not the
    // literal previous value: the Wake button's startSandbox sets 'creating'
    // first, so a button wake transitions sleeping → creating → running and the
    // old prev-only check NEVER stamped it — the wake window stayed closed on
    // every button wake, the panel's blank evaluator ran ungated, and "The
    // preview is blank" flashed at load+4s over each healthy 12-15s relaunch
    // (recorder-verified, three traces). Transient statuses pass through
    // without settling. A first boot (stopped → creating → running) now also
    // stamps — deliberately: its preview is in the identical
    // not-yet-serving state at done-time and benefits from the same patience
    // window + never-rendered reload.
    const prevSettled = lastSettledStatusRef.current
    if (
      statusSeenRef.current &&
      status === 'running' &&
      (prevSettled === 'sleeping' || prevSettled === 'stopped' || prevSettled === 'lost')
    ) {
      setSandboxWakeAt(Date.now())
    }
    statusSeenRef.current = true
    sandboxStatusRef.current = status
    if (
      status === 'running' ||
      status === 'sleeping' ||
      status === 'stopped' ||
      status === 'lost'
    ) {
      lastSettledStatusRef.current = status
    }
    setSandboxStatusRaw(status)
  }, [])

  // ── Continuous, SERVER-SIDE preview health (overlay + autonomous auto-fix) ──
  // Synthase owns the preview health check: the server probes the Vite dev server
  // INSIDE the sandbox and streams status changes here, so a crash the in-iframe
  // bridge can't report (a dead server answers ERR_EMPTY_RESPONSE — "127.0.0.1
  // didn't send any data" — with no page to run the bridge) is still caught. Active
  // only while the workspace is open AND the sandbox is running.
  const previewHealth = usePreviewHealth(
    projectId ?? null,
    !isPublicWorkspace && !!projectId && sandboxStatus === 'running',
  )
  // Keep the ref the (earlier-declared) PreviewPanel runtime-failure callbacks read in
  // sync with the live /autofix setting carried on the health stream.
  previewAutoFixRef.current = previewHealth.autoFix

  // Autonomous auto-fix guardrails. Per the user's directive the auto-fix is gated on
  // the project's /autofix setting (carried on each health event as `autoFix`); the
  // broken overlay shows regardless. Bounded: at most N dispatches per down episode
  // with a cooldown between them, reset when the preview recovers — so an unfixable
  // break never loops the executor forever.
  const previewFixEpisodeRef = useRef(0)
  const previewFixAttemptsRef = useRef(0)
  const previewFixLastAtRef = useRef(0)
  const MAX_PREVIEW_HEALTH_FIXES = 3
  const PREVIEW_HEALTH_FIX_COOLDOWN_MS = 45_000

  useEffect(() => {
    // Recovered / paused — clear the per-episode budget for the next break.
    if (previewHealth.status !== 'down') {
      previewFixAttemptsRef.current = 0
      previewFixEpisodeRef.current = previewHealth.downSeq
      return
    }
    // A fresh down episode (downSeq bumps on each ok→down transition) resets the budget.
    if (previewHealth.downSeq !== previewFixEpisodeRef.current) {
      previewFixEpisodeRef.current = previewHealth.downSeq
      previewFixAttemptsRef.current = 0
    }
    // Gate the autonomous fix (the overlay itself is unconditional — see render):
    if (!canEditProjectRef.current) return // a viewer's tab never seeds a fix turn
    if (!previewHealth.autoFix) return // the /autofix setting is off
    if (aiBuilding) return // a turn is already running — its verify→fix loop owns the preview
    if (sandboxStatusRef.current !== 'running') return
    if (!aiWroteFilesRef.current) return // don't chase the not-yet-built template's transient boot errors
    if (previewFixAttemptsRef.current >= MAX_PREVIEW_HEALTH_FIXES) return
    const now = Date.now()
    if (now - previewFixLastAtRef.current < PREVIEW_HEALTH_FIX_COOLDOWN_MS) return
    // Out of free AI usage — the dispatch cannot start a turn (checked after the
    // budget/cooldown gates so it never burns an episode attempt); the broken
    // overlay still surfaces the problem, with its manual "Fix with AI".
    if (dailyAiLimitRecentlyHit()) return

    previewFixAttemptsRef.current += 1
    previewFixLastAtRef.current = now
    const detail = previewHealth.message ? `\n\n${previewHealth.message}` : ''
    const msg = `[Preview health check] The live preview is currently broken — the automatic health check could not load it after the latest changes.${detail}\n\nInvestigate and fix the root cause so the preview loads cleanly.`
    setPendingSuppressUser(false)
    // Autonomous dispatch — a user Stop suppresses it (this exact send restarted
    // a stopped build ~10s after the Stop before the stop-guards existed).
    setPendingUserInitiated(false)
    setFixWithAIMessage(msg)
    fixWithAIKeyRef.current++
    setFixWithAIKey(fixWithAIKeyRef.current)
  }, [
    previewHealth.status,
    previewHealth.downSeq,
    previewHealth.autoFix,
    previewHealth.message,
    aiBuilding,
  ])

  // Reload the iframe when the preview RECOVERS. The iframe bond's setUrl() is a
  // no-op for an unchanged URL, and a fresh boot's first iframe load happens while
  // the sandbox still serves its STATIC "sandbox unavailable" page — so the panel
  // kept that dead document forever and only a manual page refresh showed the app
  // (observed 2026-08-10: "the preview never loads on the fresh app but does load
  // after refreshing the page"). The server-side health probe is the authoritative
  // "vite is actually serving now" signal: on any transition into `ok`, force one
  // reload. This also refreshes after every crash→recover episode (dev-server
  // bounce mid-build), at the cost of one redundant reload when opening an
  // already-healthy workspace.
  const previewHealthWasRef = useRef<'ok' | 'down' | 'unknown'>('unknown')
  useEffect(() => {
    const prev = previewHealthWasRef.current
    previewHealthWasRef.current = previewHealth.status
    if (previewHealth.status === 'ok' && prev !== 'ok') refreshPreview()
  }, [previewHealth.status, refreshPreview])

  // Hand a broken preview to Synthase manually. Only reachable from the broken overlay when
  // /autofix is off AND no turn is running (otherwise Synthase is already on it) — see
  // PreviewBrokenOverlay. Mirrors the autonomous dispatch above, but user-initiated: seed the
  // chat with the health-probe error and kick a fix turn.
  const handlePreviewFixWithAI = useCallback(() => {
    const detail = previewHealth.message ? `\n\n${previewHealth.message}` : ''
    setPendingSuppressUser(false)
    // The overlay's "Fix with AI" button is a direct user request — it bypasses
    // (and clears) a prior user Stop.
    setPendingUserInitiated(true)
    setFixWithAIMessage(
      `[Preview health check] The live preview is broken.${detail}\n\nInvestigate and fix the root cause so the preview loads cleanly.`,
    )
    fixWithAIKeyRef.current++
    setFixWithAIKey(fixWithAIKeyRef.current)
  }, [previewHealth.message])

  // Persist a project rename (E5) via the project update endpoint. Optimistic:
  // update local state immediately, revert on failure (never silently swallow).
  // Defined here, with the other hooks, so it precedes every early return.
  const handleRenameProject = useCallback(
    async (next: string) => {
      if (!project || next === project.name) return
      const previous = project.name
      setProject((cur) => (cur ? { ...cur, name: next } : cur))
      try {
        await http.patch(`/projects/${project.id}`, { name: next })
      } catch (error) {
        console.error('Failed to rename project', error)
        setProject((cur) => (cur ? { ...cur, name: previous } : cur))
      }
    },
    [project, http],
  )

  // ── Synthase conversation state (E5) ───────────────────────────────────────
  // Lifted out of ChatPanel so the top bar can host the conversation
  // title/dropdown/new-chat. ChatPanel now runs headless (renderConversationHeader
  // = false) and is driven by activeConversationId + chatKey below. The storage
  // key matches ChatPanel's old internal key so existing sessions carry over —
  // including values written by the previous raw-string helper.
  const convStorageKey = projectId ? `mol-chat-conv:${projectId}` : ''
  const {
    value: storedConversationId,
    ready: conversationPreferenceReady,
    setValue: persistStoredConversationId,
    removeValue: clearStoredConversationId,
  } = useStoragePreference<string>(convStorageKey || null)
  const [activeConversationId, setActiveConversationIdState] = useState<string | null>(() =>
    publicWorkspace
      ? publicWorkspace.defaultConversationId
      : convStorageKey
        ? safeStorageGet(convStorageKey)
        : null,
  )
  // chatKey only changes on user-initiated switches (new chat / select), which
  // remounts the chat timeline; a backend-assigned id mid-stream updates
  // activeConversationId WITHOUT touching chatKey (no remount, no lost messages).
  const [chatKey, setChatKey] = useState<string>(() =>
    publicWorkspace
      ? (publicWorkspace.defaultConversationId ?? 'default')
      : (convStorageKey && safeStorageGet(convStorageKey)) || 'default',
  )
  // Adopt an asynchronously hydrated conversation id ONCE, before any user
  // switch. A synchronous provider already answered above; this is what makes a
  // slow provider converge without a remount. It never overrides a switch the
  // user (or a backend event) already made: the store discards a read that
  // started before their write, so the value seen here is never stale.
  const adoptedStoredConversationRef = useRef(false)
  useEffect(() => {
    if (isPublicWorkspace) return
    if (!convStorageKey || !conversationPreferenceReady || adoptedStoredConversationRef.current) {
      return
    }
    adoptedStoredConversationRef.current = true
    if (typeof storedConversationId !== 'string' || !storedConversationId) return
    setActiveConversationIdState((current) => current ?? storedConversationId)
    setChatKey((current) => (current === 'default' ? storedConversationId : current))
  }, [convStorageKey, conversationPreferenceReady, storedConversationId])
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  // Host-driven signals that open the in-chat /share and /report modals (the
  // top-bar buttons bump these; ChatPanel forwards them to ChatInner).
  const [openShareSignal, setOpenShareSignal] = useState(0)
  const [openReportSignal, setOpenReportSignal] = useState(0)
  // Host-mounted copies of the SAME two modals, used only in the phase where no
  // ChatPanel is mounted (sandbox sleeping/stopped past discovery). Reporting a
  // bug and minting a share link both POST to the platform API — neither needs
  // the sandbox — so they must keep working while it hibernates, which is
  // exactly when a user most wants to report that something broke. The signals
  // above are observed INSIDE ChatPanel, so with no panel mounted they bumped a
  // counter nobody read and the buttons silently did nothing.
  const [hostReportOpen, setHostReportOpen] = useState(false)
  const [hostShareOpen, setHostShareOpen] = useState(false)
  // Bumped when another client changes the share links, so an open Share dialog
  // re-lists them instead of showing a link that was just revoked elsewhere.
  const [shareLinksTick, setShareLinksTick] = useState(0)
  useEffect(() => onResourceInvalidated('shares', () => setShareLinksTick((n) => n + 1)), [])
  // Hidden link clicked programmatically to trigger the AuthModalMount's
  // click interceptor (which opens the auth modal) when a guest clicks
  // anything that would open the user panel or profile editor (top-bar user
  // button, chat avatar, mobile More-sheet profile row).
  const authGuestTriggerRef = useRef<HTMLAnchorElement>(null)
  // An authenticated session ALWAYS wins: the guest cookie is only a claim
  // hint, and a stale one (e.g. a failed/skipped claim) must not keep showing
  // guest CTAs to a logged-in user. Guests normally have no client auth state
  // (authConfig.shouldRestoreUser rejects anonymous restores) — but an
  // anonymous user row CAN reach `user` (an old API's token auto-refresh
  // minted the mol_auth hint and /users/me returned the guest row), so the
  // test must also treat `user.isAnonymous` as guest. See isGuestUser().
  const isGuest = isGuestUser(user)
  // The profile modal (D1/D2/C5) — the current user's OWN profile (editable,
  // with Edit profile + Log out) or a TEAMMATE's (view-only). Opened in edit
  // mode from the top-bar profile button (D2), and in view mode from a click on
  // any author's avatar or name in the Synthase chat (C5) — the clicked
  // identity decides which of the two surfaces shows.
  const [profileModalOpen, setProfileModalOpen] = useState(false)
  const [profileModalMode, setProfileModalMode] = useState<'view' | 'edit'>('view')
  // Non-null = a TEAMMATE's profile is being viewed (seeded from the clicked
  // message's author snapshot, then enriched with the live profile — bio, fresh
  // avatar/name — from the project-scoped profile endpoint). Null = own.
  const [profileModalProfile, setProfileModalProfile] = useState<ProfileView | null>(null)
  // Which user id the open teammate view belongs to, so a late enrichment fetch
  // can never repaint a modal that has since moved to someone else (or to own).
  const profileModalUserIdRef = useRef<string | null>(null)
  // The full user settings panel (same drawer the dashboard's user menu opens)
  // — the top-bar user button opens THIS, so the user icon behaves identically
  // everywhere. The ProfileModal above stays for chat-avatar clicks (C5).
  const [userPanelOpen, setUserPanelOpen] = useState(false)
  // Model-provider modal — opened by the /model picker's "Add or manage your
  // own models…" row. A focused BYO ("bring your own AI") provider manager, not
  // the full Synthase settings modal (that surface has no opener right now — see
  // P2-19; routing this narrow row into it was the wrong target).
  const [modelProviderOpen, setModelProviderOpen] = useState(false)
  // The conversation mode the user was in when they opened the provider modal
  // (ChatPanel passes it) — "Use" persists the model to THAT mode's setting.
  const [modelProviderMode, setModelProviderMode] = useState<'plan' | 'execute' | undefined>(
    undefined,
  )
  // Bumped when the model-provider modal's "Use" sets the project chat model, so
  // ChatPanel re-reads its settings (its model state loads once on mount).
  const [modelSelectionSignal, setModelSelectionSignal] = useState(0)
  // A read-only VIEWER cannot write project settings (the server 403s every such
  // write), so every UI control that mutates shared project state is gated on
  // this. Only `viewer` is blocked; owner/admin/editor — and an absent role on
  // an older payload — can edit. Mirrors the server's assertProjectRole('editor').
  const canEditProject = !isPublicWorkspace && project?.role !== 'viewer'
  // Share-link minting (and list/revoke) is admin+ server-side — the Share
  // dialog shows its links only at this level, so an editor isn't handed a
  // "Create link" that can only 403. Editors still open the dialog for "How it
  // was built" (importing histories is editor+). Absent role (older payload /
  // solo) = owner.
  const canAdminProject =
    !isPublicWorkspace && project?.role !== 'viewer' && project?.role !== 'editor'
  // The tier that governs THIS project's model picker is the OWNER's — the
  // server's model clamp resolves against the owner's plan and reports it as
  // `freeTier` on GET /ai/models?projectId=. Gating the picker on the CALLER's
  // own plan showed teammates lock badges the server wouldn't enforce (free
  // teammate, Pro owner) or an unlocked picker it silently clamped (the
  // reverse). Falls back to the caller-plan heuristic until the fetch lands.
  const [projectFreeTier, setProjectFreeTier] = useState<boolean | null>(null)
  useEffect(() => {
    if (!projectId || projectId === 'new') return
    let cancelled = false
    http
      .get<{ freeTier?: boolean }>(`/ai/models?projectId=${encodeURIComponent(projectId)}`)
      .then((res) => {
        if (!cancelled && typeof res.data?.freeTier === 'boolean') {
          setProjectFreeTier(res.data.freeTier)
        }
      })
      .catch((_error) => {
        // Best-effort: without the flag the caller-plan heuristic below applies,
        // matching the pre-flag behavior.
      })
    return () => {
      cancelled = true
    }
  }, [http, projectId])
  const callerIsPro =
    !!(user as { planKey?: string } | null)?.planKey?.startsWith('stripe') ||
    !!(user as { planKey?: string } | null)?.planKey?.startsWith('team')
  const isProForProject = projectFreeTier !== null ? !projectFreeTier : callerIsPro
  // Ref mirror for empty-dep callbacks (fireBuildKickoff) that must read the
  // CURRENT role without re-creating on every project load.
  const canEditProjectRef = useRef(canEditProject)
  canEditProjectRef.current = canEditProject
  // Ref mirror of aiBuilding for stable async callbacks (the preview restart
  // escalation must never bounce the sandbox under an active turn).
  const aiBuildingRef = useRef(aiBuilding)
  aiBuildingRef.current = aiBuilding

  // PreviewPanel's backend-restart escalation (see PreviewPanelProps.onRestartBackend):
  // the panel asks for this only when client-side reloads provably cannot recover
  // (server answering, reload already tried, no render for minutes — the
  // poisoned-module-cache class, observed live 2026-08-31). Decline (false) for
  // every state where a restart is wrong: viewer role (the route is editor-gated
  // anyway — declining avoids a doomed 403), hidden tab (the visible tab owns
  // recovery; two tabs escalating would double-restart), sandbox not running,
  // or an active turn (the executor owns the sandbox mid-build). On go: stamp
  // wake patience FIRST so the panel treats the outage as a wake, then fire the
  // forceful restart route (kills listening dev servers before relaunching, so
  // the bounce is real even on pause-style providers).
  // Until when the escalation watchdog must not POST another restart (set after a 503 answer).
  const restartDeclinedUntilRef = useRef(0)
  const handleRestartBackend = useCallback(async (): Promise<boolean> => {
    if (!projectId) return false
    if (!canEditProjectRef.current) return false
    if (document.visibilityState === 'hidden') return false
    if (sandboxStatusRef.current !== 'running') return false
    if (aiBuildingRef.current) return false
    // The server answered 503 (making room / no free slot): it asked for a pause, and each attempt would only POST a
    // restart into a box that is already short of room (and re-stamp the wake patience) every few seconds.
    if (Date.now() < restartDeclinedUntilRef.current) return false
    setSandboxWakeAt(Date.now())
    try {
      await http.post(`/projects/${projectId}/sandbox/restart`)
      console.warn('[preview] backend dev-server restart triggered by the escalation watchdog')
      return true
    } catch (error) {
      console.warn('[preview] escalation restart failed', error)
      const failure = error as {
        status?: number
        response?: { status?: number; data?: { retryAfter?: number } }
      } | null
      if ((failure?.status ?? failure?.response?.status) === 503) {
        const asked = Number(failure?.response?.data?.retryAfter)
        const waitMs =
          Number.isFinite(asked) && asked > 0 ? Math.min(asked * 1000, 300_000) : 60_000
        restartDeclinedUntilRef.current = Date.now() + Math.max(60_000, waitMs)
      }
      return false
    }
  }, [projectId, http])
  // Push the role into the module-level chat-card context so custom transcript
  // cards (egress Allow, script Run) don't offer viewers editor-only buttons.
  useEffect(() => {
    setChatCardCanEdit(canEditProject)
  }, [canEditProject])
  // Guests can never see the profile editor or user panel — every opener
  // funnels through these, which redirect a guest to the signup/login modal.
  const openOwnProfile = useCallback(
    (mode: 'view' | 'edit') => {
      if (isGuest) {
        authGuestTriggerRef.current?.click()
        return
      }
      profileModalUserIdRef.current = null
      setProfileModalProfile(null)
      setProfileModalMode(mode)
      setProfileModalOpen(true)
    },
    [isGuest],
  )
  // C5: a click on any author's avatar/name in the chat. Own → the editable
  // own-profile surface (Edit profile + Log out); a teammate → their view-only
  // profile, seeded from the message's author snapshot and enriched with the
  // live profile (bio, current avatar/name) — best-effort: the snapshot alone
  // still renders when the fetch fails (e.g. the author left the team).
  const openChatProfile = useCallback(
    (clicked: ChatUserIdentity) => {
      // A guest has no teammates (teams require accounts) — every avatar in
      // their chat is their own, and guests funnel to the signup/login modal
      // (via openOwnProfile), exactly like the top-bar profile button.
      // A named author with no id is a deleted member ("Deleted user"): there
      // is no profile to open, and it must never fall through to the viewer's own.
      if (!clicked.id && clicked.name) return
      // No id and no name = an author-less own row.
      if (isGuest || !clicked.id || clicked.id === user?.id) {
        openOwnProfile('view')
        return
      }
      const clickedId = clicked.id
      profileModalUserIdRef.current = clickedId
      setProfileModalProfile({ name: clicked.name ?? null, avatar: clicked.avatar ?? null })
      setProfileModalMode('view')
      setProfileModalOpen(true)
      if (!projectId || projectId === 'new') return
      http
        .get<{ data?: { name?: string | null; avatar?: string | null; bio?: string | null } }>(
          `/projects/${projectId}/team/profile/${encodeURIComponent(clickedId)}`,
        )
        .then((res) => {
          const live = res.data?.data
          if (!live || profileModalUserIdRef.current !== clickedId) return
          setProfileModalProfile({
            name: live.name ?? clicked.name ?? null,
            avatar: live.avatar ?? clicked.avatar ?? null,
            bio: live.bio ?? null,
          })
        })
        .catch((_error) => {
          // Best-effort enrichment: the modal already shows the author snapshot;
          // a 403/404 (share viewer, departed member) just means no live bio.
        })
    },
    [isGuest, user?.id, openOwnProfile, projectId, http],
  )
  const openUserPanel = useCallback(() => {
    if (isGuest) {
      authGuestTriggerRef.current?.click()
      return
    }
    setUserPanelOpen(true)
  }, [isGuest])
  // Measured by HeaderTopOffsetPublisher on phones (fixed-banner anchoring).
  const workspaceHeaderRef = useRef<HTMLElement | null>(null)

  // In-IDE auth + upgrade via the SHARED @molecule/app-auth-modal-react flow: it
  // intercepts /login,/signup CTAs into the auth modal and /pricing,/billing into
  // the in-IDE UpgradeModal (the IDE tab never navigates and no new tab spawns —
  // only Stripe checkout still gets its own tab, with a focus-return refresh).
  // Mounted below in the workspace render, so its interceptor is scoped to the
  // IDE. molecule-dev extras: stash the guest id before auth, then claim the
  // guest's projects + refresh usage after — so a guest who hits the AI budget
  // keeps their project + chat in place.
  const [upgradeModalView, setUpgradeModalView] = useState<UpgradeModalView | null>(null)
  const authModalMountEl = isPublicWorkspace ? null : (
    <>
      <AuthModalMount
        oauthConfig={oauthConfig}
        onBeforeAuth={stashGuestUserId}
        onAuthenticated={async () => {
          // Drops the stale guest bearer, claims guest projects, re-mints the WS
          // ticket for the new session — see adoptLoginSession for why the stale
          // bearer otherwise 401s ("Unauthorized") the whole IDE after login.
          await adoptLoginSession(http)
          notifyUsageInvalidated()
        }}
        onUpgradeIntercept={(path) =>
          setUpgradeModalView(path === '/billing' ? 'billing' : 'pricing')
        }
        // The API's POST /users requireCaptcha gate is live whenever a captcha
        // secret is configured, and it does not care WHICH signup surface a
        // request came from. Without this the in-IDE signup (what a /signup CTA
        // actually opens) POSTs no token and the user is told to complete a
        // challenge that was never rendered — the standalone /signup page's
        // widget is not reachable from here. Inert in dev/local: unconfigured,
        // CaptchaGate renders nothing and reports "passed" immediately.
        captchaSlot={
          isCaptchaConfigured() ? (
            <AuthModalCaptcha onSolvedChange={setAuthModalCaptchaSolved} />
          ) : undefined
        }
        captchaSolved={!isCaptchaConfigured() || authModalCaptchaSolved}
      />
      {/* Hidden anchor — programmatically clicked when a guest clicks anything
          that would open the user panel / profile editor; the capture-phase
          click interceptor above catches it and opens the signup modal
          in-place. Bundled with the mount so it exists in every phase the
          interceptor does (incl. discovery, where the header isn't rendered). */}
      {isGuest && (
        <a
          ref={authGuestTriggerRef}
          href="/signup"
          aria-hidden="true"
          style={{ display: 'none' }}
        />
      )}
    </>
  )
  const upgradeModalEl = (
    <UpgradeModal
      view={upgradeModalView}
      onViewChange={setUpgradeModalView}
      onClose={() => {
        setUpgradeModalView(null)
        // Catch-all: a spend-cap change or completed upgrade made inside the
        // modal should reflect in the IDE's usage banners right away.
        notifyUsageInvalidated()
      }}
    />
  )

  // The shared ChatPanel's limit/upgrade notices (the OOM banner, the AI-budget /
  // locked-model cards, the live limit banner) render NO buttons unless the host
  // supplies them — this is that builder.
  //
  // It renders whatever the SERVER decided (`billingAction`), through the one
  // shared table, so the banner can no longer contradict the chat card beside
  // it: a paid user out of prepaid funds gets "Add funds", a cap-paused user
  // "Raise spend cap", a Team user at a plan cap no button at all. Only when the
  // server said nothing does it derive from `limitType` / the viewer's own guest
  // state. The hrefs (/signup, /login, /pricing, /billing) are caught by
  // AuthModalMount's click interceptor above, so they open the in-IDE modal
  // without ever navigating.
  //
  // `owner_only` has no button — the note is the whole answer — so the limit
  // text carries it; ChatPanel renders the message, not this builder, hence the
  // note is unreachable here and the CTA is simply absent.
  const buildUpgradeCta = useCallback(
    (context: {
      requiresSignup?: boolean
      limitType?: string
      billingAction?: string
      upgradeTier?: string | null
    }) => billingCta({ ...context, isGuest }).actions,
    [isGuest],
  )

  // The same resolver for the project-LOAD failure view. `null` meta (a 404, a
  // generic failure, a bare rate limit) resolves to no CTA at all rather than
  // the unconditional Upgrade link that view used to carry.
  const loadErrorCta = loadErrorMeta ? billingCta({ ...loadErrorMeta, isGuest }) : undefined

  const persistConversationId = useCallback(
    (id: string | null) => {
      setActiveConversationIdState(id)
      if (!convStorageKey) return
      // Through the preference store, so this tab's own subscribers and any
      // other tab see the switch immediately.
      if (id) persistStoredConversationId(id)
      else clearStoredConversationId()
    },
    [convStorageKey, persistStoredConversationId, clearStoredConversationId],
  )

  const fetchConversations = useCallback(async () => {
    if (!projectId || projectId === 'new') return
    try {
      const res = await http.get<{ conversations?: ConversationSummary[] }>(
        `/projects/${projectId}/conversations`,
      )
      setConversations(res.data.conversations ?? [])
    } catch (error) {
      // Non-critical — the picker degrades gracefully without the list.
      console.error('Failed to load conversations', error)
    }
  }, [http, projectId])

  // Fetch the conversation list on mount so the top-bar title renders.
  useEffect(() => {
    void fetchConversations()
  }, [fetchConversations])

  const handleNewChat = useCallback(async () => {
    if (!projectId) return
    try {
      const res = await http.post<{ id: string }>(`/projects/${projectId}/conversations`, {})
      persistConversationId(res.data.id)
      setChatKey(res.data.id)
    } catch (error) {
      // Server create failed — fall back to a client-only key so a new chat can
      // still start (the backend assigns an id on the first message).
      console.error('Failed to create conversation', error)
      persistConversationId(null)
      setChatKey(`new-${Date.now()}`)
    }
    void fetchConversations()
  }, [http, projectId, persistConversationId, fetchConversations])

  // When the backend assigns / confirms the conversation id (it emits the id
  // mid-stream AFTER persisting the first user message up front — see ChatPanel's
  // `loadOnMount` comment), refresh the conversation list so the top-bar title
  // immediately reflects the server-derived default (the first sentence of the
  // prompt) instead of staying 'New chat' until the history dropdown is opened
  // (P3-05). Loop-safe: it only updates `conversations` state, which never
  // re-fires onConversationId (that's driven by the SSE id event, not a render),
  // and both deps are stable callbacks.
  const handleConversationId = useCallback(
    (id: string | null) => {
      persistConversationId(id)
      void fetchConversations()
    },
    [persistConversationId, fetchConversations],
  )

  const handleSelectConversation = useCallback(
    (id: string) => {
      persistConversationId(id)
      setChatKey(id)
    },
    [persistConversationId],
  )

  const handleRenameConversation = useCallback(
    async (id: string, title: string) => {
      setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)))
      try {
        await http.patch(`/projects/${projectId}/conversations/${id}`, { title })
      } catch (error) {
        console.error('Failed to rename conversation', error)
        void fetchConversations() // reconcile with server truth
      }
    },
    [http, projectId, fetchConversations],
  )
  // Router state is cleared via onInitialMessageSent callback from ChatPanel
  // (not eagerly here — that was racing with ChatPanel and consuming the prompt
  // before it could be sent).

  const [files, setFiles] = useState<FileNode[]>([])
  const gitignoreRef = useRef<string[]>([])
  const [fileStatuses, setFileStatuses] = useState<Record<string, string>>({})
  const [gitStatusTick, setGitStatusTick] = useState(0)

  /** Fetch per-file git status from the API and update the fileStatuses map. */
  const fetchGitStatus = useCallback(async () => {
    if (!projectId || projectId === 'new') return
    try {
      const res = await http.get<{
        files: { path: string; status: string }[]
        sandbox?: 'unavailable'
      }>(`/projects/${projectId}/git-status`)
      // Asleep / booting / lost: nothing was read, so keep the last known
      // decorations instead of wiping them as if the tree were clean.
      if (isSandboxUnavailableBody(res.data)) return
      const map: Record<string, string> = {}
      for (const f of res.data.files) {
        // API returns paths relative to /workspace (e.g. "src/App.tsx"), prefix to match editor paths
        map[`/workspace/${f.path}`] = f.status
      }
      // Preserve synthetic 'deleted' entries for open tabs that git no longer
      // reports (e.g. untracked files deleted via context menu)
      setFileStatuses((prev) => {
        for (const [path, status] of Object.entries(prev)) {
          if (status === 'deleted' && !map[path]) {
            map[path] = 'deleted'
          }
        }
        // Avoid returning a new object when nothing changed — prevents
        // downstream effects (e.g. annotateGitStatus) from re-running on
        // every poll.
        const prevKeys = Object.keys(prev)
        if (
          prevKeys.length === Object.keys(map).length &&
          prevKeys.every((k) => prev[k] === map[k])
        ) {
          return prev
        }
        return map
      })
    } catch (_error) {
      // ignore — sandbox may not be ready yet; polling resumes next tick
    }
  }, [projectId, http])

  // ── /test browser ─────────────────────────────────────────────────────────────
  // The shared IDE owns no test routes, so the host supplies both halves. The
  // run is an SSE stream (same shape as the sandbox-boot stream above), and its
  // `cancel()` aborts the request — which the API sees as a disconnect and
  // answers by killing the run's process tree inside the sandbox.
  const listTests = useCallback(
    () => listProjectTests(http, projectId as string),
    [http, projectId],
  )
  const runTests = useCallback(
    (selection: TestSelection, onEvent: (event: TestRunEvent) => void) =>
      runProjectTests({
        projectId: projectId as string,
        selection,
        authToken: http.getAuthToken(),
        version: 2,
        onEvent,
      }),
    [http, projectId],
  )
  // Every run in the project — the agent's, the platform's, another tab's —
  // reaches the same bar; the IDE drops the echo of its own runs.
  const testAuthToken = useCallback(() => http.getAuthToken(), [http])
  const testRecords = useProjectTestRecords(projectId || '', testAuthToken, !isPublicWorkspace)

  // ── Skip the executor's in-flight tool call ───────────────────────────────────
  // The turn keeps going: the API answers the call with a `skipped_by_user`
  // result the model reads as "did not run". `skipped: false` means nothing was
  // in flight (the button raced the call finishing) — a benign outcome the IDE
  // shows as the button coming back, never as an error.
  const skipToolCall = useCallback(
    async (toolCallId: string): Promise<boolean> => {
      try {
        const response = await http.post<{ skipped?: boolean }>(
          `/projects/${projectId}/chat-tool-skip`,
          {
            ...(activeConversationId ? { conversationId: activeConversationId } : {}),
            toolCallId,
          },
        )
        return response.data?.skipped === true
      } catch (error) {
        console.error('Failed to skip the running tool call', error)
        return false
      }
    },
    [http, projectId, activeConversationId],
  )

  const [sandboxLoading, setSandboxLoading] = useState(false)
  const sandboxLoadingRef = useRef(false)
  // Start refusals the server marked retryable with a Retry-After, retried by the IDE itself (R109-H1).
  const startAutoRetryRef = useRef(0)
  // The pending retry's timer and the project it belongs to (R119-M1): cleared
  // when the user leaves the page or switches project, so a retry never starts
  // a project nobody is watching — or the wrong one; a status settled by
  // another channel (a stop, a sleep, a loss) ends it when it fires.
  const startAutoRetryTimerRef = useRef<{ id: number; projectId: string } | null>(null)
  // A generation advanced by every clear (R120-H1): a refusal that lands AFTER the
  // page left or the project changed — the start request is not aborted — must
  // not schedule a retry, and a timer scheduled before the clear must not run.
  const retryGenRef = useRef(0)
  const clearStartAutoRetry = useCallback(() => {
    retryGenRef.current += 1
    if (startAutoRetryTimerRef.current) {
      window.clearTimeout(startAutoRetryTimerRef.current.id)
      startAutoRetryTimerRef.current = null
    }
    startAutoRetryRef.current = 0
  }, [])
  useEffect(() => () => clearStartAutoRetry(), [projectId, clearStartAutoRetry])
  // The in-flight boot progress stream's abort handle, so the page-resume
  // reconcile can cut a stream whose socket died while the page was frozen.
  const bootStreamAbortRef = useRef<AbortController | null>(null)
  // The project whose waiting start the user cancelled (Cancel on the wait
  // notice): the start flow it cut short must end quietly — no error, no
  // status-poll recovery back into "Starting…". Cleared by the next start.
  const bootCancelledRef = useRef<string | null>(null)
  const [cancellingBoot, setCancellingBoot] = useState(false)
  const [sandboxError, setSandboxError] = useState<string | null>(null)
  /** The `errorKey` behind `sandboxError` (drives the action under it). */
  const [sandboxErrorKey, setSandboxErrorKey] = useState<string | null>(null)
  /** The owner's other running projects a refused Start would not stop (in use). */
  const [sandboxErrorBlocking, setSandboxErrorBlocking] = useState<
    Array<{
      id: string
      name: string
      reason: 'in-use' | 'recent'
      /** The blocking sandbox's lifecycle state when the server sends one:
       *  'running', 'starting' (being created) or 'waking' (resuming under
       *  a wake claim); absent means running. */
      state?: string
    }>
  >([])
  const [progressSteps, setProgressSteps] = useState<Array<{ id: string; detail?: string }>>([])
  // Why the start is slow and how long is left, from the latest progress event.
  const [bootWait, setBootWait] = useState<{ wait: BootWait; receivedAt: number } | null>(null)
  // Which project is on screen right now, and whether the page is still mounted: a status poll that
  // outlives either must stop (the poll runs for up to 75 minutes).
  const liveProjectIdRef = useRef(projectId)
  liveProjectIdRef.current = projectId
  const unmountedRef = useRef(false)
  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
    }
  }, [])
  // A boot that ended or restarted clears its checklist — and its wait with it.
  useEffect(() => {
    if (progressSteps.length === 0) setBootWait(null)
  }, [progressSteps])
  const [sandboxCached, setSandboxCached] = useState<boolean | null>(null)
  const [sandboxPreWarmed, setSandboxPreWarmed] = useState(false)
  const [displayProgress, setDisplayProgress] = useState(0)
  const progressCeilingRef = useRef(0)

  // ── Prompt → chat morph (discovery) ──────────────────────────────────────
  // The floating prompt textarea (PromptMorphOverlay, above the router) was
  // placed at the source prompt's rect on submit. Here we reveal the chat card
  // (fading in around the input), move that floating textarea onto the real
  // chat input, then hand off: fade the overlay out + submit the prefilled
  // prompt (which clears the input as the message appears).
  const morph = usePromptMorph()
  const morphText = morph.text || initialPrompt || ''
  // Tear down the floating prompt overlay whenever the workspace CANNOT reach the
  // discovery hand-off that normally ends it — otherwise the orphaned floating
  // textarea sits on top of the landing prompt (two textareas) after "Go back".
  // Covers EVERY blocking path (guest/anon limit, load error, captcha gate, and
  // any future one), plus unmount so navigating away always cleans up.
  const workspaceBlocked = !!createError || !!loadError || (captchaRequired && !captchaCleared)
  useEffect(() => {
    if (workspaceBlocked) endPromptMorph()
  }, [workspaceBlocked])
  useEffect(() => () => endPromptMorph(), [])
  const [autoSubmitKey, setAutoSubmitKey] = useState(0)
  // Chat card is hidden until the morph reveals it (or shown immediately when
  // there's no morph in flight — e.g. direct navigation).
  const [cardRevealed, setCardRevealed] = useState(() => !getPromptMorph().active)
  const morphRanRef = useRef(false)

  // Drive the morph once. Timers are intentionally NOT cancelled on re-render
  // (morphRanRef guards a single run) so the sequence always completes.
  useEffect(() => {
    if (morphRanRef.current) return
    const inDisc =
      !!project &&
      !project.templateSlug &&
      sandboxStatus === 'stopped' &&
      !sandboxLoading &&
      !sandboxError
    if (!inDisc) return
    morphRanRef.current = true

    const focusChatInput = (): void => {
      ;(document.querySelector('[data-mol-chat-input]') as HTMLTextAreaElement | null)?.focus()
    }

    if (!getPromptMorph().active) {
      // No morph in flight — show the chat immediately and send any prompt.
      setCardRevealed(true)
      requestAnimationFrame(() => requestAnimationFrame(focusChatInput))
      if (morphText) {
        window.setTimeout(() => {
          setAutoSubmitKey((k) => k + 1)
          onInitialMessageSent()
        }, 150)
      }
      return
    }

    // Keep the chat card hidden (but laid out, so its input can be measured)
    // while the floating textarea travels — the Synthase chat must not appear
    // until the prompt has finished moving. Measure the input CONTAINER (the
    // visible box) so the overlay docks to the same box, then dock it.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-mol-chat-input]')
        const box = el?.parentElement
        if (!box) return
        const r = box.getBoundingClientRect()
        // Clamp width — a measurement glitch shouldn't let the textarea balloon.
        // Ceiling = the discovery chat card's max-width (760px, Workspace.css
        // `.mol-pre-ide-chat`), so the morph lands at the discovery textarea
        // width (P5-01) instead of a too-narrow 640.
        const width = Math.min(r.width, 760)
        movePromptMorph({
          left: r.left + (r.width - width) / 2,
          top: r.top,
          width,
          height: r.height,
        })
      }),
    )
    // Once the overlay has docked: hard-swap in a single frame — reveal the chat
    // and remove the overlay together (same box/position, so it's seamless),
    // submit the prefilled prompt, and focus the input.
    window.setTimeout(() => {
      setCardRevealed(true)
      endPromptMorph()
      setAutoSubmitKey((k) => k + 1)
      onInitialMessageSent()
      focusChatInput()
    }, 640)
  }, [project, sandboxStatus, sandboxLoading, sandboxError, morphText, onInitialMessageSent])

  // Compute base/ceiling from current steps
  const { base: progressBase, ceiling: progressCeiling } = computeProgressRange(
    progressSteps,
    sandboxCached,
    sandboxPreWarmed,
  )
  progressCeilingRef.current = progressCeiling

  // Snap forward when a new step arrives (base jumps)
  useEffect(() => {
    setDisplayProgress((prev) => Math.max(prev, progressBase))
  }, [progressBase])

  // Slowly creep toward the ceiling while waiting for the next step
  useEffect(() => {
    if (progressSteps.length === 0) return
    const id = setInterval(() => {
      setDisplayProgress((prev) => {
        const remaining = progressCeilingRef.current - prev
        if (remaining <= 0.5) return prev
        return prev + remaining * 0.03
      })
    }, 200)
    return () => clearInterval(id)
  }, [progressSteps.length])

  // The terminal docks under the editor (not an overlay), so it stays its own
  // boolean. Every other non-preview panel opens as a single modal overlay,
  // tracked by one `activePanel` value (see utilities/workspacePanels.ts) so
  // exactly one is ever open — no boolean-per-panel mutual-exclusion clearing.
  const [showTerminal, setShowTerminal] = useState(false)
  // The Terminal is a power-user surface gated behind Settings → Advanced.
  // When advanced mode is off it's hidden from the toolbar + command palette;
  // turning it off also closes any open terminal so nothing is left orphaned.
  const advancedMode = useAdvancedMode()
  useEffect(() => {
    if (!advancedMode) setShowTerminal(false)
  }, [advancedMode])
  // Opt-in (Settings → "Keep screen awake"): hold a screen wake lock while
  // Synthase is actively working so a phone doesn't auto-lock mid-build — a
  // frozen page makes the live preview unreachable for the preview tools.
  const keepAwake = useKeepAwake()
  useScreenWakeLock(keepAwake && aiBuilding)
  const [activePanel, setActivePanel] = useState<WorkspacePanel | null>(null)
  /** Toolbar click: open the panel, or close it if it's already open. */
  const togglePanel = useCallback(
    (panel: WorkspacePanel) => setActivePanel((cur) => nextPanel(cur, panel)),
    [],
  ) // The /hooks chat card's "Open Autoruns" action opens the panel.
  useEffect(() => {
    const open = (): void => setActivePanel('autoruns')
    window.addEventListener(AUTORUNS_OPEN_EVENT, open)
    return () => window.removeEventListener(AUTORUNS_OPEN_EVENT, open)
  }, [])

  // E-03: how many env vars detected in the app's code (plus registry-declared
  // secrets) still need a value — drives the small warning count badge on the
  // Environment toolbar button. Best-effort: a failed fetch just means no badge
  // (the Environment panel itself surfaces the detected vars).
  const [envMissingCount, setEnvMissingCount] = useState(0)
  const refreshEnvMissingCount = useCallback(async () => {
    // 'new' is the pre-create route's placeholder, not a project id — polling it
    // asked the API for /projects/new/env/detected and got a 400 on a timer.
    if (!projectId || projectId === 'new') return
    // /env/detected is editor+ — a viewer's poll was a guaranteed 403/min.
    if (!canEditProjectRef.current) return
    try {
      const res = await http.get<{ vars: unknown[]; missingCount: number }>(
        `/projects/${projectId}/env/detected`,
      )
      setEnvMissingCount(res.data?.missingCount ?? 0)
    } catch (error) {
      // A 404 here can mean the project itself is gone (deleted while this tab
      // missed the presence frame — a slept laptop, a hidden tab). Re-read the
      // project row: reloadProject leaves for the dashboard on 403/404 and is a
      // harmless refresh when the project still exists.
      if ((error as { status?: number } | null)?.status === 404) {
        void reloadProject()
        return
      }
      // Best-effort badge — see above; never surface an error for it.
      console.debug('Workspace: failed to load detected env var count', error)
    }
  }, [http, projectId, reloadProject])
  useEffect(() => {
    if (isPublicWorkspace) return
    void refreshEnvMissingCount()
    // Keep the badge current while the user works: Synthase can introduce new
    // env vars mid-session (installing an integration, code reading a new
    // key), and without a poll the count only updated on mount/panel-close.
    // The server caches the report for 45s, so this is cheap; hidden tabs skip.
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void refreshEnvMissingCount()
    }, 60_000)
    return () => clearInterval(interval)
  }, [refreshEnvMissingCount])
  // ── resource_change dispatcher ────────────────────────────────────────────
  // Another client (or the server autonomously) changed project-scoped state
  // this browser renders — the presence socket delivers a coarse invalidation
  // frame and this refetches through the SAME hydration paths a reload uses,
  // so live updates can never diverge from a refresh. Panels subscribe via
  // notifyResourceInvalidated (resource-invalidation.ts) so only mounted ones
  // pay for a refetch. Kept in a ref (like chatPushHandlerRef) so the socket
  // effect never re-connects on dep churn.
  const handleResourceChange = (
    resource: string,
    action?: string,
    conversationId?: string,
  ): void => {
    switch (resource) {
      case 'project':
        if (action === 'deleted') {
          // The project is gone — a live workspace on a deleted project can
          // only 404. Say so and leave for the dashboard (not the marketing
          // landing page, which read as being signed out).
          emitNotification(
            t('workspace.projectRemoved', undefined, {
              defaultValue: 'This project was removed.',
            }),
            'warning',
          )
          navigate('/dashboard')
          return
        }
        void reloadProject()
        // ChatPanel re-reads the full settings bag (models/effort/regions/
        // max-loops/auto-fix) off this signal, so its pickers match the change
        // another member just made.
        setModelSelectionSignal((s) => s + 1)
        notifyResourceInvalidated('project')
        return
      case 'team':
        // Membership/roles changed — the caller's OWN role may be among them
        // (project.role drives canEdit/canAdmin), so refresh the row too.
        void reloadProject()
        notifyResourceInvalidated('team')
        return
      case 'conversations':
        void fetchConversations()
        // The OPEN transcript too (R92-M1): the API appends its notices —
        // restoring from a backup, the database failed to load, sandbox lost,
        // IMPORT FAILED — to the conversation and publishes this; without the
        // reload they showed only on a reconnect or a refocus.
        void chatReconcileRef.current?.()
        // A frame that names the cleared conversation only resets the chat that has it open
        // (R140-H1); one that names none (older server) resets whatever is open.
        if (action === 'cleared' && (!conversationId || conversationId === activeConversationId)) {
          // The open conversation's row was deleted (chat cleared elsewhere) —
          // reset to a fresh chat instead of rendering a transcript whose next
          // persist/read would 404.
          persistConversationId(null)
          setChatKey(`new-${Date.now()}`)
        }
        notifyResourceInvalidated('conversations')
        return
      case 'git':
        // A commit/push/pull elsewhere changed every file's git status.
        void fetchGitStatus()
        setGitStatusTick((t) => t + 1)
        notifyResourceInvalidated('git')
        return
      case 'env':
        void refreshEnvMissingCount()
        notifyResourceInvalidated('env')
        return
      case 'deployments':
      case 'shares':
      case 'snapshots':
      case 'services':
        notifyResourceInvalidated(resource)
        return
      default:
        return
    }
  }
  const handleResourceChangeRef = useRef(handleResourceChange)
  handleResourceChangeRef.current = handleResourceChange

  // Refresh when the Environment panel closes, so setting vars in it clears
  // (or updates) the badge right away.
  const prevPanelForEnvBadge = useRef<WorkspacePanel | null>(null)
  useEffect(() => {
    if (prevPanelForEnvBadge.current === 'environment' && activePanel !== 'environment') {
      void refreshEnvMissingCount()
    }
    prevPanelForEnvBadge.current = activePanel
  }, [activePanel, refreshEnvMissingCount])
  // Deep-link from the onboarding checklist: navigating with
  // state.openPanel === 'services' | 'deploy' opens that panel on arrival.
  const openPanelConsumed = useRef(false)
  useEffect(() => {
    if (openPanelConsumed.current) return
    const openPanel = (state as { openPanel?: string } | null)?.openPanel
    if (openPanel === 'services') {
      // Services now live in the unified Environment view — editor+ content,
      // so ignore the deep-link for viewers (the toolbar hides the button too).
      if (!canEditProjectRef.current) return
      setActivePanel('environment')
    } else if (openPanel === 'deploy') {
      // Deploy is the Pipeline modal's content (Build hidden until post-MVP).
      setActivePanel('pipeline')
    } else if (openPanel === 'logs') {
      // ('apiKeys' deep-links are ignored while the API Keys panel is hidden.)
      setActivePanel(openPanel)
    } else if (openPanel === 'database' || openPanel === 'storage') {
      // Storage absorbed the old Database panel — accept both ids so anything
      // still deep-linking 'database' keeps working.
      setActivePanel('storage')
    } else {
      return
    }
    openPanelConsumed.current = true
    navigate(location.pathname, { replace: true, state: {} })
  }, [state, navigate, location.pathname])
  // Channel-filter + row to focus when the Activity panel is opened from an inline chat card.
  const [activityFocus, setActivityFocus] = useState<{
    type: ActivityType | null
    id: string | null
  }>({ type: null, id: null })
  const handleActivityClick = useCallback((activity: Activity) => {
    // Activity reads are editor+ server-side (rows carry recipients/bodies) —
    // the toolbar hides the panel for viewers, and the inline chat cards must
    // not reopen it through the back door onto an endless 'Failed to load'.
    if (!canEditProjectRef.current) return
    setActivityFocus(focusFromActivity(activity))
    setActivePanel('activity')
  }, [])
  const [sidebarTab, setSidebarTab] = useState<'files' | 'search'>('files')
  // File-explorer width — draggable via a ResizeHandle (P4-01), the SAME
  // mechanism the chat split uses (WorkspaceLayout's ResizeHandle). Replaces the
  // old fixed 220px CSS width with a stateful, clamped width that the editor's
  // flex:1 absorbs the remainder of. The chat split persists in the in-memory
  // workspace store, but this sidebar isn't a top-level WorkspaceLayout panel, so
  // we mirror the app's pervasive per-project persistence instead — through the
  // bonded storage provider, which also survives reloads.
  const fileExplorerWidthKey = projectId ? `molecule_file_explorer_width_${projectId}` : null
  // The width is state now, not an effect that mirrors state into storage: a
  // drag (or keyboard nudge) writes through the store as it happens, and a slow
  // provider's hydration replaces the fallback without a remount. Persisting the
  // fallback from an effect would OVERWRITE the saved width before it ever
  // arrived — the reset this migration removes.
  const { value: storedFileExplorerWidth, setValue: setFileExplorerWidth } =
    useStoragePreference<number>(fileExplorerWidthKey)
  const fileExplorerWidth = normalizeFileExplorerWidth(storedFileExplorerWidth)
  // Main pane defaults to the live preview; users toggle to the code editor.
  // Both views stay mounted and laid out at full size (we hide the inactive
  // one with `visibility`), so opening a file or a diff from chat just needs
  // to flip this flag to reveal the editor.
  const [mainView, setMainView] = useState<'preview' | 'editor'>(
    isPublicWorkspace ? 'editor' : 'preview',
  )
  // Phone-width single-pane layout: chat + preview/editor can't sit side-by-side
  // below ~768px, so we show ONE surface at a time switched by a bottom nav.
  // `mobilePane` picks chat vs the main pane (the main pane's preview/editor is
  // still driven by `mainView`); defaults to chat — where the user drives builds.
  const isMobile = useIsMobile()
  const [mobilePane, setMobilePane] = useState<MobilePane>(
    isPublicWorkspace && !publicWorkspace?.defaultConversationId ? 'main' : 'chat',
  )
  // The mobile bottom nav's 4th tab — a bottom sheet holding everything the
  // desktop toolbar offers (panels, report/share, profile, theme).
  const [moreSheetOpen, setMoreSheetOpen] = useState(false)
  // Phone-only Files/Search drawer (the desktop inline sidebar column is
  // hidden below 768px) — opened from a floating button on the Code surface.
  const [filesDrawerOpen, setFilesDrawerOpen] = useState(false)
  // Crossing the 768px breakpoint (rotation, window resize) unmounts the
  // mobile-only sheets; without this reset they'd silently pop back open when
  // the viewport returns to phone width.
  useEffect(() => {
    if (!isMobile) {
      setMoreSheetOpen(false)
      setFilesDrawerOpen(false)
    }
  }, [isMobile])
  // iOS keyboard inset: interactive-widget=resizes-content only helps
  // Chrome/Android — iOS Safari overlays the keyboard on the layout viewport.
  // Publish the visual-viewport shortfall as --mol-kb-inset (consumed as
  // bottom padding by the mobile layout + pre-IDE phases in Workspace.css) so
  // the composer/bottom nav lift above the keyboard there too. The >80px gate
  // ignores browser-chrome jitter, and on Android-with-resizes-content the
  // shortfall is ~0 so this never double-counts.
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined' || !window.visualViewport) return
    const vv = window.visualViewport
    const update = (): void => {
      const inset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      document.documentElement.style.setProperty(
        '--mol-kb-inset',
        inset > 80 ? `${inset}px` : '0px',
      )
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      document.documentElement.style.removeProperty('--mol-kb-inset')
    }
  }, [isMobile])
  // Default the preview's device frame to responsive ('none', full-width) when
  // the layout crosses INTO the phone single-pane breakpoint. A fixed frame
  // (tablet 768px / mobile 375px) is wider than a phone viewport, so the preview
  // area (`overflow: auto`) would scroll horizontally inside its pane — the exact
  // overflow the mobile pass must eliminate. We fire only on the false→true
  // crossing (tracked by a ref) so the user can still cycle frames afterward;
  // depending on `previewState.device`/`setDevice` directly would re-pin 'none'
  // on every cycle and defeat the button.
  const wasMobileRef = useRef(false)
  useEffect(() => {
    if (isMobile && !wasMobileRef.current && previewState.device !== 'none') {
      setDevice('none')
    }
    wasMobileRef.current = isMobile
  }, [isMobile, previewState.device, setDevice])
  // The file explorer / search sidebar lives inside the editor view, so any
  // command that targets it must also reveal that view.
  const revealEditorSidebar = useCallback((tab: 'files' | 'search') => {
    setMainView('editor')
    setSidebarTab(tab)
  }, [])
  // Likewise the terminal docks under the editor.
  const toggleTerminal = useCallback(() => {
    setMainView('editor')
    setShowTerminal((v) => !v)
  }, [])
  const [showQuickOpen, setShowQuickOpen] = useState(false)
  const [showCommandPalette, setShowCommandPalette] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)

  // ---------------------------------------------------------------------------
  // Sandbox lifecycle
  // ---------------------------------------------------------------------------

  const startSandbox = useCallback(
    async (opts?: { auto?: boolean }) => {
      if (isPublicWorkspace) return
      if (!projectId || sandboxLoadingRef.current) return
      const retryGen = retryGenRef.current
      // Any start the USER (or a page event) makes begins a fresh count of automatic
      // retries (R123-L2): only the retry timer passes auto, and the strict check
      // matters because a click handler passes the click event here.
      if (opts?.auto !== true) startAutoRetryRef.current = 0
      bootCancelledRef.current = null
      // A retryable refusal that names when to try again — the egress assertion's
      // first minute after a boot, a box between probes — is retried by the IDE
      // itself, up to three times, keeping the booting state: the user should not
      // have to press Start again for the platform's own restart (R109-H1). The
      // IDE streams every start, so the refusal normally arrives as a stream
      // error event, not a 503 (R121-H1) — both paths come here.
      const scheduleAutoRetry = (
        retryable: boolean,
        retryAfterS: number | null,
        errorKey: string | null,
        sentence: string | null,
      ): boolean => {
        if (
          !retryable ||
          retryAfterS === null ||
          startAutoRetryRef.current >= 3 ||
          retryGen !== retryGenRef.current
        )
          return false
        startAutoRetryRef.current += 1
        setSandboxErrorKey(errorKey)
        setSandboxError(sentence)
        const id = window.setTimeout(
          () => {
            startAutoRetryTimerRef.current = null
            // The page may have moved on meanwhile (R119-M1, R120-H1): a clear since
            // this was scheduled ends it. And the wait keeps the booting state, so
            // any other status means another channel settled the sandbox meanwhile
            // — stopped, slept, lost (never auto-started), running or waking from
            // another tab — and the retry would start it a second time (R121-M1).
            if (retryGen !== retryGenRef.current) return
            const waiting = sandboxStatusRef.current
            if (waiting !== 'creating' && waiting !== 'queued') return
            void startSandboxRef.current({ auto: true })
          },
          Math.min(Math.max(retryAfterS, 5), 30) * 1000,
        )
        startAutoRetryTimerRef.current = { id, projectId }
        return true
      }
      sandboxLoadingRef.current = true
      setSandboxLoading(true)
      // The status this attempt started from, EXACTLY: a RETRYABLE refusal means
      // the attempt failed and nothing changed, so the project goes back to
      // whatever it was — running stays running, asleep stays asleep, lost stays
      // lost (a never-started project refused as "temporarily unreachable" /
      // egress-refused does not suddenly read "went to sleep due to inactivity",
      // nor does a running one read "stopped"; the copy comes from the error key).
      // A transient status showing ('creating' / 'queued' — an earlier attempt's
      // optimistic hop) stands for the last SETTLED status behind it. Only a
      // status never seen at all (nothing loaded yet) falls back to 'stopped'.
      const statusBefore = ((): string => {
        if (!statusSeenRef.current) return 'stopped'
        const current = sandboxStatusRef.current
        if (current !== '' && current !== 'creating' && current !== 'queued') return current
        return lastSettledStatusRef.current || 'stopped'
      })()
      setSandboxStatus('creating')
      setProgressSteps([])
      setSandboxCached(null)
      setSandboxPreWarmed(false)
      setDisplayProgress(0)
      setSandboxError(null)
      // A previous refusal's key + blocking list belong to that refusal: leave
      // them and a later, unrelated failure renders beside a stale Stop button.
      setSandboxErrorKey(null)
      setSandboxErrorBlocking([])

      // The boot is queue-decoupled: the worker keeps scaffolding on the server
      // even if THIS progress stream is cut short. The same-origin `/api` proxy
      // (Netlify) does not hold a long-lived SSE — during a quiet cold-boot gap
      // (e.g. npm install) it returns a 504 "inactivity timeout", which used to
      // land in the catch below and show "sandbox failed" for a sandbox that was
      // in fact still booting fine. So on any stream failure, reconcile against
      // the DURABLE status endpoint and only surface an error if the boot itself
      // genuinely failed.
      const recoverViaStatusPoll = async (): Promise<void> => {
        // Longer than any wait the server reports (a queue wait, or a plan-reserve hold of up to an hour for the team plan).
        const deadline = Date.now() + 75 * 60_000
        setProgressSteps([])
        setDisplayProgress(0)
        // Keep the UI in a booting state (not an error) while we reconcile.
        setSandboxStatus('creating')
        // A sleeping/stopped row read while a wake is still in flight is not an
        // outcome (R95-O-M4); read with no wake in flight, it is.
        while (Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 4_000))
          // This page left the project (unmounted, or switched to another): stop polling for it and
          // never write its preview or wait into the project now on screen.
          if (unmountedRef.current || liveProjectIdRef.current !== projectId) return
          // The user cancelled this start: nothing left to wait for.
          if (bootCancelledRef.current === projectId) {
            setBootWait(null)
            return
          }
          // Another channel (the presence socket's status broadcast) settled it
          // meanwhile: nothing left to reconcile.
          const shown = sandboxStatusRef.current
          if (shown && shown !== 'creating' && shown !== 'queued') {
            setBootWait(null)
            return
          }
          let status: string
          let previewUrl: string | null | undefined
          let waking: boolean
          let wait: unknown
          try {
            const res = await http.get<SandboxResponse>(`/projects/${projectId}/sandbox/status`)
            status = res.data.status
            previewUrl = res.data.previewUrl
            waking = res.data.waking === true
            wait = res.data.wait
          } catch (error) {
            // 401 / 403 / 404: the session ended, access was revoked or the project is gone — no later tick can answer
            // differently, and polling on would keep the UI on "creating" for the whole ceiling with no message.
            const failure = error as { status?: number; response?: { status?: number } } | null
            const code = failure?.status ?? failure?.response?.status
            if (code === 401 || code === 403 || code === 404) {
              setBootWait(null)
              setSandboxStatus('stopped')
              setSandboxError(
                t('workspace.sandboxStartFailed', undefined, {
                  defaultValue: 'Failed to start sandbox',
                }),
              )
              return
            }
            continue // transient — the next tick retries
          }
          // The boot is queued and its stream is gone: the status carries the
          // same wait the stream's queue-wait events did, so the notice (place
          // in line + estimate) survives the cut stream instead of a bare
          // "Starting sandbox…". Absent = no wait to show (the worker has the
          // job, or the boot is direct).
          const parsed = parseBootWait(wait)
          setBootWait(parsed ? { wait: parsed, receivedAt: Date.now() } : null)
          if (status === 'running') {
            setBootWait(null)
            setSandboxStatus('running')
            if (previewUrl) setUrl(previewUrl)
            return
          }
          if ((status === 'stopped' || status === 'sleeping') && waking) continue
          if (
            status === 'error' ||
            status === 'stopped' ||
            status === 'lost' ||
            status === 'sleeping'
          ) {
            setBootWait(null)
            setSandboxStatus(status)
            setSandboxError(
              t('workspace.sandboxStartFailed', undefined, {
                defaultValue: 'Failed to start sandbox',
              }),
            )
            return
          }
          // 'creating' | 'queued' — still booting; keep waiting.
        }
        // Ceiling hit — leave a soft hint; a reload re-reads the running state.
        setBootWait(null)
        setSandboxError(
          t('workspace.sandboxStartSlow', undefined, {
            defaultValue: 'Still starting. Reload the page if this continues.',
          }),
        )
      }
      // Set by a server-emitted `error` event so the catch shows THAT message
      // instead of poll-recovering (a genuine boot failure, not a dropped stream).
      let serverErrorMessage: string | null = null
      // The server's `retryable` / `errorKey` on that event: a retryable refusal
      // (the sandbox's files could not be read right now) puts the project back
      // in the status it had, with the message, so the user can press Wake /
      // Start again.
      let serverErrorRetryable = false
      // When the server said to try again (seconds), R121-H1 — the streamed form of
      // the JSON 503's retryAfter.
      let serverErrorRetryAfterS: number | null = null
      // The row's status the server named on a non-retryable error (a lost
      // sandbox: 'lost' or 'stopped'), R95-O-M3.
      let serverErrorStatus: string | null = null
      let serverErrorKey: string | null = null
      // Abortable so a page frozen mid-boot can cut the dead stream on resume
      // (the OS kills the socket without the pending read() ever rejecting); the
      // abort lands in the catch below as a dropped stream → status-poll recovery.
      const bootStream = new AbortController()
      bootStreamAbortRef.current = bootStream

      try {
        const token = http.getAuthToken()
        const headers: Record<string, string> = { Accept: 'text/event-stream' }
        if (token) headers['Authorization'] = `Bearer ${token}`

        const response = await fetch(`${API_BASE_URL}/projects/${projectId}/sandbox/start`, {
          method: 'POST',
          headers,
          credentials: 'include',
          signal: bootStream.signal,
        })

        if (!response.ok) {
          // A gateway/proxy timeout (the `/api` proxy caps long-lived responses)
          // is NOT a boot failure — the queue worker is still scaffolding.
          if (response.status === 502 || response.status === 504) {
            await recoverViaStatusPoll()
            return
          }
          // 409 "already being created" is NOT an error: the SERVER now enqueues the
          // boot itself the moment discovery ends (so a phone that drops its SSE
          // connection still boots), and another tab may also have started it. The
          // boot IS happening — reconcile against durable status and show its
          // progress/preview instead of a spurious error banner.
          if (response.status === 409) {
            await recoverViaStatusPoll()
            return
          }
          const errorData = await response.json().catch(() => ({}) as Record<string, unknown>)
          const errorKey = typeof errorData.errorKey === 'string' ? errorData.errorKey : null
          // A retryable refusal does not stop the project: it goes back to the
          // status it had (exactly — running stays running), with the message, and Wake /
          // Start tries again — or, when it names a retryAfter, the IDE tries again itself.
          const retryAfterS = typeof errorData.retryAfter === 'number' ? errorData.retryAfter : null
          if (
            scheduleAutoRetry(
              errorData.retryable === true,
              retryAfterS,
              errorKey,
              sandboxErrorSentence(errorData) || null,
            )
          )
            return
          startAutoRetryRef.current = 0
          setSandboxStatus(errorData.retryable === true ? statusBefore : 'stopped')
          setSandboxErrorKey(errorKey)
          setSandboxErrorBlocking(
            Array.isArray(errorData.blocking)
              ? (
                  errorData.blocking as Array<{
                    id?: unknown
                    name?: unknown
                    reason?: unknown
                    state?: unknown
                  }>
                )
                  .filter((b) => typeof b.id === 'string')
                  .map((b) => ({
                    id: b.id as string,
                    name:
                      typeof b.name === 'string' && b.name
                        ? b.name
                        : t('workspace.startStopBlocking.unnamed', undefined, {
                            defaultValue: 'another project',
                          }),
                    reason: b.reason === 'in-use' ? ('in-use' as const) : ('recent' as const),
                    ...(typeof b.state === 'string' ? { state: b.state } : {}),
                  }))
              : [],
          )
          setSandboxError(
            sandboxErrorSentence(errorData) ||
              t('workspace.sandboxStartFailed', undefined, {
                defaultValue: 'Failed to start sandbox',
              }),
          )
          return
        }
        // The cap on automatic retries is NOT reset here (R122-M1): an SSE refusal
        // arrives inside a 200, so resetting on response.ok made the "up to three"
        // unbounded. It resets when a start actually completes, below.

        // SSE stream — backend sends progress events
        if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''
          let gotDone = false
          let gotPreviewUrl = false

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop()!

            for (const line of lines) {
              if (!line.startsWith('data: ')) continue
              try {
                const data = JSON.parse(line.slice(6)) as Record<string, unknown>
                if (data.type === 'init') {
                  setSandboxCached(data.cached as boolean)
                  setSandboxPreWarmed(data.preWarmed === true)
                } else if (data.type === 'progress') {
                  // Why this step is slow, if it is (BootWaitNotice). Every
                  // progress event says it afresh, so one without a wait clears it.
                  const wait = parseBootWait(data.wait)
                  setBootWait(wait ? { wait, receivedAt: Date.now() } : null)
                  // A held start (`waiting`) has no checklist row of its own.
                  if (data.step === 'waiting') continue
                  setProgressSteps((prev) => {
                    const next = {
                      id: data.step as string,
                      detail: data.detail as string | undefined,
                    }
                    // Collapse an exact repeat of the step already at the tail: two
                    // boot paths emitting the same step (a caller and the function
                    // it calls) would otherwise show the same checklist row twice
                    // in a row. A repeat with a DIFFERENT detail is real progress
                    // and still appends.
                    const last = prev[prev.length - 1]
                    if (last && last.id === next.id && last.detail === next.detail) return prev
                    return [...prev, next]
                  })
                } else if (data.type === 'done') {
                  gotDone = true
                  startAutoRetryRef.current = 0
                  setProgressSteps([])
                  setDisplayProgress(0)
                  // Wake detection is centralized in setSandboxStatus (a sleeping/stopped →
                  // running transition stamps the wake), so this 'running' set covers it.
                  setSandboxStatus((data.status as string) || 'running')
                  if (data.previewUrl) {
                    setUrl(data.previewUrl as string)
                    gotPreviewUrl = true
                  }
                  // Import boot with the dependency install still running in the
                  // background: hold the stuck-preview watchdog until the post-install
                  // launch signals readiness (10-min safety ceiling for a dead install).
                  if (data.installPending) {
                    const holdStart = Date.now()
                    importInstallPendingUntilRef.current = holdStart + 600_000
                    // Durable fallback for the one-shot woke broadcast (see
                    // installReadyPollRef): poll status for previewReadyAt and treat
                    // a stamp from THIS boot as the wake. The WS woke handler clears
                    // this poll when the fast path wins.
                    clearInstallReadyPoll()
                    installReadyPollRef.current = setInterval(() => {
                      if (Date.now() >= importInstallPendingUntilRef.current) {
                        clearInstallReadyPoll()
                        return
                      }
                      void http
                        .get<SandboxResponse & { previewReadyAt?: string | null }>(
                          `/projects/${projectId}/sandbox/status`,
                        )
                        .then((statusRes) => {
                          const readyAt = statusRes.data.previewReadyAt
                          // Only a stamp from THIS boot counts — a persisted stamp
                          // from an earlier session must not fire the wake early.
                          if (!readyAt || new Date(readyAt).getTime() < holdStart - 60_000) return
                          clearInstallReadyPoll()
                          importInstallPendingUntilRef.current = 0
                          if (statusRes.data.previewUrl) setUrl(statusRes.data.previewUrl)
                          setSandboxWakeAt(Date.now())
                        })
                        .catch(() => {
                          // Transient poll failure — the next tick retries; the WS
                          // broadcast and the 10-min ceiling still bound the wait.
                        })
                    }, 4_000)
                  }
                } else if (data.type === 'error') {
                  serverErrorRetryable = data.retryable === true
                  serverErrorRetryAfterS =
                    typeof data.retryAfter === 'number' ? data.retryAfter : null
                  serverErrorStatus =
                    data.status === 'lost' ||
                    data.status === 'stopped' ||
                    data.status === 'sleeping'
                      ? data.status
                      : null
                  serverErrorKey = typeof data.errorKey === 'string' ? data.errorKey : null
                  serverErrorMessage =
                    sandboxErrorSentence({
                      error: data.message,
                      errorKey: data.errorKey,
                      messageKey: data.messageKey,
                    }) || 'Start failed'
                  throw new Error(serverErrorMessage)
                }
              } catch (e) {
                if (e instanceof SyntaxError) continue
                throw e
              }
            }
          }

          // Stream ended without a done event (backend crash / disconnect), or the
          // done event arrived without a previewUrl (an older queue worker publishes
          // a bare `{ type: 'done' }`) — poll the status endpoint to recover the
          // actual state, previewUrl included. Without this the preview panel has no
          // URL until the next full page load.
          if (!gotDone || !gotPreviewUrl) {
            const statusRes = await http.get<SandboxResponse>(
              `/projects/${projectId}/sandbox/status`,
            )
            setProgressSteps([])
            setDisplayProgress(0)
            if (statusRes.data.status === 'creating' || statusRes.data.status === 'queued') {
              // Stream ended (proxy timeout) before the boot finished — keep
              // reconciling against durable status instead of stalling here.
              await recoverViaStatusPoll()
            } else {
              setSandboxStatus(statusRes.data.status)
              if (statusRes.data.previewUrl) setUrl(statusRes.data.previewUrl)
            }
          }
        } else {
          // Fallback: non-streaming JSON response (old backend)
          const data = (await response.json()) as SandboxResponse
          setProgressSteps([])
          setDisplayProgress(0)
          setSandboxStatus(data.status)
          if (data.previewUrl) setUrl(data.previewUrl)
        }
      } catch (_err) {
        setProgressSteps([])
        setDisplayProgress(0)
        // Cut short by the user's Cancel (cancelBoot settles the status itself).
        if (bootCancelledRef.current === projectId) return
        if (serverErrorMessage) {
          // Genuine boot failure the server reported — show it, don't poll. A
          // retryable one puts the project back exactly where it was (running
          // stays running) so Wake / Start can try again — unless it named a
          // retryAfter, in which case the IDE tries again itself (R121-H1).
          if (
            scheduleAutoRetry(
              serverErrorRetryable,
              serverErrorRetryAfterS,
              serverErrorKey,
              serverErrorMessage,
            )
          )
            return
          startAutoRetryRef.current = 0
          setSandboxStatus(serverErrorRetryable ? statusBefore : (serverErrorStatus ?? 'stopped'))
          setSandboxErrorKey(serverErrorKey)
          setSandboxError(serverErrorMessage)
        } else {
          // A dropped progress stream (proxy timeout, network blip) is not a boot
          // failure — the queue worker is still scaffolding. Reconcile against the
          // durable status before surfacing any error.
          await recoverViaStatusPoll()
        }
      } finally {
        if (bootStreamAbortRef.current === bootStream) bootStreamAbortRef.current = null
        sandboxLoadingRef.current = false
        setSandboxLoading(false)
      }
    },
    [projectId, http, setUrl, t, clearInstallReadyPoll],
  )

  /**
   * Stop one of the owner's other running projects (named by the refused
   * Start) and retry this Start — the one action the limit sentence asks for.
   *
   * A blocker the API recorded LOST while stopping (its sandbox was already
   * gone) no longer occupies a running slot either, so the retry proceeds —
   * the loss sentence still toasts, but it must not dead-end the start.
   *
   * @param projectIdToStop - The blocking project.
   */
  const stopBlockingAndStart = useCallback(
    async (projectIdToStop: string): Promise<void> => {
      let slotFreed = false
      try {
        await http.post(`/projects/${projectIdToStop}/sandbox/stop`)
        slotFreed = true
      } catch (error) {
        const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data
        const sentence = typeof data?.error === 'string' ? data.error : null
        const errorKey = typeof data?.errorKey === 'string' ? data.errorKey : null
        if (errorKey === 'sandbox.error.lost' || errorKey === 'sandbox.error.notFound')
          slotFreed = true
        emitNotification(
          sentence
            ? t(
                'workspace.startStopBlockingFailedDetail',
                { detail: sentence },
                { defaultValue: 'Could not stop the other project. {{detail}}' },
              )
            : t('workspace.startStopBlockingFailed', undefined, {
                defaultValue: 'Could not stop the other project.',
              }),
          'error',
        )
        console.error('Workspace: failed to stop the blocking project', error)
      }
      if (!slotFreed) return
      setSandboxError(null)
      setSandboxErrorKey(null)
      setSandboxErrorBlocking([])
      void startSandbox()
    },
    [http, startSandbox, t],
  )

  /**
   * Cancel this project's start while it is still waiting (in line, or held
   * while the platform frees space): the same stop action as everywhere else —
   * the api cancels a boot no worker is running yet and answers 409 for one
   * that is already under way.
   */
  const cancelBoot = useCallback(async (): Promise<void> => {
    if (!projectId || cancellingBoot) return
    setCancellingBoot(true)
    // Set before the request: the stream may end (its terminal event) before the answer arrives.
    bootCancelledRef.current = projectId
    try {
      const res = await http.post<{ status?: string }>(`/projects/${projectId}/sandbox/stop`)
      clearStartAutoRetry()
      bootStreamAbortRef.current?.abort()
      setBootWait(null)
      setProgressSteps([])
      setDisplayProgress(0)
      setSandboxError(null)
      setSandboxErrorKey(null)
      setSandboxStatus(res.data?.status === 'lost' ? 'lost' : 'stopped')
    } catch (error) {
      bootCancelledRef.current = null
      emitNotification(
        t('workspace.bootWait.cancelFailed', undefined, {
          defaultValue: 'Could not cancel. The sandbox may already be starting.',
        }),
        'error',
      )
      console.error('Workspace: failed to cancel the waiting start', error)
      // Only a boot that is genuinely under way (the api answers 409: a worker has it) is followed
      // on the status poll. Any other refusal (400: nothing to stop, a cancel another tab already
      // made; 403; a dropped request) must never start a boot the user just asked to cancel.
      const refusedStatus = (error as { response?: { status?: number } })?.response?.status
      if (refusedStatus === 409 && !sandboxLoadingRef.current) {
        void startSandboxRef.current({ auto: true })
      }
    } finally {
      setCancellingBoot(false)
    }
  }, [projectId, cancellingBoot, http, clearStartAutoRetry, t])

  // Auto-start sandbox once when project loads — use ref for startSandbox
  // to avoid re-triggering when the callback identity changes
  const startSandboxRef = useRef(startSandbox)
  startSandboxRef.current = startSandbox
  const startedRef = useRef(false)

  useEffect(() => {
    if (isPublicWorkspace) {
      setSandboxStatus('running')
      if (project?.previewUrl) setUrl(project.previewUrl)
      return
    }
    if (!project || startedRef.current) return
    startedRef.current = true

    const initialStatus = project.sandboxStatus || 'stopped'
    setSandboxStatus(initialStatus)

    if (initialStatus === 'running') {
      // Already running — restore the preview URL the server issued.
      //
      // There is deliberately no fallback. This used to guess
      // `https://<slug>.preview.molecule.dev`, and a slug is `slugify(name)` —
      // deterministic and guessable, which is exactly what the preview plane was
      // moved OFF (the unguessable per-project token is the access credential;
      // see api/src/sandbox/preview-url.ts). A row with no previewUrl is a row
      // whose preview is not addressable yet, and the status poll and the health
      // check are what resolve that — not a URL invented in the client.
      if (project.previewUrl) {
        setUrl(project.previewUrl)
      }
      return
    }

    // Sandbox boot is now deferred until Synthase's discovery phase selects a
    // starting point. Two cases warrant (re)attaching to the boot on load:
    //   • status creating/queued — a boot is already in flight (page refresh).
    //   • templateSlug present — discovery already chose a starting point in a
    //     prior session but the sandbox isn't running yet (e.g. left before boot).
    // The backend dedups concurrent starts, so this won't double-scaffold.
    // A brand-new project still in discovery (stopped, no templateSlug) does NOT
    // boot here — the chat `ready_to_build` event triggers it.
    // A lost project is never auto-started: the user clicks Start fresh copy.
    if (initialStatus === 'lost') return
    if (initialStatus === 'creating' || initialStatus === 'queued' || project.templateSlug) {
      startSandboxRef.current()
    }
  }, [project, setUrl])

  // Keep the preview bond pointed at the row's CURRENT preview URL. The mount
  // effect above sets it exactly once and only when the sandbox was ALREADY
  // running — so a tab that mounted while the sandbox slept (or before another
  // member's boot minted the URL) had an URL-less preview forever: a remote wake
  // flipped it to 'running' but "Loading preview…" never resolved, because
  // refreshPreview() with no URL is a no-op (observed live 2026-08-29: viewer
  // wakes, editor browser stuck). Re-points when the bond has nothing or its
  // BASE differs (restart/migrate/snapshot minted a new one via reloadProject);
  // a navigated in-app path on the same base is never clobbered (startsWith).
  useEffect(() => {
    if (!project?.previewUrl) return
    if (!previewState.url || !previewState.url.startsWith(project.previewUrl)) {
      setUrl(project.previewUrl)
    }
  }, [project?.previewUrl, previewState.url, setUrl])

  // Post-discovery build kickoff. After discovery picks a starting point and
  // the sandbox boots, the AI has no trigger to start building — it would sit
  // idle (and the first thing to break the silence was the template's own boot
  // errors hijacking the flow). Auto-send a plan→build instruction. The AI
  // already has the full discovery transcript + selection rationale + template
  // doc in context, so this just tells it to proceed: write the plan, then
  // implement it.
  //
  // CRITICAL: wait for !sandboxLoading, not just status==='running'. startSandbox
  // sets status='running' (on the SSE 'done') but only clears sandboxLoading in
  // its finally block — between the two, isBooting is still true and the IDE
  // ChatPanel (which consumes pendingMessage) isn't mounted yet. Firing then
  // bumps the pending-message key before the ChatPanel can capture it, so the
  // message is silently dropped (the bug: AI stalls after "switch to plan mode").
  // Firing once sandboxLoading clears lines up with the IDE ChatPanel mounting.
  // Two paths reach this kickoff and the message MUST cover both, because it cannot
  // assume a plan exists:
  //   • Template match → the during-boot plan turn produced a draft; the server keeps the
  //     kickoff in PLAN mode and injects the draft with a verify-against-the-codebase
  //     instruction (read the files it edits, correct it, THEN save_plan — the turn then
  //     holds on the plan-approval card; the user's approval switches to execute and
  //     starts the build).
  //   • Custom app / no draft → no plan was captured; the kickoff lands in PLAN mode with
  //     save_plan available. Here the planner MUST save a plan, or it spins forever doing
  //     read-only research in the expensive model (the regression a hardcoded "do NOT call
  //     save_plan, the plan is finished" caused — it was a lie for the no-draft case).
  // Either way the server's own in-context instructions are authoritative; this message
  // just breaks the silence and re-states the contract.
  const fireBuildKickoff = useCallback(() => {
    // A read-only VIEWER must never fire the (platform-initiated) build kickoff:
    // the server 403s a viewer's chat send, and the ~10s post-wake reconcile was
    // surfacing that as a red "Access denied" in their chat. An editor's own
    // session drives the build; the viewer just watches.
    if (!canEditProjectRef.current) return
    setAwaitingSandboxBoot(false)
    setPendingSuppressUser(true)
    setPendingUserInitiated(false)
    setFixWithAIMessage(
      'The development environment is now fully ready and your file tools are ACTIVE again — ' +
        'read_file, write_file, edit_file, list_files, and exec_command all work now. The "no file ' +
        'access" restriction from the planning turn is OVER; do not say files are unavailable. ' +
        'If an Active Implementation Plan is shown in your context, implement it now — do NOT ' +
        'rewrite, re-summarize, or re-save it; go straight to building it step by step, starting with ' +
        'a write_file/edit_file on the first step. If NO active plan is present yet, write one now with ' +
        'save_plan (the user then reviews it on an approval card; once they approve, you are ' +
        'switched to execute mode and building begins). ' +
        'Either way, stop surveying the project and start producing — do not keep reading files.',
    )
    fixWithAIKeyRef.current++
    setFixWithAIKey(fixWithAIKeyRef.current)
  }, [])

  useEffect(() => {
    // Fire the build kickoff once the sandbox is running. The during-boot plan
    // turn may still be streaming in the same conversation; that's fine — this
    // kickoff is a new request and the chat provider's 409-retry serializes it
    // behind the active turn (the per-conversation server lock), so it lands as
    // soon as the plan turn finishes. No awaitingPlan gate is needed: the chat
    // store is mount-independent, so the plan keeps streaming into the IDE chat.
    if (sandboxStatus !== 'running' || sandboxLoading || !buildKickoffPendingRef.current) return
    buildKickoffPendingRef.current = false
    fireBuildKickoff()
  }, [sandboxStatus, sandboxLoading, fireBuildKickoff])

  // Kickoff reconcile — self-heal a stalled pipeline. The one-shot kickoff above
  // is spent the moment it fires; if that request's socket drops mid-turn (the
  // "connection issue… retrying" banner), or the page reloads after boot, nothing
  // re-drives the build and Synthase sits silent in plan mode forever (observed
  // 2026-08-10). While the sandbox is running and no turn is streaming, poll the
  // history endpoint's `pendingKickoff` flag (server-computed: plan mode, no saved
  // plan, not streaming, not waiting on ask_user) and re-fire the kickoff when set.
  // Throttled: poll every 20s only while idle, re-kick at most once per 45s.
  const lastKickoffReconcileRef = useRef(0)
  useEffect(() => {
    if (isPublicWorkspace || !projectId) return
    if (sandboxStatus !== 'running' || sandboxLoading || aiBuilding) return
    const id = window.setInterval(() => {
      void (async () => {
        if (Date.now() - lastKickoffReconcileRef.current < 45_000) return
        try {
          // `?state=1`: the two booleans this reads come from the conversation
          // row alone. Without it the poll pulled the ENTIRE transcript every
          // 20 s while the sandbox sat idle — ~97 ms to build and serialise at
          // 235 messages and ~27 s at 600, on the same machine running the
          // build. The projection carries `pendingKickoff` + `streaming` from
          // the same code the full payload uses (chat-handler `pendingKickoffFor`).
          const res = await http.get<{ pendingKickoff?: boolean; streaming?: boolean }>(
            `/projects/${projectId}/chat?state=1`,
          )
          if (res.data.pendingKickoff && !res.data.streaming) {
            lastKickoffReconcileRef.current = Date.now()
            buildKickoffPendingRef.current = false
            fireBuildKickoff()
          }
        } catch (_error) {
          // Transient — the next tick retries.
        }
      })()
    }, 20_000)
    return () => window.clearInterval(id)
  }, [projectId, sandboxStatus, sandboxLoading, aiBuilding, http, fireBuildKickoff])

  // ---------------------------------------------------------------------------
  // File tree
  // ---------------------------------------------------------------------------

  const loadFiles = useCallback(
    // `background: true` marks the request as automated (reconcile polling) so
    // the API skips its activity touch — otherwise an idle-but-visible tab
    // resets the sandbox auto-hibernation clock forever.
    async (
      dirPath: string = '/workspace',
      opts?: { background?: boolean },
    ): Promise<FileNode[] | null> => {
      if (!projectId) return []
      try {
        const res = await http.get<{ path: string; entries: DirEntry[] }>(
          `/projects/${projectId}/files`,
          { params: { path: dirPath, ...(opts?.background ? { background: '1' } : {}) } },
        )
        const patterns = gitignoreRef.current
        return (res.data.entries || [])
          .filter((entry) => entry.name !== 'node_modules' && entry.name !== '.git')
          .map((entry) => ({
            name: entry.name,
            path: `${dirPath}/${entry.name}`,
            type: entry.type,
            isDimmed: patterns.some(
              (p) =>
                (p.endsWith('/') && entry.type === 'directory' && entry.name === p.slice(0, -1)) ||
                (p.startsWith('*.') && entry.name.endsWith(p.slice(1))) ||
                entry.name === p,
            ),
            ...(entry.symlinkTarget ? { symlinkTarget: entry.symlinkTarget } : {}),
          }))
      } catch (error) {
        // "I could not look" is NOT "there is nothing there". An empty array here
        // is indistinguishable from a genuinely empty directory, and the reconcile
        // injects it — so one 429 or a busy sandbox visibly deleted a folder's
        // children in the tree. `null` lets the caller keep what it already has.
        console.debug('[Workspace] file listing failed', dirPath, error)
        return null
      }
    },
    [projectId, http],
  )

  // Load .gitignore patterns, file tree, and git status once sandbox is running
  useEffect(() => {
    if (sandboxStatus !== 'running' || !projectId) return
    let cancelled = false
    // An imported repo clones to `/workspace/<repoName>/`, so its `.gitignore` lives
    // there — NOT at the workspace root. Resolve the repo dir first (one cheap lookup;
    // returns nothing for a scaffolded project) so imports actually pick up their own
    // ignore patterns instead of 404-ing on `/workspace/.gitignore`.
    const resolveGitignorePath = async (): Promise<string> => {
      try {
        const res = await http.get<{ importSources?: Array<{ repoName?: string }> }>(
          `/projects/${projectId}/git-import-source`,
        )
        const repoName = res.data?.importSources?.[0]?.repoName
        if (repoName) return `${repoName}/.gitignore`
      } catch (_error) {
        // Not an import (or the endpoint is unavailable) — fall back to the root.
      }
      return '.gitignore'
    }
    const applyFiles = (nodes: FileNode[] | undefined): void => {
      if (nodes && !cancelled) setFiles(nodes)
    }
    void resolveGitignorePath().then((gitignorePath) => {
      if (cancelled) return
      http
        .get<{ path: string; content: string }>(`/projects/${projectId}/files/${gitignorePath}`)
        .then((res) => {
          gitignoreRef.current = res.data.content
            .split('\n')
            .map((l) => l.trim())
            .filter((l) => l && !l.startsWith('#'))
          return loadFiles('/workspace')
        })
        .then((nodes) => applyFiles(nodes ?? undefined))
        .catch(() => {
          // .gitignore may not exist — still load files
          loadFiles('/workspace').then((nodes) => applyFiles(nodes ?? undefined))
        })
    })
    fetchGitStatus()
    return () => {
      cancelled = true
    }
  }, [sandboxStatus, projectId, http, loadFiles, fetchGitStatus])

  /**
   * Tell the user a file operation failed, and why. Two short sentences: what
   * did not happen, then the cause.
   */
  const reportFileOpFailure = useCallback(
    (what: string, error: unknown): void => {
      const reason = fileOpErrorReason(error, t)
      if (reason) emitNotification(`${what} ${reason}`, 'error')
    },
    [t],
  )

  // Reload the entire file tree, including expanded subdirectories.
  // Guarded against concurrent calls — if a reload is already in-flight,
  // we remember that another was requested and re-run once the current finishes.
  const reloadingRef = useRef(false)
  const reloadRequestedRef = useRef(false)

  const reloadFileTree = useCallback(async () => {
    // If already reloading, just flag that another reload is needed
    if (reloadingRef.current) {
      reloadRequestedRef.current = true
      return
    }
    reloadingRef.current = true
    reloadRequestedRef.current = false

    try {
      // Every reloadFileTree call is automated (file-watch WS events, the 45s
      // reconcile poll) — never a direct user action — so all its reads are
      // background: they must not block sandbox auto-hibernation.
      const rootNodes = await loadFiles('/workspace', { background: true })
      // A failed listing (null) or an empty one leaves the current tree alone —
      // replacing it would delete the user's file tree over a transient 429.
      if (!rootNodes || rootNodes.length === 0) return
      // Read which directories are expanded — the FileExplorer persists them
      // through the same bonded storage provider, so the keys line up.
      let expandedDirs: string[] = []
      let collapsedDirs: string[] = []
      {
        const store = getPreferenceStore()
        const expandKey = `molecule_file_tree_${projectId}`
        // A reload can run before an ASYNCHRONOUS provider has hydrated this
        // key. Awaiting the (deduped) hydration is what keeps the already-open
        // directories' children in the tree instead of silently collapsing the
        // saved state on the first reconcile.
        await store.hydrate([expandKey])
        const parsed = store.get<{ expanded?: string[]; collapsed?: string[] }>(expandKey)
        // Absent or corrupt reads as null — the expand state just resets.
        expandedDirs = parsed?.expanded ?? []
        collapsedDirs = parsed?.collapsed ?? []
      }

      // Root-level directories default to expanded (depth < 1 in
      // isExpandedFromState) but aren't tracked in the persisted state.  Include
      // them so reloadFileTree fetches their children on every reload,
      // keeping the tree in sync when files are added/removed by the AI.
      const collapsedSet = new Set(collapsedDirs)
      for (const node of rootNodes) {
        if (
          node.type === 'directory' &&
          !collapsedSet.has(node.path) &&
          !expandedDirs.includes(node.path)
        ) {
          expandedDirs.push(node.path)
        }
      }

      // Cap total expanded dirs to prevent runaway fetches from symlink cycles.
      // No user manually expands 200+ directories — if we have more, it's a cycle.
      const MAX_EXPANDED_DIRS = 200
      if (expandedDirs.length > MAX_EXPANDED_DIRS) {
        console.warn(
          '[reloadFileTree] %d expanded dirs exceeds limit of %d (possible symlink cycle) — truncating',
          expandedDirs.length,
          MAX_EXPANDED_DIRS,
        )
        expandedDirs = expandedDirs.slice(0, MAX_EXPANDED_DIRS)
      }

      // Fetch children for all expanded directories in parallel
      if (expandedDirs.length > 0) {
        // Fetch in small batches rather than firing every expanded directory at
        // once: each listing costs the API several sandbox round trips, and a
        // 200-wide burst every 45s is what pushed a busy project into its own
        // rate limit — whose 429s then read as empty directories.
        const childResults: Array<{ dir: string; children: FileNode[] | null }> = []
        for (let i = 0; i < expandedDirs.length; i += FILE_TREE_FETCH_BATCH) {
          const batch = expandedDirs.slice(i, i + FILE_TREE_FETCH_BATCH)
          childResults.push(
            ...(await Promise.all(
              batch.map(async (dir) => ({
                dir,
                children: await loadFiles(dir, { background: true }),
              })),
            )),
          )
        }
        // Build a map of dir → children for injection. A failed listing is
        // OMITTED, not recorded as empty, so inject() keeps what is on screen.
        const childMap = new Map(
          childResults
            .filter((r): r is { dir: string; children: FileNode[] } => r.children !== null)
            .map((r) => [r.dir, r.children]),
        )

        // Recursively inject fetched children into the new root nodes
        let injectCount = 0
        const inject = (nodes: FileNode[], depth = 0): FileNode[] => {
          if (depth > 50) {
            console.error(
              '[reloadFileTree] inject() depth > 50, aborting to prevent freeze',
              nodes[0]?.path,
            )
            return nodes
          }
          return nodes.map((node) => {
            injectCount++
            if (injectCount > 100_000) {
              if (injectCount === 100_001) {
                console.error(
                  '[reloadFileTree] inject() exceeded 100k iterations, aborting to prevent freeze',
                )
              }
              return node
            }
            const children = childMap.get(node.path)
            if (children) {
              return { ...node, children: inject(children, depth + 1) }
            }
            if (node.children) {
              return { ...node, children: inject(node.children, depth + 1) }
            }
            return node
          })
        }

        const fresh = inject(rootNodes)

        // Merge with existing tree to preserve unchanged node references,
        // so React skips re-rendering subtrees that didn't change.
        setFiles((prev) => mergeFileTree(prev, fresh))
      } else {
        setFiles((prev) => (prev.length === 0 ? rootNodes : mergeFileTree(prev, rootNodes)))
      }
      await fetchGitStatus()
    } finally {
      reloadingRef.current = false
      // If another reload was requested while we were busy, do one more pass
      // to pick up the latest state.
      if (reloadRequestedRef.current) {
        reloadRequestedRef.current = false
        reloadFileTree()
      }
    }
  }, [loadFiles, projectId, fetchGitStatus])

  // Heartbeat — a REAL user interaction sends a throttled ping so the sandbox stays alive
  // while the IDE is actively in use, even with no file saves / API calls.
  //
  // Critically this must NOT fire on the `scroll` event: `scroll` fires for PROGRAMMATIC
  // scrolling too (a streaming chat auto-scrolling, terminal output, a retrying/animated
  // preview), so a window-level `scroll` listener kept the sandbox marked active every 60s
  // forever — even with the user AFK — and it never hibernated. Bind to actual user
  // GESTURES instead (`wheel`/`touchstart` are the real scroll intents and never fire from
  // a programmatic scroll), plus pointer/key, and only while the tab is visible — a
  // backgrounded tab is not "in use".
  useEffect(() => {
    if (isPublicWorkspace || !projectId || sandboxStatus !== 'running') return

    let lastPing = 0
    const THROTTLE = 60_000 // match server-side throttle

    function ping(e?: Event): void {
      // Ignore synthetic events and anything fired while the tab is hidden — neither is a
      // user actively using the IDE.
      if (e && e.isTrusted === false) return
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastPing < THROTTLE) return
      lastPing = now
      http.post(`/projects/${projectId}/sandbox/heartbeat`).catch(() => {})
    }

    // Real user-intent events only — NOT `scroll` (which fires on programmatic scroll).
    const events = ['mousemove', 'keydown', 'pointerdown', 'wheel', 'touchstart'] as const
    for (const ev of events) window.addEventListener(ev, ping, { passive: true })

    return () => {
      for (const ev of events) window.removeEventListener(ev, ping)
    }
  }, [projectId, sandboxStatus, http])

  // Periodic sandbox-status reconcile — the "belt" to the sandbox_status broadcast
  // "suspenders", in BOTH directions. The IDE learns of a hibernation only via the
  // one-shot WS broadcast, and the file-watch WS does NOT close on hibernation, so a
  // missed broadcast leaves the IDE stuck believing the sandbox is awake while the
  // preview can't load. Symmetrically, a browser showing a SLEEPING/STOPPED sandbox
  // must pick up a wake another member performed (the presence broadcast is the fast
  // path; this poll covers a missed one) — before this, that view never recovered
  // without a refresh. Runs only from SETTLED local states (never fighting the local
  // boot/wake transients: creating/queued/waking) and only while the tab is visible.
  // Shared by the slow poll below and the page-resume reconcile (which runs the
  // same convergence the instant a phone comes back instead of up to 45s later).
  // Resolves to the DURABLE status it read (or null on a failed read).
  const reconcileSandboxStatus = useCallback(async (): Promise<string | null> => {
    if (!projectId || projectId === 'new') return null
    try {
      const res = await http.get<Project>(`/projects/${projectId}`)
      const status = res.data.sandboxStatus
      if (!status) return null
      const local = sandboxStatusRef.current
      if (status !== 'running' && local === 'running') {
        // Settled away-from-running transition the broadcast missed.
        setSandboxStatus(status)
      } else if (
        status === 'running' &&
        (local === 'sleeping' || local === 'stopped' || local === 'lost')
      ) {
        // Another member woke the sandbox and the broadcast was missed —
        // adopt it, with wake patience so the preview panel tolerates the
        // freshly-relaunched app instead of calling it blank. Adopt the
        // whole row too: the boot/wake may have minted previewUrl, which
        // the preview-URL sync effect needs.
        setProject(res.data)
        setSandboxWakeAt(Date.now())
        setSandboxStatus('running')
      }
      return status
    } catch (_error) {
      // Status reconcile is best-effort — a transient API hiccup must not disrupt
      // the IDE; the next tick (or the WS broadcast) will catch the change.
      return null
    }
  }, [projectId, http, setSandboxStatus])

  useEffect(() => {
    if (isPublicWorkspace) return
    const settled =
      sandboxStatus === 'running' ||
      sandboxStatus === 'sleeping' ||
      sandboxStatus === 'stopped' ||
      sandboxStatus === 'lost'
    if (!projectId || !settled) return
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      void reconcileSandboxStatus()
    }, SANDBOX_STATUS_RECONCILE_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [projectId, sandboxStatus, reconcileSandboxStatus])

  // Page-resume reconcile — what a manual refresh did for a returning phone,
  // done in place. A page frozen by a lock / app switch / long background comes
  // back with every timer stalled and every socket dead-but-OPEN, so nothing it
  // holds can tell it the sandbox hibernated meanwhile: the preview just never
  // loaded until the user refreshed (the 45s poll is the only thing that would
  // have caught up, and a phone user does not wait 45s on a blank preview).
  //   • a boot progress stream that was in flight when the page froze has a
  //     pending read() that never rejects — abort it so startSandbox's catch
  //     reconciles against durable status instead of spinning forever;
  //   • otherwise re-read durable status now, and mirror the reload path: a
  //     fresh load auto-starts any project past discovery, so reopening the IDE
  //     on a sandbox that fell asleep wakes it instead of parking the user on a
  //     Wake button they only find after a refresh. Editor-gated at the source
  //     (a viewer's wake would 403) and visible-tab only.
  usePageResume(({ hiddenForMs }) => {
    if (isPublicWorkspace) return
    if (!projectId || projectId === 'new') return
    if (sandboxLoadingRef.current) {
      if (hiddenForMs >= WS_SERVER_REAPED_AFTER_MS) bootStreamAbortRef.current?.abort()
      return
    }
    void reconcileSandboxStatus().then((status) => {
      if (status !== 'sleeping') return
      if (!canEditProjectRef.current || document.visibilityState !== 'visible') return
      if (sandboxLoadingRef.current) return
      startSandboxRef.current()
    })
  }, RESUME_RECONCILE_AFTER_HIDDEN_MS)

  // File watch WebSocket — refreshes file tree when AI writes files.
  // Only connects when sandbox is running. Reconnects with backoff on
  // unexpected drops so hibernation broadcasts are never missed.
  useEffect(() => {
    if (isPublicWorkspace || !projectId || sandboxStatus !== 'running') return
    if (!wsAuthReady) return // wait for the guest WS ticket so the first connect is authed

    let intentionalClose = false
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let reconnectAttempts = 0
    let currentWs: WebSocket | null = null
    let reloadTimer: ReturnType<typeof setTimeout> | null = null
    // Distinguishes the very first successful connect from a reconnect after a
    // drop, so the reconnect-catch-up reload below doesn't double the initial
    // sandbox-ready tree load on first open.
    let hasOpened = false

    // Every (re)connect goes through the ticket funnel: a reconnect after the
    // 6h WS ticket expired is rejected at the handshake forever otherwise.
    // No-op while the held ticket is usable.
    let connectPending = false
    function connect() {
      if (connectPending) return
      connectPending = true
      void ensureWsAuthToken(http).then((token) => {
        connectPending = false
        if (intentionalClose) return
        openSocket(token)
      })
    }

    function openSocket(token: string | null) {
      const wsUrl = buildWsUrl(`/ws/projects/${projectId}/files`, token)

      const ws = new WebSocket(wsUrl)
      currentWs = ws

      ws.onopen = () => {
        reconnectAttempts = 0
        if (hasOpened) {
          // Reconnected after a drop. The API does NOT buffer the `file_change`
          // broadcasts emitted while the socket was down, so any files created
          // or deleted during the gap (an AI write, a shell command) would be
          // lost from the tree until a manual reload. Reload now to catch up.
          // Guarded by `hasOpened` so the first connect doesn't fire — the
          // sandbox-ready effect already loads the tree on initial open.
          reloadFileTree()
        }
        hasOpened = true
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string) as {
            event?: string
            type?: string
            path?: string
            status?: string
            reason?: string
          }
          if (msg.event === 'file_change') {
            // Leading-edge throttle — fire immediately on the first event so
            // new files appear in the tree right away, then coalesce subsequent
            // events into a trailing reload 800ms after the last one.
            if (!reloadTimer) {
              reloadFileTree()
            } else {
              clearTimeout(reloadTimer)
            }
            reloadTimer = setTimeout(() => {
              reloadTimer = null
              reloadFileTree()
            }, 800)
          } else if (msg.event === 'sandbox_status') {
            // A wake broadcast (running/woke) means the dev servers were just relaunched —
            // stamp it so PreviewPanel enters wake patience instead of accusing the
            // restarting preview of being blank. This is the ONLY signal for silent wakes
            // (a chat turn's ensureSandboxRunning), where the IDE stayed mounted throughout.
            if (msg.status === 'running' && msg.reason === 'woke') {
              setSandboxWakeAt(Date.now())
              // The post-install launch (or any wake) just brought the dev servers
              // up — the install-pending hold on the stuck watchdog ends here and
              // normal wake patience takes over. The fast WS path won; stop the
              // durable poll fallback.
              importInstallPendingUntilRef.current = 0
              clearInstallReadyPoll()
            }
            if (
              (sandboxLoadingRef.current || startAutoRetryTimerRef.current) &&
              msg.reason === 'boot_failed'
            ) {
              lastSettledStatusRef.current = msg.status || 'stopped'
              return
            }
            setSandboxStatus(msg.status || 'stopped')
          }
        } catch (_error) {
          // ignore malformed WebSocket messages — JSON.parse throws on non-JSON frames
        }
      }

      ws.onerror = () => ws.close()

      ws.onclose = () => {
        if (reloadTimer) clearTimeout(reloadTimer)
        reloadTimer = null
        if (intentionalClose) return

        // Check project status — sandbox may have hibernated
        http
          .get<Project>(`/projects/${projectId}`)
          .then((res) => {
            const status = res.data.sandboxStatus || 'stopped'
            if (status !== 'running') {
              setSandboxStatus(status)
            } else if (reconnectAttempts < 5) {
              // Sandbox still running — reconnect with exponential backoff
              const delay = Math.min(1000 * 2 ** reconnectAttempts, 30_000) * (0.5 + Math.random())
              reconnectAttempts++
              reconnectTimer = setTimeout(connect, delay)
            }
          })
          .catch(() => {
            // API unreachable — try reconnecting anyway
            if (reconnectAttempts < 5) {
              const delay = Math.min(1000 * 2 ** reconnectAttempts, 30_000) * (0.5 + Math.random())
              reconnectAttempts++
              reconnectTimer = setTimeout(connect, delay)
            }
          })
      }
    }

    connect()

    // Foreground revive (lib/pageResume — the one resume signal). Two cases:
    //   • the socket is CLOSED — a phone lock / long background burned through
    //     all 5 backoff attempts while the radio was down (each failed connect
    //     closes immediately). Reset the budget and reconnect at once.
    //   • the socket still reports OPEN after a real absence — a ZOMBIE. The
    //     OS tears a frozen page's socket down (and the server's ping/pong
    //     reaps it) WITHOUT the browser ever delivering `close`, so readyState
    //     lies; the old "OPEN means fine" early-return left it dead until
    //     remount — no file_change, no sandbox_status, no hibernation
    //     broadcast, and the IDE never "loaded" again until a manual refresh.
    //     Close it ourselves: the onclose path re-reads the durable sandbox
    //     status (exactly the catch-up a return needs) and reconnects, and the
    //     reconnect's onopen reloads the tree. Quick tab switches stay below
    //     the gate so a healthy socket is never bounced for nothing.
    const offResume = onPageResume(({ hiddenForMs }) => {
      if (intentionalClose) return
      const state = currentWs?.readyState
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        if (hiddenForMs < WS_BOUNCE_AFTER_HIDDEN_MS) return
        reconnectAttempts = 0
        currentWs?.close()
        return
      }
      if (reconnectTimer) clearTimeout(reconnectTimer)
      reconnectAttempts = 0
      connect()
    })

    // Slow visible-tab reconcile — the "belt" to the explicit-broadcast
    // "suspenders". Even with the reconnect catch-up above, a `file_change` can
    // still be missed (a future FS-mutating endpoint that forgets to broadcast,
    // an event lost in a sub-reconnect-window blip). A periodic reload while the
    // tab is open + visible converges the tree on its own. Gated on visibility
    // so a backgrounded tab adds no idle traffic; reloadFileTree merges via
    // mergeFileTree, so an already-current tree is a no-op for React. This
    // effect only runs while the sandbox is running, so the poll is too.
    const reconcileTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        reloadFileTree()
      }
    }, FILE_TREE_RECONCILE_INTERVAL_MS)

    return () => {
      intentionalClose = true
      offResume()
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (reloadTimer) clearTimeout(reloadTimer)
      clearInterval(reconcileTimer)
      if (currentWs) currentWs.close()
    }
  }, [projectId, sandboxStatus, http, reloadFileTree, setSandboxStatus, wsAuthReady])

  // Handler the ChatPanel registers (via onRegisterPushHandler) so the presence
  // socket below can deliver a teammate's broadcast chat event into the open chat.
  // Typed with the package's ChatStreamEvent so the forward is type-safe end to end.
  const chatPushHandlerRef = useRef<
    ((conversationId: string, event: ChatStreamEvent) => void) | null
  >(null)

  // History reconcile the ChatPanel registers (via onRegisterHistoryReconcile).
  // Called on every presence-socket REconnect: a chat broadcast sent while the
  // socket was down (a teammate's /teamsay note against a backgrounded tab or a
  // slept laptop) is delivered by nothing else — the persisted copy only shows
  // on a page-lifecycle reconcile that may never fire. Cheap when nothing
  // changed; the panel skips an identical transcript.
  const chatReconcileRef = useRef<(() => Promise<void>) | null>(null)

  // Latest handleClientAction for the presence-socket closure below — it is declared
  // later in this component and re-created on dep changes, so the socket reads it
  // through a ref (same pattern as chatPushHandlerRef). Carries the server's
  // broadcast preview_ui client_actions (see the onmessage routing).
  const handleClientActionRef = useRef<((action: { action: string }) => void) | null>(null)

  // Presence WebSocket — the CLIENT half of the chat push channel. The server
  // broadcasts each build-activity chat frame to all project members over this
  // socket; here we receive it and hand it to the ChatPanel to render live for the
  // OTHER members (chat is server→client only — we never send on this socket).
  // Mirrors the file-watch socket's token/host/port + reconnect-with-backoff
  // structure, but it is NOT gated on the sandbox running: chat events flow during
  // discovery and while the sandbox sleeps, so presence stays connected as long as
  // the project is open.
  useEffect(() => {
    if (isPublicWorkspace || !projectId || projectId === 'new') return
    if (!wsAuthReady) return // wait for the guest WS ticket so the first connect is authed

    let intentionalClose = false
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let reconnectAttempts = 0
    let currentWs: WebSocket | null = null
    let hadConnected = false

    // Ticket funnel on every (re)connect — see the file-watch socket above.
    let connectPending = false
    function connect() {
      if (connectPending) return
      connectPending = true
      void ensureWsAuthToken(http).then((token) => {
        connectPending = false
        if (intentionalClose) return
        openSocket(token)
      })
    }

    function openSocket(token: string | null) {
      const wsUrl = buildWsUrl(`/ws/projects/${projectId}/presence`, token)

      const ws = new WebSocket(wsUrl)
      currentWs = ws

      let opened = false
      ws.onopen = () => {
        opened = true
        reconnectAttempts = 0
        // A REconnect means broadcasts may have been missed while the socket
        // was down — converge the chat on the persisted transcript. (The first
        // connect is skipped: the ChatPanel is loading history at mount anyway.)
        // The project row is re-read too: a `resource_change` deleted frame
        // missed while down would otherwise leave this tab on a dead project
        // (reloadProject leaves for the dashboard on 403/404).
        if (hadConnected) {
          void chatReconcileRef.current?.()
          void reloadProject()
        }
        hadConnected = true
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string) as {
            type?: string
            event?: string | ChatStreamEvent
            conversationId?: string
            originUserId?: string
            status?: string
            reason?: string
            resource?: string
            action?: string
            path?: string
          }
          // Project-scoped state changed elsewhere (another client, or the
          // server autonomously) — refetch what this browser renders. See
          // handleResourceChange for the per-resource reactions.
          if (msg.type === 'resource_change' && typeof msg.resource === 'string') {
            handleResourceChangeRef.current?.(msg.resource, msg.action, msg.conversationId)
            return
          }
          // Presence-channel copy of a file-change event (cross-instance, and
          // the file-watch socket only exists while the sandbox is running on
          // this browser's view). reloadFileTree throttles + self-guards, so
          // the same-instance double delivery coalesces harmlessly.
          if (msg.event === 'file_change') {
            reloadFileTree()
            return
          }
          // Sandbox lifecycle transition (waking / running / sleeping / stopped).
          // This is the channel that reaches a browser showing a SLEEPING sandbox
          // — the file-watch WS (which carries the same event) only connects
          // while the sandbox is running, so without this branch a wake in one
          // browser never reached the others until refresh. Same handling as the
          // file-watch copy: a completed wake stamps wake patience so the
          // preview panel tolerates the restarting app.
          if (msg.type === 'sandbox_status' && typeof msg.status === 'string') {
            if (msg.status === 'running' && msg.reason === 'woke') {
              setSandboxWakeAt(Date.now())
              importInstallPendingUntilRef.current = 0
              clearInstallReadyPoll()
            }
            if (msg.status === 'running') {
              // A boot/wake elsewhere may have minted (or changed) previewUrl —
              // refetch the row so the preview-URL sync effect can point the
              // bond at it (a tab that mounted while the sandbox slept has none).
              void reloadProject()
            }
            // The revert of the very attempt the IDE is waiting to retry (R122-H1):
            // the server broadcasts it BEFORE the refusal reaches the stream (our
            // request is still in flight) and applying it would cancel the retry and
            // show the Start screen. The stream error settles the state itself.
            if (
              (sandboxLoadingRef.current || startAutoRetryTimerRef.current) &&
              msg.reason === 'boot_failed'
            ) {
              lastSettledStatusRef.current = msg.status
              return
            }
            setSandboxStatus(msg.status)
            return
          }
          // Only chat broadcasts concern us — presence/cursor/lock frames are not our
          // business here, so they're ignored (never thrown on). Apply EVERY chat broadcast
          // for the open conversation, INCLUDING this user's own actions: a second tab of the
          // same user must see them live too, so we must NOT suppress by user id. The
          // originating tab's own echo is de-duped downstream (server cards by their unique
          // timestamp, activities by id, mode/model by their last-shown refs), so re-applying
          // can't double-add. (`originUserId` rides along for future per-tab use.)
          if (msg.type === 'chat' && msg.conversationId && typeof msg.event === 'object') {
            // preview_ui client_actions are ALSO broadcast on this socket so they survive a
            // mid-build tab reload: the chat SSE dies with the old page, so an SSE-only
            // client_action goes nowhere and the executor's read_preview_ui/interact_preview
            // blocks until timeout (observed live — a reload 12s into a build killed every
            // preview read for the whole build). Route them to the same handler as the SSE
            // path; PreviewPanel dedups by command id, so a tab that already received the
            // SSE copy executes the command once.
            const ev = msg.event as { type?: string; action?: string; card?: { by?: string } }
            if (ev.type === 'client_action' && typeof ev.action === 'string') {
              handleClientActionRef.current?.(ev as { action: string })
            } else if (
              ev.type === 'card' &&
              typeof ev.card?.by === 'string' &&
              msg.originUserId &&
              msg.originUserId === user?.id
            ) {
              // An attributed out-of-turn SETTING card (model/effort/fast/mode/
              // max-loops/region change) echoing back to the member who made it —
              // their own client already showed its local confirmation card, so
              // skip the echo; the persisted copy appears on reload. In-turn
              // shared cards carry no `by` and always apply (id-deduped).
            } else {
              chatPushHandlerRef.current?.(msg.conversationId, msg.event)
            }
          }
        } catch (_error) {
          // ignore malformed WebSocket messages — JSON.parse throws on non-JSON frames
        }
      }

      ws.onerror = () => ws.close()

      ws.onclose = (event?: CloseEvent) => {
        if (intentionalClose) return
        // Access may have ended: the server closes this socket with 4003 when the
        // member is removed (Collaboration/index.ts closeLocalProjectSocketsForUser),
        // and a later reconnect is refused at the upgrade (server.ts rejectUpgrade:
        // 'not-a-member' / 'project-not-found'), which the browser sees as a close
        // without an open. Re-read the project row in both cases: reloadProject
        // leaves for the dashboard on 403/404 and is a no-op refresh otherwise
        // (a plain network drop also closes before open; the GET then fails
        // without a status and the view stays put).
        if (event?.code === 4003 || !opened) {
          void reloadProject()
        }
        // Reconnect with exponential backoff while the project stays open. Unlike the
        // file-watch socket there is no sandbox-status gate — presence is independent
        // of the sandbox lifecycle.
        if (reconnectAttempts < 5) {
          const delay = Math.min(1000 * 2 ** reconnectAttempts, 30_000) * (0.5 + Math.random())
          reconnectAttempts++
          reconnectTimer = setTimeout(connect, delay)
        }
      }
    }

    connect()

    // Foreground revive — same two cases as the file-watch socket above (closed
    // after exhausted backoff, or a zombie that still reports OPEN). This socket
    // is the chat push channel, so a dead one silently drops every live build
    // frame until remount; the bounce's reconnect runs the chat reconcile.
    const offResume = onPageResume(({ hiddenForMs }) => {
      if (intentionalClose) return
      const state = currentWs?.readyState
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        if (hiddenForMs < WS_BOUNCE_AFTER_HIDDEN_MS) return
        reconnectAttempts = 0
        currentWs?.close()
        return
      }
      if (reconnectTimer) clearTimeout(reconnectTimer)
      reconnectAttempts = 0
      connect()
    })

    return () => {
      intentionalClose = true
      offResume()
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (currentWs) currentWs.close()
    }
  }, [
    projectId,
    http,
    user?.id,
    wsAuthReady,
    setSandboxStatus,
    clearInstallReadyPoll,
    reloadFileTree,
    reloadProject,
  ])

  // Re-annotate file tree when git statuses change (including clearing after commit)
  useEffect(() => {
    setFiles((prev) => annotateGitStatus(prev, fileStatuses))
  }, [fileStatuses])

  const handleDirExpand = useCallback(
    async (dirPath: string) => {
      const children = await loadFiles(dirPath)
      // A failed listing keeps the folder as it was; showing it as empty would
      // claim the user's directory has nothing in it.
      if (!children) {
        reportFileOpFailure(
          t('workspace.fileError.list', undefined, {
            defaultValue: 'Could not list that folder.',
          }),
          null,
        )
        return
      }
      // Annotate newly loaded children with git status before injecting
      const annotated =
        Object.keys(fileStatuses).length > 0 ? annotateGitStatus(children, fileStatuses) : children
      setFiles((prev) => injectChildrenInTree(prev, dirPath, annotated))
    },
    [loadFiles, fileStatuses, reportFileOpFailure, t],
  )

  /** Fetch a file's content from the sandbox and open it in the editor. */
  const openFileFromPath = useCallback(
    async (filePath: string) => {
      if (!projectId || sandboxStatusRef.current !== 'running') return
      const apiPath = filePath.replace(/^\/workspace\//, '')
      try {
        const res = await http.get<{ path: string; content: string }>(
          `/projects/${projectId}/files/${apiPath}`,
        )
        openFile({
          path: filePath,
          content: res.data.content,
          language: langFromPath(filePath),
        })
      } catch (_error) {
        // File may have been deleted — skip silently; caller doesn't need the content
      }
    },
    [projectId, http, openFile],
  )
  const publicFileOpenedRef = useRef(false)
  useEffect(() => {
    if (
      !publicWorkspace?.initialFile ||
      !editorReady ||
      sandboxStatus !== 'running' ||
      publicFileOpenedRef.current
    )
      return
    publicFileOpenedRef.current = true
    void openFileFromPath(`/workspace/${publicWorkspace.initialFile}`)
  }, [publicWorkspace?.initialFile, editorReady, sandboxStatus, openFileFromPath])

  const handleFileSelect = useCallback(
    async (rawPath: string, opts?: { focus?: boolean }) => {
      // A user-initiated open reveals the editor. System-initiated opens (a saved
      // plan, an AI open_file while the user is busy) pass focus:false to add the
      // tab quietly — the file is available without yanking the user off the preview.
      if (opts?.focus !== false) {
        setMainView('editor')
        // Phones: a user-initiated open must also reveal the Code SURFACE —
        // otherwise a tap on a chat file row/diff chip changes only the hidden
        // pane's state and appears to do nothing. System-initiated quiet opens
        // pass focus:false and stay quiet.
        setMobilePane('main')
      }
      // Normalize: tabs always use /workspace/ prefix
      const filePath = rawPath.startsWith('/workspace/') ? rawPath : `/workspace/${rawPath}`
      // If already open as a permanent tab, just switch to it
      const existingTab = provider.getTabs().find((t) => t.path === filePath)
      if (existingTab && !existingTab.isPreview) {
        setActiveTab(filePath)
        setActiveFile(filePath)
        return
      }
      // Open as preview tab (replaces existing preview)
      if (!projectId || sandboxStatusRef.current !== 'running') return
      const apiPath = filePath.replace(/^\/workspace\//, '')
      try {
        const res = await http.get<{ path: string; content: string }>(
          `/projects/${projectId}/files/${apiPath}`,
        )
        openFile({
          path: filePath,
          content: res.data.content,
          language: langFromPath(filePath),
          isPreview: true,
        })
      } catch (_error) {
        // File may have been deleted — skip silently; tab remains closed
      }
      setActiveFile(filePath)
    },
    [projectId, http, openFile, setActiveTab, provider],
  )

  // Track the user's last direct interaction with the IDE so AI-driven view changes
  // (below) never yank focus while the user is actively clicking/typing — most
  // importantly mid-test in the preview. A pointerdown ON the preview iframe is seen
  // here (the event targets the iframe element) before focus moves inside it.
  const lastInteractionRef = useRef(0)
  useEffect(() => {
    const mark = (): void => {
      lastInteractionRef.current = Date.now()
    }
    window.addEventListener('pointerdown', mark, true)
    window.addEventListener('keydown', mark, true)
    return () => {
      window.removeEventListener('pointerdown', mark, true)
      window.removeEventListener('keydown', mark, true)
    }
  }, [])

  // AI-driven live preview control: the executor's read_preview_ui / interact_preview tools emit
  // a `preview_ui` client_action; we relay it to PreviewPanel (uiCommand) and POST the iframe's
  // reply back so the BLOCKED tool call resolves (see api preview-ui-bridge + the ui-result
  // endpoint). Lets Synthase drive + verify the app in the live preview the user is watching.
  const [previewUiCommand, setPreviewUiCommand] = useState<PreviewUiCommand | null>(null)
  const handlePreviewUiResult = useCallback(
    (id: string, result: PreviewUiResult | { verdict: PreviewRenderState; url?: string }) => {
      // ui-result is editor+ — a viewer tab racing the editor's tab can only
      // 403, so don't answer the broadcast from viewer tabs at all.
      if (!canEditProjectRef.current) return
      // Loading is an observation, not a verdict. handleRenderState answers the pending request
      // once a decided state arrives; every other command/transport also shares this one-shot gate.
      if ('verdict' in result && result.verdict === 'loading') return
      if (!rememberPreviewRequest(answeredPreviewRequestsRef.current, id)) return
      pendingRenderRequestsRef.current.delete(id)
      const token = http.getAuthToken()
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`
      void fetch(`${API_BASE_URL}/projects/${projectId}/preview/ui-result`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ requestId: id, result }),
      }).catch(() => {
        // Best-effort: if the POST fails, the tool call times out cleanly server-side.
      })
    },
    [projectId],
  )
  previewUiResultHandlerRef.current = handlePreviewUiResult

  // The AI agent (via the `client_action` chat-stream event) asks the IDE to
  // reload/navigate the live preview or open a file — the human-equivalent of
  // clicking refresh, typing a path, or opening a file in the explorer. We perform
  // these only when the user ISN'T busy: if they interacted in the last few seconds
  // or are focused inside the preview iframe, we don't steal their view — preview
  // nav/reload is skipped and files open quietly (no pane switch). The agent gets a
  // success result regardless; these are conveniences, not critical operations.
  // The app's base path, announced by the previewed page itself (`molecule:base`,
  // posted by the e2e preview client from the app's resolved Vite `base`). An
  // app served under `/blog/` has every URL under it; a navigation that lands
  // outside it reaches the dev server's "did you mean /blog/…" page, which has
  // no app and nothing to drive (X0 R62). Prepending here needs no env var and
  // no settings panel — the page is the source of truth.
  const appBaseRef = useRef<string>('/')
  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      if (event.data?.type !== 'molecule:base' || typeof event.data.base !== 'string') return
      try {
        if (event.origin !== new URL(previewState.url).origin) return
      } catch (_error) {
        return // no preview URL yet — nothing to match the announcement against
      }
      // Only a plain absolute path: the tenant's page chooses this, and it is prepended to the
      // preview origin when the IDE navigates.
      const base = sanitizeAppBase(event.data.base)
      if (base) appBaseRef.current = base
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [previewState.url])
  /** `/about/` → `/blog/about/` when the app announced a base it lacks. */
  const underAppBase = useCallback((path: string): string => {
    const base = appBaseRef.current
    if (base === '/' || path === base || path.startsWith(base) || path + '/' === base) return path
    return base.replace(/\/$/, '') + path
  }, [])

  const handleClientAction = useCallback(
    (action: {
      action: string
      path?: string
      requestId?: string
      command?: string
      molId?: string
      selector?: string
      text?: string
      value?: string
    }) => {
      const RECENT_MS = 12_000
      const userBusy =
        Date.now() - lastInteractionRef.current < RECENT_MS ||
        (typeof document !== 'undefined' && document.activeElement?.tagName === 'IFRAME')
      switch (action.action) {
        case 'preview_ui': {
          // AI-driven verification — ALWAYS execute (unlike open_file/reload conveniences): the
          // executor's read_preview_ui/interact_preview/navigate_preview tool BLOCKS on the
          // reply, so skipping would time it out. Show the preview and relay the command to
          // PreviewPanel.
          if (!action.requestId || !action.command) break
          if (!rememberPreviewRequest(handledPreviewRequestsRef.current, action.requestId)) break
          setMainView('preview')
          if (action.command === 'confirm_render') {
            const current = previewRenderRef.current
            if (current.state === 'loading') {
              rememberPreviewRequest(pendingRenderRequestsRef.current, action.requestId)
              break
            }
            handlePreviewUiResult(action.requestId, {
              verdict: current.state,
              ...(current.url ? { url: current.url } : {}),
            })
            break
          }
          if (action.command === 'navigate') {
            // Agent navigation, round-trip flavor: set the iframe URL ourselves, then enqueue a
            // snapshot with the SAME requestId. PreviewPanel re-posts the snapshot command until
            // the (newly loading) page's bridge answers, so the reply is the LANDED page's
            // snapshot — the tool learns where the app actually went. Never gated on userBusy:
            // the old fire-and-forget navigate was silently dropped while the user watched,
            // which read as a "stuck preview cache" to the executor and derailed whole builds.
            // `minLoadedAt` is set ONLY when a new document is genuinely going to load —
            // i.e. the URL really changed and setUrl didn't throw. Nothing can satisfy it
            // otherwise (re-pointing at the current URL doesn't reload the iframe), and an
            // unsatisfiable stamp means NO bridge answers and the tool times out.
            let navMinLoadedAt: number | undefined
            if (action.path) {
              const clean = action.path.startsWith('/') ? action.path : `/${action.path}`
              try {
                const target = new URL(previewState.url).origin + clean
                if (target !== previewState.url) navMinLoadedAt = Date.now()
                setUrl(target)
              } catch (_error) {
                // No valid preview base URL yet — fall through: the snapshot below still
                // answers with whatever screen is current, and its url tells the agent.
              }
            }
            // Enqueue the snapshot a beat AFTER the src change lands. With minLoadedAt, only
            // the document this navigation loads may answer — so the OUTGOING page can't
            // reply with a pre-navigation snapshot, and the incoming one waits until it has
            // actually rendered. PreviewPanel re-posts for ~40s, covering a cold route compile.
            // In a hidden tab, enqueue synchronously instead: background timers are throttled
            // (up to one tick per MINUTE under intensive throttling), so the 600ms beat could
            // fire after the tool's 45s wait — minLoadedAt already keeps the outgoing page
            // from answering, so posting early is safe.
            const navRequestId = action.requestId
            const enqueueNavSnapshot = (): void => {
              setPreviewUiCommand({
                id: navRequestId,
                action: 'snapshot',
                minLoadedAt: navMinLoadedAt,
              })
            }
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
              enqueueNavSnapshot()
            } else {
              window.setTimeout(enqueueNavSnapshot, 600)
            }
            break
          }
          setPreviewUiCommand({
            id: action.requestId,
            action: action.command as PreviewUiCommand['action'],
            molId: action.molId,
            selector: action.selector,
            text: action.text,
            value: action.value,
          })
          break
        }
        case 'reload_preview': {
          if (userBusy) break
          // Honor an agent reload ONLY when the preview recently looked broken
          // (runtime error / stuck). Vite HMR applies every edit automatically
          // and full-reloads itself when a change can't hot-apply, so reloading
          // a healthy preview is always redundant — it wipes the app state the
          // user is looking at and causes the "theme updated, then the whole
          // preview reloaded anyway" flash. Weak executors call this after
          // routine edits despite the tool description saying not to.
          const UNHEALTHY_WINDOW_MS = 60_000
          const previewUnhealthy = Date.now() - previewUnhealthyAtRef.current < UNHEALTHY_WINDOW_MS
          if (!previewUnhealthy) break
          setMainView('preview')
          refreshPreview()
          break
        }
        case 'navigate_preview': {
          if (userBusy || !action.path) break
          setMainView('preview')
          const clean = action.path.startsWith('/') ? action.path : `/${action.path}`
          try {
            setUrl(new URL(previewState.url).origin + underAppBase(clean))
          } catch (_error) {
            // No valid base URL yet — URL constructor throws; ignore this navigate request
          }
          break
        }
        case 'open_file':
          // Always make the tab available; only steal the editor view when idle.
          if (action.path) void handleFileSelect(action.path, { focus: !userBusy })
          break
      }
    },
    [
      refreshPreview,
      setUrl,
      previewState.url,
      handleFileSelect,
      underAppBase,
      handlePreviewUiResult,
    ],
  )

  // Keep the presence-socket's ref pointed at the LATEST handleClientAction (the socket
  // closure outlives this callback's re-creations — see handleClientActionRef above).
  useEffect(() => {
    handleClientActionRef.current = handleClientAction
  }, [handleClientAction])

  // A USER clicked a route link in a chat message (e.g. the agent's "your app is ready"
  // handoff) — navigate the preview to that page. UNGATED, unlike the agent's navigate_preview
  // action: this is a direct user request, so always honor it (switch to the preview pane and
  // load the route). Falls back gracefully if the preview has no base URL yet.
  const handleNavigatePreviewFromChat = useCallback(
    (path: string) => {
      setMainView('preview')
      const clean = path.startsWith('/') ? path : `/${path}`
      try {
        setUrl(new URL(previewState.url).origin + underAppBase(clean))
      } catch (_error) {
        // No valid preview base URL yet — ignore this navigate request.
      }
    },
    [setUrl, previewState.url, underAppBase],
  )

  const handleFileDoubleClick = useCallback(
    async (rawPath: string) => {
      setMainView('editor')
      setMobilePane('main') // reveal the Code surface on phones
      // Normalize: tabs always use /workspace/ prefix
      const filePath = rawPath.startsWith('/workspace/') ? rawPath : `/workspace/${rawPath}`
      // If already open (preview or permanent), pin it
      const existingTab = provider.getTabs().find((t) => t.path === filePath)
      if (existingTab) {
        if (existingTab.isPreview) pinTab(filePath)
        setActiveTab(filePath)
        setActiveFile(filePath)
        return
      }
      // Not yet open — open as permanent
      await openFileFromPath(filePath)
      setActiveFile(filePath)
    },
    [provider, pinTab, setActiveTab, openFileFromPath],
  )

  /** Double-clicking a diff tab closes the diff and opens the actual file. */
  const handleTabDoubleClick = useCallback(
    (path: string): boolean => {
      const tab = provider.getTabs().find((t) => t.path === path)
      if (!tab?.isDiff) return false
      // openFileFromPath fetches the file content and calls openFile,
      // which calls closeDiff() internally before opening the regular tab.
      openFileFromPath(path)
      return true
    },
    [provider, openFileFromPath],
  )

  // ---------------------------------------------------------------------------
  // Context menu handlers
  // ---------------------------------------------------------------------------

  const handleRename = useCallback(
    async (filePath: string) => {
      if (!projectId) return
      const oldName = filePath.split('/').pop() ?? ''
      const newName = await promptDialog({
        message: t('workspace.contextMenu.renamePrompt', undefined, { defaultValue: 'New name:' }),
        defaultValue: oldName,
        molId: 'file-rename-dialog',
      })
      if (!newName || newName === oldName) return
      const parentDir = filePath.slice(0, filePath.lastIndexOf('/'))
      const newPath = `${parentDir}/${newName}`
      try {
        await http.post(`/projects/${projectId}/files/rename`, { oldPath: filePath, newPath })
        // Update the editor tab if the renamed file was open
        const tab = provider.getTabs().find((t) => t.path === filePath)
        if (tab) {
          const wasActive = activeFile === filePath
          // Read content from the new path (file already moved on disk)
          const apiPath = newPath.replace(/^\/workspace\//, '')
          const res = await http.get<{ content: string }>(`/projects/${projectId}/files/${apiPath}`)
          provider.closeFile(filePath)
          openFile({
            path: newPath,
            content: res.data.content,
            language: langFromPath(newPath),
            isPreview: false,
          })
          if (wasActive) setActiveFile(newPath)
        }
        await reloadFileTree()
        setGitStatusTick((n) => n + 1)
      } catch (error) {
        console.error('[Workspace] rename failed', error)
        reportFileOpFailure(
          t('workspace.fileError.rename', undefined, { defaultValue: 'Could not rename it.' }),
          error,
        )
      }
    },
    [projectId, http, reloadFileTree, t, provider, openFile, activeFile, reportFileOpFailure],
  )

  const handleDeleteFile = useCallback(
    async (filePath: string) => {
      if (!projectId) return
      const name = filePath.split('/').pop() ?? filePath
      if (
        !(await confirmDialog({
          message: t(
            'workspace.contextMenu.deleteConfirm',
            { name },
            { defaultValue: `Delete "${name}"?` },
          ),
          danger: true,
          molId: 'file-delete-dialog',
        }))
      )
        return
      const apiPath = filePath.replace(/^\/workspace\//, '')
      try {
        await http.delete(`/projects/${projectId}/files/${apiPath}`)
        // Mark as deleted in fileStatuses so the tab shows strikethrough,
        // even for untracked files that git won't report as 'deleted'
        setFileStatuses((prev) => ({ ...prev, [filePath]: 'deleted' }))
        await reloadFileTree()
        setGitStatusTick((n) => n + 1)
      } catch (error) {
        console.error('[Workspace] delete file failed', error)
        reportFileOpFailure(
          t('workspace.fileError.delete', undefined, { defaultValue: 'Could not delete it.' }),
          error,
        )
      }
    },
    [projectId, http, reloadFileTree, t, reportFileOpFailure],
  )

  const handleDeleteMultiple = useCallback(
    async (paths: string[]) => {
      if (!projectId || paths.length === 0) return
      const count = paths.length
      if (
        !(await confirmDialog({
          message: t(
            'workspace.contextMenu.deleteMultipleConfirm',
            { count },
            { defaultValue: `Delete ${count} items?` },
          ),
          danger: true,
          molId: 'file-delete-multiple-dialog',
        }))
      )
        return
      for (const filePath of paths) {
        const apiPath = filePath.replace(/^\/workspace\//, '')
        try {
          await http.delete(`/projects/${projectId}/files/${apiPath}`)
          setFileStatuses((prev) => ({ ...prev, [filePath]: 'deleted' }))
        } catch (error) {
          console.error('[Workspace] delete item failed, continuing with rest', error)
          reportFileOpFailure(
            t('workspace.fileError.delete', undefined, { defaultValue: 'Could not delete it.' }),
            error,
          )
        }
      }
      await reloadFileTree()
      setGitStatusTick((n) => n + 1)
    },
    [projectId, http, reloadFileTree, t, reportFileOpFailure],
  )

  const handleMoveFiles = useCallback(
    async (moves: Array<{ oldPath: string; newPath: string }>) => {
      if (!projectId || moves.length === 0) return
      for (const { oldPath, newPath } of moves) {
        try {
          await http.post(`/projects/${projectId}/files/rename`, { oldPath, newPath })
          // Update editor tab if the moved file was open
          const tab = provider.getTabs().find((t) => t.path === oldPath)
          if (tab) {
            const wasActive = activeFile === oldPath
            const apiPath = newPath.replace(/^\/workspace\//, '')
            const res = await http.get<{ content: string }>(
              `/projects/${projectId}/files/${apiPath}`,
            )
            provider.closeFile(oldPath)
            openFile({
              path: newPath,
              content: res.data.content,
              language: langFromPath(newPath),
              isPreview: false,
            })
            if (wasActive) setActiveFile(newPath)
          }
        } catch (error) {
          console.error('[Workspace] move file failed, continuing with rest', error)
          reportFileOpFailure(
            t('workspace.fileError.move', undefined, { defaultValue: 'Could not move it.' }),
            error,
          )
        }
      }
      await reloadFileTree()
      setGitStatusTick((n) => n + 1)
    },
    [projectId, http, reloadFileTree, provider, openFile, activeFile, t, reportFileOpFailure],
  )

  const handleNewFile = useCallback(
    async (dirPath: string) => {
      if (!projectId) return
      const name = await promptDialog({
        message: t('workspace.contextMenu.newFilePrompt', undefined, {
          defaultValue: 'File name:',
        }),
        molId: 'file-new-dialog',
      })
      if (!name) return
      const fullPath = `${dirPath}/${name}`
      const apiPath = fullPath.replace(/^\/workspace\//, '')
      try {
        await http.put(`/projects/${projectId}/files/${apiPath}`, { content: '' })
        await reloadFileTree()
        setGitStatusTick((n) => n + 1)
        await handleFileSelect(fullPath)
      } catch (error) {
        console.error('[Workspace] new file failed', error)
        reportFileOpFailure(
          t('workspace.fileError.newFile', undefined, {
            defaultValue: 'Could not create the file.',
          }),
          error,
        )
      }
    },
    [projectId, http, reloadFileTree, handleFileSelect, t, reportFileOpFailure],
  )

  const handleNewFolder = useCallback(
    async (dirPath: string) => {
      if (!projectId) return
      const name = await promptDialog({
        message: t('workspace.contextMenu.newFolderPrompt', undefined, {
          defaultValue: 'Folder name:',
        }),
        molId: 'folder-new-dialog',
      })
      if (!name) return
      const fullPath = `${dirPath}/${name}`
      try {
        await http.post(`/projects/${projectId}/files/mkdir`, { path: fullPath })
        await reloadFileTree()
        setGitStatusTick((n) => n + 1)
      } catch (error) {
        console.error('[Workspace] new folder failed', error)
        reportFileOpFailure(
          t('workspace.fileError.newFolder', undefined, {
            defaultValue: 'Could not create the folder.',
          }),
          error,
        )
      }
    },
    [projectId, http, reloadFileTree, t, reportFileOpFailure],
  )

  /** Open a diff view. Uses pre-computed snapshot if available, otherwise fetches live git diff. */
  const handleFileDiff = useCallback(
    async (rawPath: string, diff?: { original: string; modified: string }) => {
      if (!projectId) return
      setMainView('editor')
      setMobilePane('main') // reveal the Code surface on phones (diff taps)
      // Normalize: strip leading /workspace/ for API calls, but keep prefixed version for tabs
      const apiPath = rawPath.replace(/^\/workspace\//, '')
      const filePath = rawPath.startsWith('/workspace/') ? rawPath : `/workspace/${rawPath}`

      // Pre-computed diff from the tool call snapshot (survives commits)
      if (diff) {
        provider.openDiff?.({
          path: filePath,
          originalContent: diff.original,
          modifiedContent: diff.modified,
          language: langFromPath(filePath),
        })
        return
      }

      // Fallback: live git diff (works for uncommitted file list)
      try {
        const res = await http.get<{ path: string; original: string; modified: string }>(
          `/projects/${projectId}/git-diff`,
          { params: { path: apiPath } },
        )
        const orig = res.data.original ?? ''
        const mod = res.data.modified ?? ''
        // If identical (already committed) or both empty, just open the file
        if (orig === mod || (!orig && !mod)) {
          await openFileFromPath(rawPath)
        } else {
          provider.openDiff?.({
            path: filePath,
            originalContent: orig,
            modifiedContent: mod,
            language: langFromPath(filePath),
          })
        }
      } catch (_error) {
        // Git diff fetch failed — fall back to opening the file normally
        await openFileFromPath(rawPath)
      }
    },
    [projectId, http, provider, openFileFromPath],
  )

  /** Update the editor if a file is currently open — used after external writes. */
  const refreshEditorFile = useCallback(
    (rawPath: string, content: string) => {
      // Tabs store paths with /workspace/ prefix (from file explorer)
      const filePath = rawPath.startsWith('/workspace/') ? rawPath : `/workspace/${rawPath}`
      provider.setContentSilent?.(filePath, content)
    },
    [provider],
  )

  const handleFileDeleted = useCallback(
    (path: string) => {
      const fullPath = path.startsWith('/workspace/') ? path : `/workspace/${path}`
      setFileStatuses((prev) => ({ ...prev, [fullPath]: 'deleted' }))
      reloadFileTree()
    },
    [reloadFileTree],
  )

  /** Called when the AI creates or modifies a file during streaming.
   *  Only updates the editor content — the file tree reload is handled by the
   *  FileWatch WebSocket to avoid duplicate reloads and flicker. */
  const handleFileChange = useCallback(
    (rawPath: string, content: string) => {
      // The AI has started building — enable preview-error monitoring from here on.
      aiWroteFilesRef.current = true
      // Cancel pending preview error send — AI writing files likely fixes the error
      if (previewErrorTimerRef.current) {
        clearTimeout(previewErrorTimerRef.current)
        previewErrorTimerRef.current = null
      }
      refreshEditorFile(rawPath, content)
      // Signal PreviewPanel that a file changed. Healthy previews are untouched
      // (Vite HMR applies the edit; Vite full-reloads itself when needed) — the
      // tick only drives the recovery reload while the preview is BROKEN.
      setFileChangeTick((n) => n + 1)
      // Surface the current edit in the BROKEN-preview overlay ("Updating `X`…"
      // instead of a blank wall while fixes land). No effect on a healthy
      // preview. Clear a few seconds after edits stop.
      const base =
        rawPath
          .replace(/^\/workspace\//, '')
          .split('/')
          .filter(Boolean)
          .pop() ?? rawPath
      setBuildingHint(base)
      if (buildingHintTimerRef.current) clearTimeout(buildingHintTimerRef.current)
      buildingHintTimerRef.current = setTimeout(() => setBuildingHint(null), 3000)
    },
    [refreshEditorFile],
  )

  /** Undo/redo a file change by writing content back to the file. */
  const handleFileRevert = useCallback(
    async (rawPath: string, content: string) => {
      if (!projectId || sandboxStatusRef.current !== 'running') return
      const filePath = rawPath.replace(/^\/workspace\//, '')
      await http.put(`/projects/${projectId}/files/${filePath}`, { content })
      refreshEditorFile(rawPath, content)
    },
    [projectId, http, refreshEditorFile],
  )

  // ---------------------------------------------------------------------------
  // Tab persistence
  // ---------------------------------------------------------------------------

  const tabStorageKey = projectId && projectId !== 'new' ? `mol_editor_tabs_${projectId}` : null
  // Restore reads the SAME preference the save effect writes. Subscribing (via
  // the store) instead of reading once means an asynchronous provider's
  // hydration reaches the restore path instead of skipping it for good.
  const { value: storedTabs, ready: storedTabsReady } = useStoragePreference<{
    paths?: string[]
    active?: string | null
  }>(tabStorageKey)

  // Reset editor restore state when the sandbox leaves 'running' (sleep/stop)
  // so tabs are re-opened from storage the next time it wakes.
  useEffect(() => {
    const isRunning = sandboxStatus === 'running'
    if (prevRunningRef.current && !isRunning) {
      setEditorReady(false)
      restoredRef.current = false
      if (tabStorageKey) {
        // Absent or corrupt reads as null — restoring state is just skipped.
        const saved = getPreferenceStore().get<{ paths?: string[] }>(tabStorageKey)
        if (saved?.paths?.length) setRestoring(true)
      }
    }
    prevRunningRef.current = isRunning
  }, [sandboxStatus, tabStorageKey])

  // Save open tabs whenever they change (after initial restore).
  // Use Workspace's own activeFile (not tabs.isActive) — it's always correct because
  // EditorPanel's handleTabSelect calls onActiveFileChange directly on every tab click.
  useEffect(() => {
    if (!tabStorageKey || !restoredRef.current) return
    getPreferenceStore().set(tabStorageKey, { paths: openFilePaths, active: activeFile })
  }, [openFilePaths, activeFile, tabStorageKey])

  // Restore tabs once the editor is mounted and the sandbox is running.
  // Every code path must call setRestoring(false) to avoid a stuck overlay.
  useEffect(() => {
    if (!editorReady || sandboxStatus !== 'running' || !tabStorageKey || !storedTabsReady) return
    if (restoredRef.current) {
      // Already restored (e.g. deps changed) — just clear the overlay
      setRestoring(false)
      return
    }
    restoredRef.current = true

    // Absent or corrupt reads as null — skip restore; tabs reopen fresh on the next edit.
    const saved = storedTabs
    if (!saved?.paths?.length) {
      setRestoring(false)
      return
    }
    // The saved tabs are known only now (an asynchronous provider hydrated
    // after the first paint): show the restore overlay from here instead.
    setRestoring(true)
    const savedPaths = saved.paths

    const restore = async () => {
      const fetched = await Promise.all(
        savedPaths.map(async (path) => {
          if (!projectId) return null
          const apiPath = path.replace(/^\/workspace\//, '')
          try {
            const res = await http.get<{ path: string; content: string }>(
              `/projects/${projectId}/files/${apiPath}`,
            )
            return { path, content: res.data.content }
          } catch (_error) {
            // Individual file no longer exists (deleted) — skip it during restore
            return null
          }
        }),
      )

      const files = fetched.filter(Boolean) as { path: string; content: string }[]
      for (const f of files) {
        openFile({ path: f.path, content: f.content, language: langFromPath(f.path) })
      }
      if (saved.active && files.some((f) => f.path === saved.active)) {
        setActiveTab(saved.active)
        setActiveFile(saved.active)
      }
    }
    restore()
      .catch(() => {})
      .finally(() => {
        setRestoring(false)
      })
  }, [
    editorReady,
    sandboxStatus,
    storedTabs,
    storedTabsReady,
    tabStorageKey,
    projectId,
    http,
    openFile,
    setActiveTab,
  ])

  // ---------------------------------------------------------------------------
  // File resolver: enables Go to Definition / Peek Definition to open files
  // that aren't already loaded in the editor.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!projectId) return
    provider.setFileResolver?.(async (filePath: string) => {
      if (sandboxStatusRef.current !== 'running') return null
      const apiPath = filePath.replace(/^\/workspace\//, '')
      try {
        const res = await http.get<{ path: string; content: string }>(
          `/projects/${projectId}/files/${apiPath}`,
        )
        return { content: res.data.content, language: langFromPath(filePath) }
      } catch (_error) {
        // File not found or sandbox unavailable — LSP skips this file gracefully
        return null
      }
    })
    return () => {
      provider.setFileResolver?.(() => Promise.resolve(null))
    }
  }, [projectId])

  // ---------------------------------------------------------------------------
  // LSP: connect to typescript-language-server running inside the sandbox
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!editorReady || sandboxStatus !== 'running' || !projectId) return
    if (!wsAuthReady) return // wait for the guest WS ticket so the first connect is authed
    // The LSP WS is editor+ (it spawns sandbox processes) — a viewer's connect
    // is 5 denied handshakes and console noise, never a session.
    if (!canEditProject) return
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let attempts = 0

    const connect = (): void => {
      if (cancelled || sandboxStatusRef.current !== 'running') return
      // Ticket funnel — the URL is built per attempt so a reconnect after the
      // 6h ticket expired carries a fresh one instead of a dead one.
      void ensureWsAuthToken(http).then((token) => {
        if (cancelled || sandboxStatusRef.current !== 'running') return
        const wsUrl = buildWsUrl(`/ws/projects/${projectId}/lsp`, token)
        provider
          .connectLsp?.(wsUrl)
          ?.then(() => {
            if (cancelled) {
              provider.disconnectLsp?.()
            } else {
              attempts = 0
            }
          })
          ?.catch((err: unknown) => {
            if (!cancelled && sandboxStatusRef.current === 'running' && attempts < 5) {
              const delay = Math.min(2000 * 2 ** attempts, 15_000)
              attempts++
              console.debug('[Workspace] LSP retry in', delay, 'ms', err)
              retryTimer = setTimeout(connect, delay)
            } else if (!cancelled && sandboxStatusRef.current === 'running') {
              // All retries exhausted — sandbox may have died without a broadcast
              // reaching us. Check actual status so the wake-up overlay appears.
              http
                .get<Project>(`/projects/${projectId}`)
                .then((res) => {
                  const status = res.data.sandboxStatus || 'stopped'
                  if (status !== 'running') setSandboxStatus(status)
                })
                .catch(() => {})
            }
          })
      })
    }

    // Reconnect when the connection drops mid-session (e.g. sandbox restart)
    provider.onLspDisconnect?.(() => {
      if (!cancelled && sandboxStatusRef.current === 'running') {
        attempts = 0
        retryTimer = setTimeout(connect, 2000)
      }
    })

    connect()

    // A page frozen long enough for the server's ping/pong to reap the socket
    // comes back with a zombie LSP client: every request hangs and the editor
    // silently loses completions/diagnostics. `disconnectLsp` marks the client
    // disconnected BEFORE closing, so it does not echo through onLspDisconnect
    // (no double reconnect); we schedule the fresh connect ourselves. The gate
    // is deliberately long — an LSP connect spawns a server in the sandbox, so
    // ordinary tab switches must never bounce it.
    const offResume = onPageResume(({ hiddenForMs }) => {
      if (cancelled || hiddenForMs < WS_SERVER_REAPED_AFTER_MS) return
      if (sandboxStatusRef.current !== 'running') return
      attempts = 0
      if (retryTimer) clearTimeout(retryTimer)
      provider.disconnectLsp?.()
      retryTimer = setTimeout(connect, 0)
    })

    return () => {
      cancelled = true
      offResume()
      if (retryTimer) clearTimeout(retryTimer)
      provider.onLspDisconnect?.(() => {}) // clear callback
      provider.disconnectLsp?.()
    }
  }, [editorReady, sandboxStatus, projectId, wsAuthReady, canEditProject])

  // ---------------------------------------------------------------------------
  // Theme-aware colors
  // ---------------------------------------------------------------------------

  // Search result click handler — opens the file, then scrolls to the line
  const handleSearchResultClick = useCallback(
    async (path: string, line: number) => {
      const fullPath = path.startsWith('/workspace/') ? path : `/workspace/${path}`
      await handleFileSelect(fullPath)
      // Small delay to let the editor load the file before setting cursor
      setTimeout(() => {
        provider.setCursorPosition({ line, column: 1 })
        provider.focus()
      }, 100)
    },
    [handleFileSelect, provider],
  )

  // Shortcut reference entries for the keyboard shortcuts panel
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent)
  const mod = isMac ? '⌘' : 'Ctrl'
  const shortcutEntries = useMemo<ShortcutEntry[]>(
    () => [
      {
        label: t('ide.command.goToFile', undefined, { defaultValue: 'Go to File' }),
        keys: `${mod}P`,
        category: t('ide.command.category.file', undefined, { defaultValue: 'File' }),
        execute: () => setShowQuickOpen(true),
      },
      {
        label: t('ide.command.searchInFiles', undefined, { defaultValue: 'Search in Files' }),
        keys: `${mod}⇧F`,
        category: t('ide.command.category.search', undefined, { defaultValue: 'Search' }),
        execute: () => revealEditorSidebar('search'),
      },
      {
        label: t('ide.command.commandPalette', undefined, { defaultValue: 'Command Palette' }),
        keys: `${mod}⇧P`,
        category: t('ide.command.category.general', undefined, { defaultValue: 'General' }),
        execute: () => setShowCommandPalette(true),
      },
      {
        label: t('ide.command.keyboardShortcuts', undefined, {
          defaultValue: 'Keyboard Shortcuts',
        }),
        keys: `${mod}/`,
        category: t('ide.command.category.general', undefined, { defaultValue: 'General' }),
        execute: () => setShowShortcuts(true),
      },
    ],
    [t, mod],
  )

  // Command palette commands
  const commands = useMemo<Command[]>(
    () => [
      {
        id: 'quickOpen',
        label: t('ide.command.goToFile', undefined, { defaultValue: 'Go to File' }),
        shortcut: `${mod}P`,
        category: t('ide.command.category.file', undefined, { defaultValue: 'File' }),
        execute: () => setShowQuickOpen(true),
      },
      {
        id: 'searchInFiles',
        label: t('ide.command.searchInFiles', undefined, { defaultValue: 'Search in Files' }),
        shortcut: `${mod}⇧F`,
        category: t('ide.command.category.search', undefined, { defaultValue: 'Search' }),
        execute: () => revealEditorSidebar('search'),
      },
      // Terminal is gated behind Settings → Advanced (same as the toolbar button).
      ...(advancedMode && canEditProject
        ? [
            {
              id: 'toggleTerminal',
              label: t('ide.command.toggleTerminal', undefined, {
                defaultValue: 'Toggle Terminal',
              }),
              category: t('ide.command.category.view', undefined, { defaultValue: 'View' }),
              execute: () => toggleTerminal(),
            },
          ]
        : []),
      {
        id: 'toggleExplorer',
        label: t('ide.command.toggleExplorer', undefined, { defaultValue: 'Toggle Explorer' }),
        category: t('ide.command.category.view', undefined, { defaultValue: 'View' }),
        execute: () => revealEditorSidebar('files'),
      },
      {
        id: 'keyboardShortcuts',
        label: t('ide.command.keyboardShortcuts', undefined, {
          defaultValue: 'Keyboard Shortcuts',
        }),
        shortcut: `${mod}/`,
        category: t('ide.command.category.general', undefined, { defaultValue: 'General' }),
        execute: () => setShowShortcuts(true),
      },
    ],
    [t, mod, advancedMode, toggleTerminal],
  )

  // Global keyboard shortcuts — allowInInput + allowInEditor so Cmd combos
  // fire even when focus is in Monaco's hidden textarea or chat input.
  const shortcuts = useMemo(
    () => [
      {
        keys: 'mod+shift+f',
        handler: () => revealEditorSidebar('search'),
        allowInInput: true,
        allowInEditor: true,
      },
      {
        keys: 'mod+p',
        handler: () => setShowQuickOpen(true),
        allowInInput: true,
        allowInEditor: true,
      },
      {
        keys: 'mod+shift+p',
        handler: () => setShowCommandPalette(true),
        allowInInput: true,
        allowInEditor: true,
      },
      {
        keys: 'mod+/',
        handler: () => setShowShortcuts((v) => !v),
        allowInInput: true,
        allowInEditor: true,
      },
    ],
    [],
  )
  useKeyboardShortcuts(shortcuts)
  // ---------------------------------------------------------------------------
  // Memoized indicators for PreviewPanel (must be before early returns)
  // ---------------------------------------------------------------------------

  const previewLoadingIndicator = useMemo(
    () =>
      isPublicWorkspace && !project?.previewUrl ? (
        <p>
          {t('workspace.public.noPreview', undefined, {
            defaultValue:
              'No running app preview is available. Published source and conversations remain available.',
          })}
        </p>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <MoleculeSpinner size="lg" />
          <span
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--mol-color-text, currentColor)',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            {t('ide.preview.starting', {}, { defaultValue: 'Loading preview...' })}
          </span>
        </div>
      ),
    [t, isPublicWorkspace, project?.previewUrl],
  )

  const previewRestartingIndicator = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <MoleculeSpinner size="lg" />
        <span
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--mol-color-text, currentColor)',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          {t('ide.preview.restarting', {}, { defaultValue: 'Loading preview...' })}
        </span>
      </div>
    ),
    [t],
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Non-preview panels render as a centered modal scoped to the MAIN PANE
  // (preview/editor) — this fragment is rendered INSIDE `.mol-workspace-main-pane`
  // (see mainPaneEl below), so the PanelModal backdrop's dim + blur cover ONLY
  // the preview/editor. The chat pane and toolbar are siblings outside that
  // region and stay crisp + clickable (so clicking a toolbar icon while a panel
  // is open switches panels instead of dismissing the modal). One `activePanel`
  // value keeps exactly one open. The preview/editor underneath stay mounted.
  // Logs is the one panel that does NOT need a running dev sandbox: its Prod tab
  // reads the deployed instance through the production provider, and production
  // instances are deliberately excluded from hibernation, so a live app routinely
  // coexists with a sleeping sandbox. It is therefore rendered in the
  // stopped/sleeping return as well as in the full IDE — the 3am "my deployment
  // is broken" case used to require booting a dev sandbox first, and the Logs
  // button was greyed out anyway.
  const logsPanelModal = (
    <PanelModal
      open={activePanel === 'logs'}
      onClose={() => setActivePanel(null)}
      title={t('workspace.logs', undefined, { defaultValue: 'Logs' })}
      icon="log"
      molId="panel-logs"
      // The log viewport scrolls itself and stays pinned to the newest line, so
      // it needs a bounded body rather than one that scrolls for it.
      fillBody
    >
      <LogsPanel projectId={projectId!} />
    </PanelModal>
  )

  // Environment — ONE sectioned view (In your app · More services · Custom
  // environment variables) replacing the old Setup/Variables/Services tabs.
  // The second panel that does NOT need a running dev sandbox: it manages
  // CONTROL-PLANE data. Secrets and env vars live in the platform database
  // (vaultSecrets + projects.envVars), and all five reads — /setup, /services,
  // /vault, /env, /env/detected — are served from there; the code scan just
  // falls back to registry-derived vars when there is nothing to scan. So it is
  // rendered in the stopped/sleeping return as well as in the full IDE: pasting
  // the key you hibernated waiting to find must not require a sandbox first.
  const environmentPanelModal = (
    <PanelModal
      open={activePanel === 'environment'}
      onClose={() => setActivePanel(null)}
      title={t('workspace.environment', undefined, { defaultValue: 'Environment' })}
      icon="server"
      molId="panel-environment"
    >
      <EnvironmentPanel projectId={projectId!} canEdit={canEditProject} />
    </PanelModal>
  )

  // Pipeline — DeployPanel owns the pill tab row (Deploy · Repository · CI/CD ·
  // Domain; see PIPELINE_TABS). Native builds (BuildPanel) stay HIDDEN until
  // after launch (product decision 2026-07-06) — when they ship, add a Builds
  // entry to PIPELINE_TABS. (No Tests tab: the test runner is the `/test`
  // command — `listTests`/`runTests` on ChatPanel, served by
  // `src/lib/projectTests.ts` over `/projects/:id/tests` — which opens the same
  // closeable browser as /scripts and /skills, because running a spec belongs
  // beside the conversation that wrote it, not behind a panel tab.)
  // The third panel that does NOT need a running dev sandbox (D6). Most of what
  // it shows is control-plane or production state: deployment history and build
  // logs come from the `deployments` rows, the live URL points at the deployed
  // instance, which does not depend on the dev sandbox (Fly parks it when idle
  // and wakes it on request), and custom domains are DNS + certificate records
  // in the platform database. Only three actions inside it touch the container
  // — Deploy, git push, apply-workflow — and DeployPanel gates exactly those on
  // `sandboxStatus`, each with its own reason. Gating the whole panel meant the
  // deployment you are trying to read about was unreachable until you booted a
  // sandbox you did not need.
  const pipelinePanelModal = (
    <PanelModal
      open={activePanel === 'pipeline'}
      onClose={() => setActivePanel(null)}
      title={t('workspace.pipeline', undefined, { defaultValue: 'Pipeline' })}
      icon="rocket"
      molId="panel-pipeline"
    >
      <DeployPanel
        canEdit={canEditProject}
        projectId={projectId!}
        sandboxStatus={sandboxStatus}
        onWakeSandbox={startSandbox}
        onFixWithSynthase={handleFixDeployWithSynthase}
      />
    </PanelModal>
  )

  const panelModals = isPublicWorkspace ? null : (
    <>
      {environmentPanelModal}

      {/* API Keys panel hidden 2026-08-19 (owner request, "for now") — see the
          toolbar note by the topbar config group. Component + utilities stay
          mounted so re-enabling is a straight revert of the button, this note,
          and the MoreSheet entry. */}

      {pipelinePanelModal}

      <PanelModal
        open={activePanel === 'activity'}
        onClose={() => {
          setActivePanel(null)
          setActivityFocus({ type: null, id: null })
        }}
        title={t('workspace.activity', undefined, { defaultValue: 'Activity' })}
        icon="history"
        molId="panel-activity"
      >
        <ActivityPanel
          canEdit={canEditProject}
          projectId={projectId!}
          initialType={activityFocus.type}
          initialActivityId={activityFocus.id}
        />
      </PanelModal>

      {logsPanelModal}

      <PanelModal
        open={activePanel === 'storage'}
        onClose={() => setActivePanel(null)}
        title={t('workspace.storage', undefined, { defaultValue: 'Storage' })}
        icon="database"
        molId="panel-storage"
      >
        <StoragePanel projectId={projectId!} />
        {projectId && project && (
          <ProjectBackupSection
            projectId={projectId}
            isOwner={!project.role || project.role === 'owner'}
            sandboxRunning={sandboxStatus === 'running'}
          />
        )}
      </PanelModal>

      {/* Autoruns — hooks the platform runs when something happens (after a
          deploy, a background task, a turn, a file change), what is running
          right now, and scheduled tasks. NOT sandbox-gated: the running/
          scheduled halves are platform state, and the panel explains that
          the hook files themselves live in the sandbox. */}
      <PanelModal
        open={activePanel === 'autoruns'}
        onClose={() => setActivePanel(null)}
        title={t('workspace.autoruns', undefined, { defaultValue: 'Autoruns' })}
        icon="zap"
        molId="panel-autoruns"
      >
        <AutorunsPanel
          projectId={projectId!}
          canEdit={canEditProject}
          sandboxRunning={sandboxStatus === 'running'}
          onWakeSandbox={startSandbox}
        />
      </PanelModal>

      <PanelModal
        open={activePanel === 'team'}
        onClose={() => setActivePanel(null)}
        title={t('workspace.team', undefined, { defaultValue: 'Team' })}
        icon="people"
        molId="panel-team"
      >
        <TeamPanel projectId={projectId!} />
      </PanelModal>
    </>
  )

  if (projectId === 'new' || loading || !project) {
    return (
      <div
        className="mol-vh-fill"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          color: 'var(--mol-color-text-secondary, #aaaaaa)',
        }}
      >
        {createError ? (
          <>
            <div
              style={{
                fontSize: 15,
                color: 'var(--mol-color-error, #ef4444)',
                maxWidth: 400,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {createError.message}
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 12,
                marginTop: 8,
                alignItems: 'center',
              }}
            >
              {/* Guest/anon limits are escaped by AUTHENTICATING — offer both
                  Sign up (new account) and Log in (existing) as first-class
                  actions, not just "Go back". requiresSignup is set by the
                  server for the guest-session / anonymous-project limits. */}
              {createError.requiresSignup && (
                <>
                  {/* Canonical CTA colors (molecule/DESIGN.md → "CTA color
                      semantics"): Sign up primary, Log in success — the same
                      pair the auth pages, the Banner and UsageNudge render. */}
                  <Link
                    to="/signup"
                    data-mol-id="create-error-signup"
                    className={cm.cn(
                      cm.button({ variant: 'solid', color: 'primary', size: 'md' }),
                      cm.touchTargetCompact,
                    )}
                    style={{ textDecoration: 'none' }}
                  >
                    {t('common.signUp', undefined, { defaultValue: 'Sign up' })}
                  </Link>
                  <Link
                    to="/login"
                    data-mol-id="create-error-login"
                    className={cm.cn(
                      cm.button({ variant: 'solid', color: 'success', size: 'md' }),
                      cm.touchTargetCompact,
                    )}
                    style={{ textDecoration: 'none' }}
                  >
                    {t('common.logIn', undefined, { defaultValue: 'Log in' })}
                  </Link>
                </>
              )}
              <Link
                to="/"
                data-mol-id="create-error-go-back"
                className={cm.cn(
                  cm.button({ variant: 'outline', size: 'md' }),
                  cm.touchTargetCompact,
                )}
                style={{ textDecoration: 'none' }}
              >
                {t('workspace.goBack', undefined, { defaultValue: 'Go back' })}
              </Link>
            </div>
          </>
        ) : loadError ? (
          <>
            <div
              style={{
                fontSize: 15,
                color: 'var(--mol-color-error, #ef4444)',
                maxWidth: 400,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {loadError}
              {loadErrorCta?.note ? ` ${loadErrorCta.note}` : ''}
            </div>
            <div
              style={{
                display: 'flex',
                // Wraps + centers so all three actions stay reachable at 390px
                // (the row's intrinsic width exceeds a phone viewport).
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 12,
                marginTop: 8,
                alignItems: 'center',
              }}
            >
              {/* The billing CTA. It renders ONLY when the server named a limit, and
                  says what that limit actually needs (Add funds, Raise spend cap,
                  Upgrade…) — a 404 or a plain rate-limit has no plan to sell.
                  `billingCta()` already resolves the CANONICAL color for each action
                  (molecule/DESIGN.md → "CTA color semantics"); this surface renders it
                  with the same `cm.button` recipe the Dashboard and UsageNudge banners
                  use, so one action never changes shape or hue between surfaces. */}
              {loadErrorCta?.actions?.map((action) => {
                const molId =
                  action.id === 'upgrade' ? 'rate-limit-upgrade' : `rate-limit-${action.id}`
                const className = cm.cn(
                  cm.button({ variant: 'solid', color: action.color, size: 'md' }),
                  cm.touchTargetCompact,
                )
                const style = { textDecoration: 'none' } as const
                // An external target (the support mailto) is a plain anchor; an
                // in-app route stays a router Link.
                return action.external ? (
                  <a
                    key={action.id}
                    href={action.href}
                    data-mol-id={molId}
                    className={className}
                    style={style}
                  >
                    {action.label}
                  </a>
                ) : (
                  <Link
                    key={action.id}
                    to={action.href}
                    data-mol-id={molId}
                    className={className}
                    style={style}
                  >
                    {action.label}
                  </Link>
                )
              })}
              {/* Retry — the surface's primary recovery action, identical to every
                  other Retry on the platform (Analytics, UsageInspector, CaptchaGate). */}
              <button
                type="button"
                data-mol-id="rate-limit-retry"
                onClick={() => window.location.reload()}
                className={cm.cn(
                  cm.button({ variant: 'solid', color: 'primary', size: 'md' }),
                  cm.touchTargetCompact,
                )}
              >
                {t('common.retry', undefined, { defaultValue: 'Retry' })}
              </button>
              {/* Go back — the secondary sibling: outline, per DESIGN.md's
                  "Actions look like buttons" (solid for the one primary action,
                  outline for every secondary action beside it). */}
              <Link
                to="/"
                data-mol-id="rate-limit-go-back"
                className={cm.cn(
                  cm.button({ variant: 'outline', size: 'md' }),
                  cm.touchTargetCompact,
                )}
                style={{ textDecoration: 'none' }}
              >
                {t('workspace.goBack', undefined, { defaultValue: 'Go back' })}
              </Link>
            </div>
          </>
        ) : projectId === 'new' && captchaRequired && !captchaCleared ? (
          // Bot/abuse protection (P2-26): a guest's first prompt clears a
          // human-proof challenge before the project is created (which spends
          // free AI). The CaptchaGate is inert (renders nothing + reports
          // "passed") when captcha is unconfigured, so this branch is never
          // reached in dev/local — `captchaRequired` is already false there.
          <>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--mol-color-text, #e0e0e0)' }}>
              {t('workspace.captcha.title', undefined, {
                defaultValue: 'Quick check before we start',
              })}
            </div>
            <div style={{ fontSize: 13, maxWidth: 360, textAlign: 'center', lineHeight: 1.5 }}>
              {t('workspace.captcha.subtitle', undefined, {
                defaultValue: "Confirm you're human to start building.",
              })}
            </div>
            <CaptchaGate
              action="guest-project"
              molId="workspace-captcha-gate"
              onVerify={(token) => {
                // Hand the token over BEFORE flipping the flag: the flip
                // unmounts this gate, whose cleanup clears the shared store.
                guestCaptchaTokenRef.current = token || null
                setCaptchaCleared(true)
              }}
              onExpire={() => {
                guestCaptchaTokenRef.current = null
                setCaptchaCleared(false)
              }}
            />
          </>
        ) : (
          <>
            <MoleculeSpinner size="lg" />
            <div style={{ fontSize: 14 }}>
              {projectId === 'new'
                ? t('workspace.creatingProject', undefined, {
                    defaultValue: 'Creating project…',
                  })
                : t('common.loading', undefined, { defaultValue: 'Loading…' })}
            </div>
          </>
        )}
      </div>
    )
  }

  // Pre-IDE phases: discovery (a centered chat card) and booting (the chat
  // slides into the left panel while the synthesizing view fills the preview
  // area). Both share ONE ChatPanel mount so the layout change is a pure CSS
  // transition and the conversation never reloads. The full IDE takes over
  // once the sandbox is running.
  const isBooting = sandboxLoading || sandboxStatus === 'creating' || sandboxStatus === 'queued'
  const inDiscovery =
    !!project &&
    !project.templateSlug &&
    sandboxStatus === 'stopped' &&
    !sandboxLoading &&
    !sandboxError

  // The sandbox is fully up. Toolbar actions that drive the live preview/editor
  // or the running container (the view toggle, panels, terminal) are functional
  // only here; everywhere else after discovery they render visible-but-disabled
  // (E2) so the chrome appears the instant discovery ends, without firing actions
  // that need a running sandbox.
  const sandboxRunning = sandboxStatus === 'running'

  // Whether a ChatPanel is mounted in the branch about to render — true in the
  // discovery/booting return and in the full IDE, false in the stopped/sleeping
  // return below. The report/share modals live INSIDE ChatPanel, so this decides
  // which of the two equivalent paths the toolbar's bug/share buttons take.
  const chatMounted = inDiscovery || isBooting || sandboxRunning

  // Report a bug / share the project. Neither touches the sandbox — both POST to
  // the platform API (`/projects/:id/report`, `/projects/:id/shares`) — so they
  // stay available in EVERY phase, including a hibernating sandbox, which is
  // precisely when someone wants to report that their project won't come back.
  // Defined once and used by BOTH the desktop toolbar and the mobile More sheet
  // so the two surfaces can't drift. Plain functions, NOT useCallback — this
  // point sits after the loading/discovery early returns, so a hook here would
  // break the rules of hooks; the consumers aren't memoized either way.
  const openReport = (): void => {
    if (chatMounted) {
      // The chat owns the modal here; its confirmation lands in the timeline.
      // The modal renders inside ChatPanel, so reveal chat on phones first.
      if (isMobile) setMobilePane('chat')
      setOpenReportSignal((n) => n + 1)
      return
    }
    setHostReportOpen(true)
  }
  // The Share dialog's own sections below the links — one element, handed to
  // every Share dialog (both ChatPanel mounts and the host one) so they match.
  const shareModalContent =
    projectId && projectId !== 'new' ? (
      <PublicBuildShareSection
        projectId={projectId}
        canAdmin={canAdminProject}
        showDivider={canAdminProject}
      />
    ) : null
  const openShare = (): void => {
    if (chatMounted) {
      if (isMobile) setMobilePane('chat')
      setOpenShareSignal((n) => n + 1)
      return
    }
    setHostShareOpen(true)
  }

  // The host-mounted report/share modals. Only openReport/openShare above ever
  // open them, and only when no ChatPanel is mounted — so they never double up
  // with the chat's own copies. ReportModal has no success state of its own and
  // ShareModal's link disappears with the modal, so the outcome is surfaced
  // through the global NotificationBanner — the equivalent of the chat system
  // card the in-chat path adds.
  const hostReportShareModalsEl =
    projectId && projectId !== 'new' ? (
      <>
        {hostReportOpen && (
          <ReportModal
            projectId={projectId}
            conversationId={activeConversationId}
            productName={PRODUCT_NAME}
            appVersion={versionState.version || undefined}
            onClose={() => setHostReportOpen(false)}
            onSubmitted={(result: ReportResult) => {
              setHostReportOpen(false)
              const { key, defaultValue } = formatReportConfirmation(result)
              emitNotification(
                t(key, { productName: PRODUCT_NAME }, { defaultValue }),
                result.ok ? 'info' : 'error',
                result.url
                  ? {
                      label: t('ide.chat.report.viewIssue', undefined, {
                        defaultValue: 'View issue',
                      }),
                      href: result.url,
                    }
                  : undefined,
              )
            }}
          />
        )}
        {hostShareOpen && (
          <ShareModal
            projectId={projectId}
            canManage={canAdminProject}
            showLinks={canAdminProject}
            refreshSignal={shareLinksTick}
            onClose={() => setHostShareOpen(false)}
            onCreated={(result: ShareLinkResult) => {
              emitNotification(
                t(
                  'ide.chat.share.created',
                  { role: result.role },
                  { defaultValue: 'Created a {{role}} share link.' },
                ),
                'info',
                {
                  label: t('ide.chat.share.openLink', undefined, { defaultValue: 'Open link' }),
                  href: buildShareUrl(
                    result,
                    typeof window !== 'undefined' ? window.location.origin : '',
                  ),
                },
              )
            }}
          >
            {shareModalContent}
          </ShareModal>
        )}
      </>
    ) : null

  // Dashboard-link label (E5): there is no team/org concept yet, so show the
  // signed-in user's display name, falling back to "Guest" for anonymous users
  // — never the project slug.
  const ownerLabel = isPublicWorkspace
    ? t('workspace.identity.public', undefined, { defaultValue: 'Public' })
    : user?.name?.trim() || t('workspace.identity.guest', undefined, { defaultValue: 'Guest' })

  // The reusable profile modal (D1) — the own profile (editable) or, when a
  // teammate's avatar/name was clicked in the chat, THEIR view-only profile
  // (profileModalProfile non-null). Defined once and rendered inside the header
  // below (so it's mounted in the booting / sleeping / running phases) plus a
  // guarded copy in the pure-discovery branch (where the header isn't shown).
  // Portals to body, so its place in the markup is immaterial.
  const profileModalEl = (
    <ProfileModal
      open={profileModalOpen}
      onClose={() => setProfileModalOpen(false)}
      initialMode={profileModalMode}
      profile={profileModalProfile ?? undefined}
    />
  )

  // Model-provider modal — opened by the /model picker's "manage your own
  // models" row (a focused BYO provider manager). Portals to body; mounted in
  // BOTH the pre-IDE and full-IDE returns, like the repo-import modal below.
  const modelProviderModalEl =
    !isPublicWorkspace && projectId && projectId !== 'new' ? (
      <ModelProviderModal
        open={modelProviderOpen}
        onClose={() => setModelProviderOpen(false)}
        projectId={projectId}
        canEdit={canEditProject}
        activeMode={modelProviderMode}
        onModelSelected={() => setModelSelectionSignal((n) => n + 1)}
      />
    ) : null

  // Repo-import modal (GIT2) — self-contained fixed overlay so it works in the
  // discovery layout too (no main pane there for PanelModal to scope to).
  // Mounted in BOTH the pre-IDE and full-IDE returns.
  const importRepoModalEl =
    !isPublicWorkspace && projectId && projectId !== 'new' ? (
      <ImportRepoModal
        projectId={projectId}
        open={importRepoOpen}
        onClose={() => setImportRepoOpen(false)}
        onDone={sendImportResume}
        onProjectRenamed={(name) => setProject((prev) => (prev ? { ...prev, name } : prev))}
      />
    ) : null

  // The 48px IDE header (logo + project name · toolbar icons · theme toggle),
  // extracted so it can render the moment discovery ends — during booting and the
  // stopped/sleeping states too, not only once the sandbox is running (E2). The
  // Preview/Code view switch now lives here as part of the main icons (E1); the
  // five semantic groups are consolidated to three dividers (E4).
  const workspaceHeader = (
    <header
      ref={workspaceHeaderRef}
      className="mol-workspace-header"
      style={{
        height: 48,
        flexShrink: 0,
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        backgroundColor: 'var(--mol-color-background, #0e0e0e)',
      }}
    >
      {/* Mobile: the fixed HealthBanner anchors below this header. */}
      {isMobile && <HeaderTopOffsetPublisher headerRef={workspaceHeaderRef} />}
      {/* Identity row (P2-24): user name / project name / conversation title —
          ONE consistent style (same fontSize + colour; user + project semibold,
          conversation normal weight), evenly spaced on a single gap, no pencils.
          This group flex-GROWS into the space-between free space so the
          conversation title can flex-fill the gap up to the toolbar icons. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <Link
          to="/dashboard"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--color-foreground)',
            textDecoration: 'none',
            flexShrink: 0,
          }}
        >
          <img src="/logo.svg" width={20} height={20} alt={APP_NAME} />
          {/* E5: the dashboard link names the owner (team/org → user → Guest),
              not the project slug. No team/org model exists yet, so it is the
              signed-in user's name or "Guest". Semibold + same size/colour as the
              project title (P2-24). */}
          {/* P2-24: same 2px/6px inner padding as the editable titles so the
              first "/" separator is centered symmetrically (matching the second).
              Hidden on phones (.mol-owner-label, Workspace.css) — the 390px
              header has room for project + conversation only. */}
          <span
            className="mol-owner-label"
            style={{ fontSize: 13, fontWeight: 600, padding: '2px 6px' }}
          >
            {ownerLabel}
          </span>
        </Link>
        {project && (
          <>
            <span
              className="mol-owner-label"
              style={{ color: 'var(--color-foreground-tertiary)', flexShrink: 0 }}
            >
              /
            </span>
            {/* P2-24: the project title is a contentEditable field (no pencil)
                that persists the rename. UNLIMITED width on desktop — never
                ellipsized — so the wrapper never shrinks (flexShrink:0). On
                phones it MUST shrink + ellipsize (fill mode) or a long name
                pushes the conversation controls off the 390px row. */}
            <div
              // mol-conv-title-fill: the Tooltip wraps its trigger in an
              // inline-block div that can't shrink; the structural `> div` rule
              // (Workspace.css) makes it a flex item so the fill-mode ellipsis
              // resolves instead of painting over the "/" separator (mobile).
              className={isMobile ? 'mol-conv-title-fill' : undefined}
              style={
                isMobile
                  ? // 80px floor: the project name is the primary identity — it
                    // ellipsizes but never collapses to a couple of glyphs when
                    // the conversation title is long.
                    { flexShrink: 1, display: 'flex', minWidth: 80, maxWidth: '38vw' }
                  : { flexShrink: 0, display: 'flex', minWidth: 0 }
              }
            >
              {/* Viewers see the plain name — renaming is editor work. */}
              {canEditProject ? (
                <InlineEditableTitle
                  value={project.name}
                  onSave={handleRenameProject}
                  molId="topbar-project-title"
                  ariaLabel={t('workspace.project.renameAria', undefined, {
                    defaultValue: 'Project name',
                  })}
                  editTooltip={t('workspace.project.rename', undefined, {
                    defaultValue: 'Rename project',
                  })}
                  maxLength={120}
                  fill={isMobile}
                  textStyle={{ fontSize: 13, fontWeight: 600 }}
                />
              ) : (
                <span
                  data-mol-id="topbar-project-title"
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '2px 6px',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {project.name}
                </span>
              )}
            </div>
            {/* P2-24: the conversation title (contentEditable) flex-fills the
                space between the project name and the toolbar icons, ellipsizing
                when it can't fit; + the searchable history dropdown and new-chat
                "+", relocated from the chat header. */}
            <ConversationMenu
              readOnly={!canEditProject}
              conversations={conversations}
              activeConversationId={activeConversationId}
              onSelect={(id) => {
                handleSelectConversation(id)
                // Switching conversations is a chat action — reveal the chat
                // surface on phones so the switch is immediately visible.
                if (isMobile) setMobilePane('chat')
              }}
              onNewChat={() => {
                void handleNewChat()
                if (isMobile) setMobilePane('chat')
              }}
              onRename={handleRenameConversation}
              onOpenDropdown={fetchConversations}
            />
          </>
        )}
      </div>
      {publicWorkspace && (
        <Button
          size="sm"
          color="primary"
          disabled={publicWorkspace.cloning || !publicWorkspace.cloneUrl}
          onClick={publicWorkspace.onClone}
          data-mol-id="workspace-clone"
        >
          {publicWorkspace.cloning
            ? t('workspace.public.cloning', undefined, { defaultValue: 'Cloning…' })
            : t('workspace.public.clone', undefined, { defaultValue: 'Clone to my workspace' })}
        </Button>
      )}
      <div
        className="mol-workspace-toolbar"
        // Phones hide this whole row in every phase (Workspace.css) — the bottom
        // nav + its More sheet carry every toolbar action, and the
        // stopped/sleeping return renders that same nav on phones too, so the
        // desktop header toolbar is never the only chrome there.
        style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}
      >
        {/* View — the Preview ⇆ Code switch for the main pane, now the LEFTMOST,
            standout group (P2-18): a token-tinted segmented pill (.mol-toolbar-view-group
            in Workspace.css) frames them as the primary main-pane control so they
            stand out from the rest of the flat icons. Hidden on phones, where the
            bottom nav drives Preview/Code. Disabled until the sandbox is running. */}
        <span
          className="mol-toolbar-view-group"
          style={{ display: 'flex', alignItems: 'center', gap: 2 }}
        >
          {/* The whole group acts as ONE toggle: clicking EITHER button flips
              Preview↔Code regardless of which is currently selected (so clicking the
              already-active button switches to the other view). */}
          <ToolbarIconButton
            icon="eye"
            label={t('workspace.viewToggle.preview', undefined, { defaultValue: 'Preview' })}
            active={mainView === 'preview'}
            molId="topbar-view-preview"
            disabled={!sandboxRunning}
            segmented
            onClick={() => setMainView((v) => (v === 'preview' ? 'editor' : 'preview'))}
          />
          <ToolbarIconButton
            icon="code"
            label={t('workspace.viewToggle.code', undefined, { defaultValue: 'Code' })}
            active={mainView === 'editor'}
            molId="topbar-view-editor"
            disabled={!sandboxRunning}
            segmented
            onClick={() => setMainView((v) => (v === 'preview' ? 'editor' : 'preview'))}
          />
        </span>
        <span className="mol-toolbar-divider" aria-hidden="true" />
        {/* Terminal (Advanced only) — NOT one of the six; sits just left of the
            grouped six so they stay a clean contiguous ordered run. Wrapped in a
            classed slot for the same reason the six are grouped: it gives
            Workspace.css a handle to drop this sandbox-dependent control in the
            phone actions-only toolbar. Editor+ like the command palette + mobile
            More sheet — the terminal WS closes 4003 for viewers. */}
        {advancedMode && canEditProject && (
          <span
            className="mol-toolbar-terminal-slot"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <ToolbarIconButton
              icon="terminal"
              label={t('workspace.terminal', undefined, { defaultValue: 'Terminal' })}
              active={showTerminal}
              molId="topbar-terminal"
              disabled={!sandboxRunning}
              onClick={() => toggleTerminal()}
            />
          </span>
        )}
        {/* The six config/inspect icons grouped in the exact requested order
            (P2-22): storage · environment · API keys · activity · logs · pipeline. */}
        {!isPublicWorkspace && (
          <span
            className="mol-toolbar-config-group"
            style={{ display: 'flex', alignItems: 'center', gap: 2 }}
          >
            <ToolbarIconButton
              icon="database"
              label={t('workspace.storage', undefined, { defaultValue: 'Storage' })}
              active={activePanel === 'storage'}
              molId="topbar-storage"
              disabled={!sandboxRunning}
              onClick={() => togglePanel('storage')}
            />
            {/* Environment is NOT sandbox-gated: it edits control-plane data —
              secrets and env vars live in the platform database, not in the
              container — so every read still works while the sandbox sleeps.
              Greying it out locked users out of pasting the very key they were
              hibernating waiting to find. The classed slot is what lets
              Workspace.css keep this icon in the phone's actions-only toolbar,
              alongside Logs. */}
            <span
              className="mol-toolbar-environment-slot"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {/* Env/setup/services GETs are editor+ — the panel would only 403 for viewers. */}
              {canEditProject && (
                <ToolbarIconButton
                  icon="server"
                  label={t('workspace.environment', undefined, { defaultValue: 'Environment' })}
                  active={activePanel === 'environment'}
                  molId="topbar-environment"
                  // E-03: small warning count badge — how many detected env vars
                  // still need a value. 0 → no badge.
                  badgeCount={envMissingCount}
                  badgeTone="warning"
                  onClick={() => togglePanel('environment')}
                />
              )}
            </span>
            {/* API Keys hidden 2026-08-19 (owner request, "for now"). Both the
              mint AND the revoke surface live ONLY in ApiKeysPanel (POST/DELETE
              /projects/:id/api-keys — see api-keys-utilities.ts), so hiding this
              button + the panel disables minting too; it does NOT re-open the
              "unrevokeable `mk_` credential" hole the 2026-07-07 restore fixed,
              because that hole existed only when minting lived elsewhere while
              the list/revoke surface was hidden — it doesn't here. Re-enable by
              reverting this button, the panel modal, and the MoreSheet entry. */}
            {/* Activity is NOT sandbox-gated: every row it renders lives in the
              platform database, and the panel reads nothing else. It used to be
              greyed out whenever the sandbox slept, which is exactly the moment
              a user opens it — "what did my app send yesterday?" — and the
              inline chat cards opened the same panel anyway, bypassing the
              guard from a surface the user could still click. */}
            {/* Activity rows carry recipients/bodies (editor+ server-side) — hidden for viewers. */}
            {canEditProject && (
              <ToolbarIconButton
                icon="history"
                label={t('workspace.activity', undefined, { defaultValue: 'Activity' })}
                active={activePanel === 'activity'}
                molId="topbar-activity"
                onClick={() =>
                  setActivePanel((cur) => {
                    if (cur === 'activity') {
                      // Closing — clear inline-card focus so a later manual open is unfiltered.
                      setActivityFocus({ type: null, id: null })
                      return null
                    }
                    return 'activity'
                  })
                }
              />
            )}
            {/* Logs is NOT sandbox-gated: its Prod tab reads the deployed
              instance, which keeps running while the dev sandbox hibernates.
              The classed slot is what lets Workspace.css keep this one icon in
              the phone's actions-only toolbar, where the rest of the config
              group is hidden because it IS sandbox-dependent. */}
            <span
              className="mol-toolbar-logs-slot"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {/* Logs is editor+ server-side (container output can carry secrets) — hidden for viewers. */}
              {canEditProject && (
                <ToolbarIconButton
                  icon="log"
                  label={t('workspace.logs', undefined, { defaultValue: 'Logs' })}
                  active={activePanel === 'logs'}
                  molId="topbar-logs"
                  onClick={() => togglePanel('logs')}
                />
              )}
            </span>
            {/* Pipeline is NOT sandbox-gated (D6): deployment history, the live
              URL, build logs and custom domains are all control-plane or
              production state, and the live URL points at the deployed
              instance, which does not depend on the dev sandbox (Fly parks it
              when idle and wakes it on request). The three actions that DO
              need the container — Deploy, git push, apply-workflow — are gated
              inside DeployPanel, each with its own reason. Classed slot for the
              same reason as Logs/Environment: it is what keeps this icon in the
              phone's actions-only toolbar. */}
            {/* Autoruns is NOT sandbox-gated: running work and scheduled tasks are
              platform state; the panel says when the hook files need the sandbox. */}
            <ToolbarIconButton
              icon="zap"
              label={t('workspace.autoruns', undefined, { defaultValue: 'Autoruns' })}
              active={activePanel === 'autoruns'}
              molId="topbar-autoruns"
              onClick={() => togglePanel('autoruns')}
            />
            <span
              className="mol-toolbar-pipeline-slot"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <ToolbarIconButton
                icon="rocket"
                label={t('workspace.pipeline', undefined, { defaultValue: 'Pipeline' })}
                active={activePanel === 'pipeline'}
                molId="topbar-pipeline"
                onClick={() => togglePanel('pipeline')}
              />
            </span>
          </span>
        )}
        <span className="mol-toolbar-divider" aria-hidden="true" />
        {/* Account actions + appearance: theme · report a bug · share · team ·
            your profile. The theme toggle is the FIRST control in this group (far
            LEFT of it), then the bug icon, share, Team, and the user button LAST
            at the far-right end. The Synthase-settings gear
            (P2-19) is removed for now. The Team icon (P2-20) is back: its panel
            was mounted but no control on any breakpoint could open it, so
            members, pending invitations and public links were unreachable.
            Share/Report go through openShare/openReport, which drive
            openShareSignal/openReportSignal into ChatPanel while it is mounted and
            fall back to the host-mounted modals while the sandbox sleeps — never
            sandbox-gated, since neither action touches the container. */}
        {/* Theme toggle rendered THROUGH ToolbarIconButton so it matches the other
            toolbar controls (size/hover/active/mobile-44px/tooltip) — the IDE1 fix;
            it used to be a different 30px component breaking the row. Always enabled
            (a pure UI preference, independent of the sandbox). Leftmost of this
            group (owner request). */}
        <WorkspaceThemeToggle />
        {!isPublicWorkspace && (
          <ToolbarIconButton
            icon="bug"
            label={t('workspace.report', undefined, { defaultValue: 'Report a bug' })}
            molId="topbar-report"
            onClick={openReport}
          />
        )}
        {/* Share hidden from viewers. Below admin the dialog shows only "How it
            was built" — minting/listing links is admin+ (server-gated). */}
        {canEditProject && (
          <ToolbarIconButton
            icon="share"
            label={t('workspace.share', undefined, { defaultValue: 'Share project' })}
            molId="topbar-share"
            onClick={openShare}
          />
        )}
        {/* Team sits directly left of the user button — both are "who is on this
            project / who am I" controls, so they read as one pair at the end of
            the row. */}
        {!isPublicWorkspace && (
          <ToolbarIconButton
            icon="people"
            label={t('workspace.team', undefined, { defaultValue: 'Team' })}
            active={activePanel === 'team'}
            molId="topbar-team"
            onClick={() => togglePanel('team')}
          />
        )}
        {/* User button — LAST in the toolbar (far right end). Opens the FULL user
            settings panel in the same drawer the dashboard's user menu uses, so the
            user icon behaves identically everywhere. Account-level, so always
            enabled (independent of the sandbox), like the theme toggle. The
            ProfileModal remains reachable from a chat-avatar click (C5, view mode →
            Edit profile). */}
        {!isPublicWorkspace && (
          <ToolbarIconButton
            icon="user"
            label={t('userMenu.open', undefined, { defaultValue: 'Open user menu' })}
            active={userPanelOpen}
            molId="topbar-profile"
            onClick={openUserPanel}
          />
        )}
      </div>

      {/* D1/D2/C5: the reusable profile modal. Like the settings modal it portals
          to body, so its place in the header markup is immaterial. */}
      {!isPublicWorkspace && profileModalEl}
      {/* The user settings drawer the top-bar user button opens. Portals to body. */}
      {!isPublicWorkspace && (
        <UserSettingsDrawer open={userPanelOpen} onClose={() => setUserPanelOpen(false)} />
      )}
      {/* In-IDE log in / sign up / upgrade (shared flow; mounts the modal lazily +
          the CTA click-interceptor). Portals to body. */}
      {authModalMountEl}
      {/* The in-IDE pricing/billing modal the interceptor routes upgrade CTAs
          to. Portals to body. */}
      {!isPublicWorkspace && upgradeModalEl}
      {/* Host-mounted report/share modals, used in the phases where no ChatPanel
          is mounted to own them. Mounted HERE (rather than in the stopped/sleeping
          return) so an open form survives the phase change when the user hits
          "Wake up" mid-report — the header is the one element common to booting,
          sleeping and the full IDE. Fixed overlays, so the markup position is
          immaterial. */}
      {hostReportShareModalsEl}
      {/* Mobile "More" bottom sheet — ONE instance for BOTH phone overflow
          affordances: the full-IDE bottom-nav 4th tab AND the stopped/sleeping
          actions-only header button above. Mounted in the header because the
          header is common to booting, sleeping and the full IDE; it portals to
          <body>, so the markup position is immaterial. Phone-only (both
          triggers are), so it is not instantiated on desktop. */}
      {isMobile && !isPublicWorkspace && (
        <WorkspaceMoreSheet
          open={moreSheetOpen}
          onClose={() => setMoreSheetOpen(false)}
          onOpenPanel={(panel) => setActivePanel(panel)}
          onToggleTerminal={
            advancedMode && canEditProject
              ? () => {
                  // Open-only from the sheet: the terminal's state is invisible
                  // there, so a toggle could HIDE it and land the user on Code
                  // without the thing they asked for. The dock has its own
                  // in-place close affordance.
                  setShowTerminal(true)
                  setMainView('editor')
                  setMobilePane('main')
                }
              : null
          }
          onReport={openReport}
          onShare={canEditProject ? openShare : null}
          canEdit={canEditProject}
          onProfile={() => openOwnProfile('edit')}
          envMissingCount={envMissingCount}
          sandboxRunning={sandboxRunning}
          isGuest={isGuest}
        />
      )}
    </header>
  )

  // Ambient account strips — guest reminder, budget-reset welcome-back, usage
  // nudge — ONE stacked group at the VERY TOP of the page, ABOVE the workspace
  // header, identical in every post-discovery phase (booting, sleeping/stopped,
  // full IDE). They used to be split around the header (guest above it, usage
  // strips below the toolbar), which read as randomly-placed banners. Each
  // renders nothing when it has nothing to say. Discovery stays chrome-free.
  const topBannersEl = publicWorkspace ? (
    <div
      className={cm.cn(cm.surface, cm.borderB, cm.sp('px', 3), cm.sp('py', 2), cm.textSize('sm'))}
      role="status"
      data-mol-id="workspace-public-notice"
    >
      {t('workspace.public.notice', undefined, {
        defaultValue:
          'Public read-only workspace. Only published source and selected conversations are visible. Clone to make changes in your own copy.',
      })}
      {publicWorkspace.cloneUrl && (
        <p className={cm.cn(cm.textMuted, cm.textSize('xs'))} style={{ margin: '4px 0 0' }}>
          {t('workspace.public.snapshotNotice', undefined, {
            defaultValue:
              'Source is a published snapshot. Preview shows the owner’s live app and may include later edits. Downloaded or cloned copies cannot be recalled.',
          })}
        </p>
      )}
      {publicWorkspace.revision && (
        <code className={cm.sp('px', 2)}>{publicWorkspace.revision.slice(0, 10)}</code>
      )}
      {publicWorkspace.cloneUrl && (
        <button
          type="button"
          className={cm.button({ variant: 'ghost', size: 'sm' })}
          onClick={() => {
            void writeText(`git clone '${publicWorkspace.cloneUrl!.replace(/'/g, `'\\''`)}'`)
              .then(() => setPublicCommandCopied(true))
              .catch((_error: unknown) => {
                emitNotification(
                  t('workspace.public.copyError', undefined, {
                    defaultValue: 'Could not copy the Git command.',
                  }),
                  'error',
                )
              })
          }}
        >
          {publicCommandCopied
            ? t('workspace.public.copied', undefined, { defaultValue: 'Copied Git command' })
            : t('workspace.public.gitClone', undefined, { defaultValue: 'Copy Git clone command' })}
        </button>
      )}
      {!publicWorkspace.cloneUrl && (
        <span>
          {t('workspace.public.unpublished', undefined, {
            defaultValue: ' The owner has not published a source repository yet.',
          })}
        </span>
      )}
      {publicWorkspace.cloneError && (
        <p className={cm.textError} role="alert">
          {publicWorkspace.cloneError}
        </p>
      )}
    </div>
  ) : (
    <>
      <PwaUpdateBanner />
      <GuestReminder isAnonymous={isGuest} />
      <BudgetResetBanner variant="compact" />
      <UsageNudge variant="compact" />
    </>
  )

  // The phone bottom nav — Chat · Preview · Code · More. Rendered in BOTH the
  // full IDE (surface tabs switch the visible pane) AND the stopped/sleeping
  // return (surface tabs wake the sandbox, since the surface can't show until it
  // is running), so the phone's actions live at the bottom in every phase — the
  // desktop header toolbar is hidden on phones throughout. `onSurface` is what
  // differs between the two callers; the More tab always opens the shared sheet.
  const renderMobileNav = ({
    activeTarget,
    onSurface,
  }: {
    /** The surface tab to highlight, or `null` when none is active (sleeping). */
    activeTarget: MobileNavTarget | null
    /** Handler for a surface-tab tap. */
    onSurface: (target: MobileNavTarget) => void
  }) => {
    const icons: Record<MobileNavTarget, { icon: IconName; label: string }> = {
      chat: {
        icon: 'chat',
        label: t('workspace.mobileNav.chat', undefined, { defaultValue: 'Chat' }),
      },
      preview: {
        icon: 'eye',
        label: t('workspace.mobileNav.preview', undefined, { defaultValue: 'Preview' }),
      },
      code: {
        icon: 'code',
        label: t('workspace.mobileNav.code', undefined, { defaultValue: 'Code' }),
      },
    }
    return (
      <nav
        className="mol-workspace-mobile-nav"
        role="tablist"
        aria-label={t('workspace.mobileNav.label', undefined, {
          defaultValue: 'Workspace surface',
        })}
      >
        {MOBILE_NAV_TARGETS.map((target) => {
          const active = !moreSheetOpen && activeTarget === target
          return (
            <button
              key={target}
              type="button"
              role="tab"
              aria-selected={active}
              data-mol-id={`mobile-nav-${target}`}
              className={
                active ? 'mol-workspace-mobile-nav-btn is-active' : 'mol-workspace-mobile-nav-btn'
              }
              onClick={() => onSurface(target)}
            >
              <Icon name={icons[target].icon} size={20} />
              <span>{icons[target].label}</span>
            </button>
          )
        })}
        {/* 4th tab: everything the desktop toolbar offers, as a bottom sheet.
            Badge-dotted while env vars are missing so the warning the desktop
            toolbar shows isn't lost. */}
        {!isPublicWorkspace && (
          <button
            type="button"
            role="tab"
            aria-selected={moreSheetOpen}
            data-mol-id="mobile-nav-more"
            className={
              moreSheetOpen
                ? 'mol-workspace-mobile-nav-btn is-active'
                : 'mol-workspace-mobile-nav-btn'
            }
            onClick={() => setMoreSheetOpen(true)}
          >
            <span className="mol-mobile-nav-icon-wrap">
              <Icon name="menu" size={20} />
              {envMissingCount > 0 && <span className="mol-mobile-nav-dot" aria-hidden="true" />}
            </span>
            <span>{t('workspace.mobileNav.more', undefined, { defaultValue: 'More' })}</span>
          </button>
        )}
      </nav>
    )
  }

  if (inDiscovery || isBooting) {
    // The chat card stays hidden until the floating PromptMorphOverlay has
    // docked onto where the input is, then hard-swaps in (same box/position) so
    // the Synthase chat never appears alongside the still-moving prompt.
    const cardStyle: React.CSSProperties | undefined =
      inDiscovery && !cardRevealed ? { opacity: 0 } : undefined
    return (
      <div className="mol-vh-fill" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Suppressed during discovery — nudging an anonymous user to sign up
            before they've even described what they want is bad UX. The strips
            return once the build starts (booting) and in the full IDE. */}
        {!inDiscovery && topBannersEl}
        {/* The toolbar appears the moment discovery ends — i.e. during booting
            (E2), not only once the sandbox is running. Its sandbox-dependent
            icons render disabled here. Discovery itself stays chrome-free. */}
        {isBooting && workspaceHeader}
        {/* C5: a chat-avatar click during pure discovery (no header → no
            header-hosted modal) still needs the profile modal mounted. When
            booting, the header above already renders it, so guard to avoid a
            double mount. */}
        {!isBooting && profileModalEl}
        {!isBooting && authModalMountEl}
        {!isBooting && upgradeModalEl}
        {importRepoModalEl}
        {modelProviderModalEl}
        <div className="mol-pre-ide-body" data-phase={inDiscovery ? 'discovery' : 'booting'}>
          <div className="mol-pre-ide-chat" style={cardStyle}>
            <ChatPanel
              {...IDE_IDENTITY}
              buildUpgradeCta={buildUpgradeCta}
              version={versionState.version}
              extraCommands={moleculeCommands}
              projectId={projectId!}
              userAvatar={user?.avatar}
              // C5: clicking any author's avatar/name opens their profile modal
              // — the viewer's own is editable, a teammate's is view-only.
              // currentUserId stamps author-less rows as the viewer's own.
              currentUserId={user?.id}
              onProfileClick={openChatProfile}
              initialInputValue={morphText || undefined}
              autoSubmitSignal={autoSubmitKey}
              hideConversationMenu={inDiscovery}
              // Programmatic sends must work during discovery too — the
              // repo-import card's outcome resumes the pending tool this way.
              pendingMessage={fixWithAIMessage}
              pendingMessageKey={fixWithAIKey}
              pendingMessageSuppressUser={pendingSuppressUser}
              // E3/E5: headless — the conversation chrome + share/report live in
              // the top bar now; the host drives the conversation + modals.
              renderConversationHeader={false}
              conversationId={activeConversationId}
              chatKey={chatKey}
              onConversationId={handleConversationId}
              openShareSignal={openShareSignal}
              openReportSignal={openReportSignal}
              onManageCustomModels={(ctx) => {
                setModelProviderMode(ctx?.mode)
                setModelProviderOpen(true)
              }}
              modelSelectionSignal={modelSelectionSignal}
              canEdit={canEditProject}
              canSendTeamMessages={!isPublicWorkspace}
              canShare={canEditProject}
              shareLinksAllowed={canAdminProject}
              shareLinksRefreshSignal={shareLinksTick}
              shareModalContent={shareModalContent}
              suppressGuestReminder={inDiscovery}
              isAnonymous={isGuest}
              isPro={isProForProject}
              onReadyToBuild={() => {
                // Discovery done + starting point chosen — boot the sandbox and
                // mark the build kickoff pending. The plan turn keeps streaming in
                // the SAME conversation; the IDE shows as soon as the sandbox is
                // running (no awaitingPlan gate) because the chat store is
                // mount-independent — the boot→IDE swap can't cut the stream.
                buildKickoffPendingRef.current = true
                setAwaitingSandboxBoot(true)
                startSandboxRef.current()
                // P2-23: selection just rewrote the project name to the chosen
                // template's label (or "Untitled" for a custom build) on the
                // server. Re-fetch so the new name shows live (top bar + tab
                // title) without a reload. Mirror the GET effect's error
                // handling — never silently swallow.
                if (projectId && projectId !== 'new') {
                  http
                    .get<Project>(`/projects/${projectId}`)
                    .then((res) => setProject(res.data))
                    .catch((err: unknown) => {
                      console.error('Failed to refresh project after ready_to_build', err)
                    })
                }
              }}
              awaitingSandboxBoot={awaitingSandboxBoot}
              onClientAction={handleClientAction}
              onLoadingChange={setAiBuilding}
              onNavigatePreview={handleNavigatePreviewFromChat}
              onRegisterPushHandler={(h) => {
                chatPushHandlerRef.current = h
              }}
              onRegisterHistoryReconcile={(r) => {
                chatReconcileRef.current = r
              }}
            />
          </div>
          {isBooting && (
            <div className="mol-pre-ide-synth">
              <SynthesizingView
                progressSteps={progressSteps}
                displayProgress={displayProgress}
                sandboxCached={sandboxCached}
                sandboxPreWarmed={sandboxPreWarmed}
                bootWait={bootWait}
                onCancelBoot={() => void cancelBoot()}
                cancellingBoot={cancellingBoot}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  if (sandboxStatus !== 'running') {
    return (
      <div className="mol-vh-fill" style={{ display: 'flex', flexDirection: 'column' }}>
        {topBannersEl}
        {/* Past discovery (the project exists with a starting point) but the
            sandbox is stopped/sleeping — the toolbar stays visible (E2) with its
            sandbox-dependent icons disabled until it is started again. */}
        {workspaceHeader}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            // Horizontal breathing room so error copy never runs edge-to-edge
            // on a 390px phone.
            padding: '0 20px',
            color: 'var(--mol-color-text-secondary, #aaaaaa)',
            backgroundColor: 'var(--mol-color-background, #0e0e0e)',
          }}
        >
          {sandboxStatus === 'sleeping' && !sandboxLoading ? (
            <>
              <div
                style={{ fontSize: 15, fontWeight: 500, color: 'var(--mol-color-text, #e0e0e0)' }}
              >
                {t('workspace.sandboxSleeping', undefined, {
                  defaultValue: 'Sandbox went to sleep due to inactivity',
                })}
              </div>
              {/* A retryable Start refusal (the sandbox's files could not be
                  read right now) keeps the project asleep and says so here. */}
              {sandboxError && (
                <div
                  data-mol-id="workspace-wake-error"
                  role="alert"
                  style={{
                    fontSize: 14,
                    color: 'var(--mol-color-error, #ef4444)',
                    maxWidth: 400,
                    textAlign: 'center',
                    lineHeight: 1.5,
                  }}
                >
                  {sandboxError}
                </div>
              )}
              {/* Design-system success button (the auth submit treatment) —
                  replaces the hand-rolled greenBtn styling per DESIGN.md's
                  CTA rule; lg is 40px, so the compact floor is a no-op. */}
              <button
                type="button"
                data-mol-id="workspace-wake"
                onClick={() => void startSandbox()}
                className={cm.cn(
                  cm.button({ color: 'success', size: 'lg' }),
                  cm.touchTargetCompact,
                )}
              >
                {t('workspace.wake', undefined, { defaultValue: 'Wake up' })}
              </button>
            </>
          ) : sandboxStatus === 'stopped' && !sandboxLoading ? (
            <>
              {sandboxError && (
                <div
                  style={{
                    fontSize: 14,
                    color: 'var(--mol-color-error, #ef4444)',
                    maxWidth: 400,
                    textAlign: 'center',
                    lineHeight: 1.5,
                    marginBottom: 4,
                  }}
                >
                  {sandboxError}
                </div>
              )}
              {/* The projects in the way, each with a Stop that then retries
                  this Start — the instruction is doable right here. */}
              {/* Rendered by the blocker's STATE. The server sends 'starting'
                  (being created) and 'waking' (resuming under a wake claim)
                  with reason 'in-use' — they cannot be stopped from here yet,
                  so they are named instead. A 'running' one gets a one-click
                  Stop only when it is merely RECENTLY active: one with a Synthase
                  turn in flight (running + 'in-use') is exactly what the refusal
                  protects — the sentence names it and the user decides from the
                  dashboard. */}
              {sandboxError &&
                sandboxErrorKey === SANDBOX_CONCURRENCY_KEY &&
                sandboxErrorBlocking
                  .filter(
                    (blocking) =>
                      blocking.state === 'starting' ||
                      blocking.state === 'waking' ||
                      blocking.reason === 'recent',
                  )
                  .map((blocking) =>
                    blocking.state === 'starting' || blocking.state === 'waking' ? (
                      <div
                        key={blocking.id}
                        data-mol-id="workspace-start-blocking-starting"
                        style={{
                          fontSize: 13,
                          color: 'var(--mol-color-text-muted, #999)',
                          lineHeight: 1.5,
                        }}
                      >
                        {t(
                          'workspace.startBlockingStarting',
                          { name: blocking.name },
                          { defaultValue: '{{name}} is starting.' },
                        )}
                      </div>
                    ) : (
                      <button
                        key={blocking.id}
                        type="button"
                        data-mol-id="workspace-start-stop-blocking"
                        onClick={() => void stopBlockingAndStart(blocking.id)}
                        className={cm.cn(
                          cm.button({ color: 'primary', size: 'md' }),
                          cm.touchTargetCompact,
                        )}
                      >
                        {t(
                          'workspace.startStopBlocking',
                          { name: blocking.name },
                          { defaultValue: 'Stop {{name}} and start this one' },
                        )}
                      </button>
                    ),
                  )}
              {sandboxError && sandboxErrorKey === SANDBOX_CONCURRENCY_KEY && (
                // "Stop another project" is only doable from the dashboard — take
                // them there instead of leaving the instruction dangling.
                <button
                  type="button"
                  data-mol-id="workspace-start-manage-projects"
                  onClick={() =>
                    navigate(sandboxErrorAction(sandboxErrorKey)?.href ?? '/dashboard')
                  }
                  className={cm.cn(
                    cm.button({ color: 'secondary', variant: 'outline', size: 'md' }),
                    cm.touchTargetCompact,
                  )}
                >
                  {sandboxErrorAction(sandboxErrorKey)?.label}
                </button>
              )}
              <div
                style={{ fontSize: 15, fontWeight: 500, color: 'var(--mol-color-text, #e0e0e0)' }}
              >
                {t('workspace.sandboxStopped', undefined, { defaultValue: 'Sandbox is stopped' })}
              </div>
              {/* Same design-system success treatment as the Wake button. */}
              <button
                type="button"
                data-mol-id="workspace-start"
                onClick={() => void startSandbox()}
                className={cm.cn(
                  cm.button({ color: 'success', size: 'lg' }),
                  cm.touchTargetCompact,
                )}
              >
                {t('workspace.start', undefined, { defaultValue: 'Start' })}
              </button>
            </>
          ) : sandboxStatus === 'lost' && !sandboxLoading ? (
            <SandboxLostState onStart={startSandbox} error={sandboxError} />
          ) : (
            <SynthesizingView
              progressSteps={progressSteps}
              displayProgress={displayProgress}
              sandboxCached={sandboxCached}
              sandboxPreWarmed={sandboxPreWarmed}
              bootWait={bootWait}
              onCancelBoot={() => void cancelBoot()}
              cancellingBoot={cancellingBoot}
            />
          )}
        </div>
        {/* Phones move every action to the bottom nav here, exactly as in the
            full IDE — so a hibernated phone never crams the toolbar into the
            header. The surface tabs can't show a pane while the sandbox sleeps,
            so tapping one sets the destination and wakes it (it lands there once
            running); More opens the shared sheet. Desktop keeps the header
            toolbar (it has the room). */}
        {isMobile && (
          <>
            {renderMobileNav({
              activeTarget: null,
              onSurface: (target) => {
                const next = resolveMobileNav(target)
                setMobilePane(next.pane)
                if (next.view) setMainView(next.view)
                void startSandbox()
              },
            })}
            <FixedBottomNavOffset />
          </>
        )}
        {/* Mount the SAME panel set the running IDE does. Every toolbar button
            that stays enabled while the sandbox sleeps — Environment, API keys,
            Activity, Logs, Pipeline, Team — reads control-plane or production
            state the dev container never held, so its panel must be mounted here
            too or the click is a silent no-op until the sandbox wakes (the bug a
            hand-picked subset caused: Activity/API keys/Team were reachable but
            unrendered). Storage is the only sandbox-gated one, and its button is
            disabled in this phase, so its modal simply stays closed. */}
        {panelModals}
      </div>
    )
  }

  return (
    <div
      className="mol-vh-fill"
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {topBannersEl}

      {/* Quick Open overlay */}
      {showQuickOpen && (
        <QuickOpen
          projectId={projectId!}
          onFileOpen={handleFileSelect}
          onDismiss={() => setShowQuickOpen(false)}
        />
      )}

      {/* Command Palette overlay */}
      {showCommandPalette && (
        <CommandPalette commands={commands} onDismiss={() => setShowCommandPalette(false)} />
      )}

      {/* Keyboard Shortcuts panel */}
      {showShortcuts && (
        <KeyboardShortcutsPanel
          shortcuts={shortcutEntries}
          onDismiss={() => setShowShortcuts(false)}
        />
      )}

      {/* Workspace header (shared element — also rendered during booting and the
          stopped/sleeping states for E2). */}
      {workspaceHeader}

      {/* Repo-import modal (GIT2) — viewport-fixed overlay; also mounted in the
          discovery return above (the card can fire in either layout). */}
      {importRepoModalEl}
      {modelProviderModalEl}

      {/* IDE panels — desktop: a resizable chat | main row (WorkspaceLayout).
          Phone (<768px): ONE full-width surface switched by a bottom nav, so
          neither chat nor preview is squeezed to ~130px. Both branches reuse the
          SAME chatPanelEl / mainPaneEl; the chat store is mount-independent and
          the editor/preview are hidden with `visibility` (never unmounted), so
          switching surfaces never reloads Monaco or the preview iframe. */}
      {(() => {
        const chatPanelEl = (
          <ChatPanel
            {...IDE_IDENTITY}
            buildUpgradeCta={buildUpgradeCta}
            version={versionState.version}
            extraCommands={moleculeCommands}
            projectId={projectId!}
            userAvatar={user?.avatar}
            // C5: clicking any author's avatar/name opens their profile modal —
            // the viewer's own is editable, a teammate's is view-only.
            currentUserId={user?.id}
            onProfileClick={openChatProfile}
            initialMessage={initialPrompt}
            onInitialMessageSent={onInitialMessageSent}
            isAnonymous={isGuest}
            isPro={isProForProject}
            activeFile={activeFile}
            openTabs={openFilePaths}
            onFileOpen={handleFileSelect}
            onFileDoubleClick={handleFileDoubleClick}
            onFileDiff={handleFileDiff}
            onFileRevert={handleFileRevert}
            onFileChange={handleFileChange}
            onFileDeleted={handleFileDeleted}
            onCommit={fetchGitStatus}
            onActivityClick={handleActivityClick}
            onReadyToBuild={() => startSandboxRef.current()}
            awaitingSandboxBoot={awaitingSandboxBoot}
            onClientAction={handleClientAction}
            onLoadingChange={setAiBuilding}
            onNavigatePreview={handleNavigatePreviewFromChat}
            gitStatusTick={gitStatusTick}
            pendingMessage={fixWithAIMessage}
            pendingMessageKey={fixWithAIKey}
            pendingMessageSuppressUser={pendingSuppressUser}
            pendingMessageUserInitiated={pendingUserInitiated}
            userEditedFile={userEditedFile}
            userEditedFileKey={userEditedFileKey}
            // E3/E5: headless — conversation chrome + share/report live in the
            // top bar; the host drives the conversation + modals.
            renderConversationHeader={false}
            conversationId={activeConversationId}
            chatKey={chatKey}
            onConversationId={handleConversationId}
            openShareSignal={openShareSignal}
            openReportSignal={openReportSignal}
            onManageCustomModels={(ctx) => {
              setModelProviderMode(ctx?.mode)
              setModelProviderOpen(true)
            }}
            modelSelectionSignal={modelSelectionSignal}
            canEdit={canEditProject}
            canSendTeamMessages={!isPublicWorkspace}
            canShare={canEditProject}
            shareLinksAllowed={canAdminProject}
            shareLinksRefreshSignal={shareLinksTick}
            shareModalContent={shareModalContent}
            // The /test browser. A VIEWER still sees what the project tests but cannot
            // run anything (the server refuses it too — the run endpoint is
            // editor-only), and the run controls are gated on a RUNNING sandbox
            // the same way DeployPanel gates its three container actions: the
            // tests execute inside the sandbox, and the e2e specs drive the live
            // preview it serves.
            listTests={isPublicWorkspace ? undefined : listTests}
            runTests={isPublicWorkspace ? undefined : runTests}
            nativeTests={
              isPublicWorkspace
                ? undefined
                : {
                    visible: !!testRecords.payload?.snapshot.records.some(
                      (r) => r.start.recordKind === 'invocation',
                    ),
                    history: (
                      <ProjectTestRecordHistory
                        state={testRecords}
                        projectId={projectId || ''}
                        getAuthToken={testAuthToken}
                        canControl={
                          canEditProject && sandboxStatus === 'running' && !testRecords.unavailable
                        }
                      />
                    ),
                    renderStatus: (open) => (
                      <ProjectTestRecordStatus state={testRecords} onOpen={open} />
                    ),
                  }
            }
            canRunTests={canEditProject}
            testsAvailable={!isPublicWorkspace && sandboxStatus === 'running'}
            skipToolCall={skipToolCall}
            onRegisterPushHandler={(h) => {
              chatPushHandlerRef.current = h
            }}
            onRegisterHistoryReconcile={(r) => {
              chatReconcileRef.current = r
            }}
          />
        )
        // The Files/Search sidebar content is shared between the desktop inline
        // column and the phone's slide-in drawer — built once so the two can
        // never drift. The select handlers also close the drawer; on desktop
        // that's a no-op (the drawer is never open there).
        const sidebarContentEl = (
          <SidebarTabs activeTab={sidebarTab} onTabChange={setSidebarTab}>
            {sidebarTab === 'files' ? (
              <div style={{ height: '100%', overflow: 'auto' }}>
                {files.length === 0 && (
                  <div
                    style={{
                      padding: '12px 10px',
                      fontSize: 12,
                      color: 'var(--mol-color-text-secondary, #888)',
                    }}
                  >
                    {sandboxStatus === 'running'
                      ? t('workspace.fileTree.empty', undefined, {
                          defaultValue: 'No files yet',
                        })
                      : t('workspace.fileTree.loading', undefined, {
                          defaultValue: 'Loading…',
                        })}
                  </div>
                )}
                <FileExplorer
                  files={files}
                  onFileSelect={(path) => {
                    handleFileSelect(path)
                    setFilesDrawerOpen(false)
                  }}
                  onFileDoubleClick={(path) => {
                    handleFileDoubleClick(path)
                    setFilesDrawerOpen(false)
                  }}
                  onDirExpand={handleDirExpand}
                  // Viewers browse only: readOnly hides the mutating context-menu
                  // entries, and omitting the handlers no-ops keyboard shortcuts
                  // (Delete/F2/⌘V) and drag-moves too.
                  readOnly={!canEditProject}
                  onRename={canEditProject ? handleRename : undefined}
                  onDelete={canEditProject ? handleDeleteFile : undefined}
                  onDeleteMultiple={canEditProject ? handleDeleteMultiple : undefined}
                  onMoveFiles={canEditProject ? handleMoveFiles : undefined}
                  onNewFile={canEditProject ? handleNewFile : undefined}
                  onNewFolder={canEditProject ? handleNewFolder : undefined}
                  persistKey={`molecule_file_tree_${projectId}`}
                  activeFile={activeFile}
                  fileStatuses={fileStatuses}
                />
              </div>
            ) : (
              <SearchPanel
                // Viewers search freely; Replace + exclude-set persistence are editor work.
                readOnly={!canEditProject}
                projectId={projectId!}
                onResultClick={(path, line) => {
                  handleSearchResultClick(path, line)
                  setFilesDrawerOpen(false)
                }}
                // VS Code-style search.exclude — ONE per-project set
                // the server applies to the panel AND the AI search
                // tools; edits persist via the standard settings PATCH.
                excludedDirs={project?.settings?.searchExcludes}
                onExcludedDirsChange={(dirs) => {
                  if (!canEditProject) return
                  const nextSettings = {
                    ...(project?.settings ?? {}),
                    searchExcludes: dirs,
                  }
                  setProject((prev) => (prev ? { ...prev, settings: nextSettings } : prev))
                  http
                    .patch(`/projects/${projectId}`, { settings: nextSettings })
                    .catch((err: unknown) => {
                      console.error('Failed to save search excludes', err)
                    })
                }}
              />
            )}
          </SidebarTabs>
        )
        // Main pane: live preview (default) ⇆ file explorer / code editor. Both
        // views stay mounted and full-size so Monaco state + the preview iframe
        // survive toggling (see the visibility note below).
        const mainPaneEl = (
          <div
            className="mol-workspace-main-pane"
            // `position: relative` makes this the offset parent for the panel
            // modal backdrop (PanelModal's backdrop is `position: absolute;
            // inset: 0`), scoping its dim + blur to THIS pane only. `isolation:
            // isolate` gives it its own stacking context so the backdrop's high
            // z-index can never paint over the sibling chat pane or the toolbar.
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minWidth: 0,
              position: 'relative',
              isolation: 'isolate',
            }}
          >
            {/* The Preview ⇆ Code switch moved into the main toolbar (E1); this
                pane no longer carries its own bar above the preview/editor. */}
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
              {/* Both views stay laid out at full size; we hide the inactive
                  one with `visibility` (not `display: none`) so Monaco and the
                  preview iframe keep real dimensions. Monaco initialized in a
                  zero-size container crashes the renderer (SIGTRAP). */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'absolute',
                  inset: 0,
                  minWidth: 0,
                  visibility: mainView === 'editor' ? 'visible' : 'hidden',
                  zIndex: mainView === 'editor' ? 1 : 0,
                  pointerEvents: mainView === 'editor' ? 'auto' : 'none',
                }}
              >
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                  {/* Desktop-only inline sidebar column (phones mount the same
                      content in the slide-in drawer below — never both, so the
                      FileExplorer's expand/selection state lives in exactly one
                      instance at a time). */}
                  {!isMobile && (
                    <div
                      className="mol-workspace-file-explorer"
                      // Width is now stateful + draggable (P4-01) via the ResizeHandle
                      // below — replacing the old fixed CSS 220px. The inline width
                      // is the source of truth (it intentionally shadows the CSS
                      // fallback) and is clamped + persisted per project.
                      style={{
                        width: fileExplorerWidth,
                        flexShrink: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {sidebarContentEl}
                    </div>
                  )}
                  {/* Draggable vertical divider between the file explorer and the
                      editor (P4-01) — the SAME ResizeHandle the chat split uses.
                      Dragging adjusts the (leading) file-explorer width; the editor
                      (trailing, flex:1) absorbs the rest. Hidden on phones, where
                      the file explorer itself is `display:none`. */}
                  {!isMobile && (
                    <ResizeHandle
                      direction="horizontal"
                      onResize={(delta) =>
                        setFileExplorerWidth((w) =>
                          Math.min(
                            FILE_EXPLORER_MAX_WIDTH,
                            Math.max(
                              FILE_EXPLORER_MIN_WIDTH,
                              normalizeFileExplorerWidth(w) + delta,
                            ),
                          ),
                        )
                      }
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                    <EditorPanel
                      // Viewers read code, never write it — the server rejects
                      // their file writes, so Monaco blocks the typing up front.
                      readOnly={!canEditProject}
                      onActiveFileChange={setActiveFile}
                      onEditorReady={handleEditorReady}
                      onTabsChange={handleTabsChange}
                      fileStatuses={fileStatuses}
                      formattingFile={formattingFile}
                      countdownFile={countdownFile}
                      countdownKey={countdownKey}
                      formatEstimate={formatEstimate}
                      onFixWithAI={handleFixWithAI}
                      onTabDoubleClick={handleTabDoubleClick}
                    />
                    {restoring && editorReady && sandboxStatus === 'running' && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundColor: 'var(--mol-color-background, #0e0e0e)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 12,
                          zIndex: 10,
                        }}
                      >
                        <MoleculeSpinner size="lg" />
                        <div
                          style={{
                            fontSize: 13,
                            color: 'var(--mol-color-text-secondary, #aaaaaa)',
                          }}
                        >
                          {t('workspace.restoringTabs', undefined, {
                            defaultValue: 'Restoring editor…',
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {showTerminal && (
                  <div
                    className="mol-workspace-terminal"
                    // The inline height would defeat the CSS 38dvh phone rule
                    // (inline beats class), so the phone value lives here too —
                    // and the Files FAB offsets against the same 38dvh.
                    style={{
                      height: isMobile ? '38dvh' : 220,
                      flexShrink: 0,
                      borderTop: '1px solid var(--color-border)',
                    }}
                  >
                    <TerminalPanel projectId={projectId!} sandboxStatus={sandboxStatus} />
                  </div>
                )}
                {/* Phone-only floating Files/Search trigger — the inline sidebar
                    column doesn't exist below 768px, and its old entry points
                    (mod+shift+f, the command palette) are keyboard-only. Lives
                    inside the editor layer so it appears exactly when the Code
                    surface does. */}
                {isMobile && (
                  <button
                    type="button"
                    className="mol-files-fab"
                    data-mol-id="mobile-files-fab"
                    aria-label={t('workspace.filesDrawer.open', undefined, {
                      defaultValue: 'Files & search',
                    })}
                    onClick={() => setFilesDrawerOpen(true)}
                    // Clears the terminal dock when it's open (38dvh on phones).
                    style={showTerminal ? { bottom: 'calc(38dvh + 12px)' } : undefined}
                  >
                    <Icon name="folder" size={20} />
                  </button>
                )}
                {/* Phone empty state: with no tabs the Code surface is a black
                    void and the folder FAB is the only (unlabeled) affordance —
                    name the next step instead. */}
                {isMobile && openFilePaths.length === 0 && !restoring && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 14,
                      zIndex: 5,
                      pointerEvents: 'none',
                      padding: '0 24px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: 14, color: 'var(--mol-color-text-secondary, #999)' }}>
                      {t('workspace.editorEmpty', undefined, { defaultValue: 'No file open' })}
                    </div>
                    {/* The empty state's one action — outline, the design-system
                        secondary treatment. `pointerEvents` is the only thing left
                        inline: the overlay above it is click-through. */}
                    <button
                      type="button"
                      data-mol-id="mobile-editor-empty-browse"
                      onClick={() => setFilesDrawerOpen(true)}
                      className={cm.cn(
                        cm.button({ variant: 'outline', size: 'md' }),
                        cm.touchTargetCompact,
                      )}
                      style={{ pointerEvents: 'auto' }}
                    >
                      <Icon name="folder" size={18} />
                      {t('workspace.editorEmpty.browse', undefined, {
                        defaultValue: 'Browse files',
                      })}
                    </button>
                  </div>
                )}
              </div>
              <div
                style={{
                  display: 'block',
                  position: 'absolute',
                  inset: 0,
                  visibility: mainView === 'preview' ? 'visible' : 'hidden',
                  zIndex: mainView === 'preview' ? 1 : 0,
                  pointerEvents: mainView === 'preview' ? 'auto' : 'none',
                }}
              >
                <PreviewPanel
                  loadingIndicator={previewLoadingIndicator}
                  restartingIndicator={previewRestartingIndicator}
                  onPreviewError={handlePreviewError}
                  onPreviewStuck={handlePreviewStuck}
                  onRestartBackend={handleRestartBackend}
                  onRenderState={handleRenderState}
                  fileChangeTick={fileChangeTick}
                  buildingHint={buildingHint}
                  isBuilding={aiBuilding}
                  wakeAt={sandboxWakeAt || undefined}
                  uiCommand={previewUiCommand}
                  onUiResult={handlePreviewUiResult}
                />
                {/* Broken-preview overlay — driven by the continuous SERVER-SIDE
                    health check (usePreviewHealth), so it appears even when the
                    preview crashed so hard the iframe can't report anything
                    (ERR_EMPTY_RESPONSE). Shown whenever the server reports `down`;
                    the autonomous auto-fix above is what's gated on /autofix.
                    Deliberately calm: it leads with "Synthase is fixing the
                    preview…" + spinner and hides the raw error behind a "More
                    details" toggle (see PreviewBrokenOverlay). Keyed on downSeq so
                    each fresh break remounts it — resetting the toggle to collapsed. */}
                {previewHealth.status === 'down' && mainView === 'preview' && (
                  <PreviewBrokenOverlay
                    key={previewHealth.downSeq}
                    message={previewHealth.message}
                    autoFix={previewHealth.autoFix}
                    aiBuilding={aiBuilding}
                    onFixWithAI={handlePreviewFixWithAI}
                  />
                )}
              </div>
            </div>
            {/* Panel modals overlay ONLY this main pane (backdrop is
                `position: absolute` within `.mol-workspace-main-pane`), so the
                blur/dim never reaches the chat pane or the toolbar. */}
            {/* Desktop: panel modals live INSIDE the main pane so their blur
                scopes to the preview/editor. On phones they render at the
                mobile-branch root instead (below) — PanelModal.css makes them
                viewport-fixed full-screen sheets there, independent of which
                surface is showing. */}
            {!isMobile && panelModals}
          </div>
        )
        const chatSurfaceVisible = mobilePane === 'chat'
        const mainSurfaceVisible = mobilePane === 'main'
        return (
          <div style={{ flex: 1, minHeight: 0 }}>
            {isMobile ? (
              <div
                className="mol-workspace-mobile"
                style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
              >
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                  <div
                    className="mol-workspace-mobile-pane"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      visibility: chatSurfaceVisible ? 'visible' : 'hidden',
                      zIndex: chatSurfaceVisible ? 1 : 0,
                      pointerEvents: chatSurfaceVisible ? 'auto' : 'none',
                    }}
                  >
                    {chatPanelEl}
                  </div>
                  <div
                    className="mol-workspace-mobile-pane"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      visibility: mainSurfaceVisible ? 'visible' : 'hidden',
                      zIndex: mainSurfaceVisible ? 1 : 0,
                      pointerEvents: mainSurfaceVisible ? 'auto' : 'none',
                    }}
                  >
                    {mainPaneEl}
                  </div>
                </div>
                {renderMobileNav({
                  // Full IDE: the active surface is highlighted, and a tab
                  // switches the visible pane.
                  activeTarget: activeMobileNav(mobilePane, mainView),
                  onSurface: (target) => {
                    const next = resolveMobileNav(target)
                    setMobilePane(next.pane)
                    if (next.view) setMainView(next.view)
                  },
                })}
                <FixedBottomNavOffset />
                {/* WorkspaceMoreSheet is mounted ONCE in the workspace header
                    (it portals to <body>) and shared by both this bottom-nav
                    "More" tab and the stopped/sleeping header overflow button. */}
                {/* Phone presentation of the panel modals: viewport-fixed
                    full-screen sheets (PanelModal.css ≤767px), so they cover
                    whichever surface is active — no surface-forcing needed. */}
                {panelModals}
                {/* Files/Search as a left drawer (portaled; content shared with
                    the desktop sidebar column). */}
                <MobileSheet
                  open={filesDrawerOpen}
                  onClose={() => setFilesDrawerOpen(false)}
                  side="left"
                  title={t('workspace.filesDrawer.title', undefined, {
                    defaultValue: 'Explorer',
                  })}
                  molId="mobile-files-drawer"
                >
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {sidebarContentEl}
                  </div>
                </MobileSheet>
              </div>
            ) : (
              <WorkspaceLayout>
                {chatPanelEl}
                {mainPaneEl}
              </WorkspaceLayout>
            )}
          </div>
        )
      })()}
    </div>
  )
}
